/** @typedef {'es' | 'en'} Locale */

import { flowIaEs, flowIaEn } from './flowIaBundles';

/** @type {Record<Locale, Record<string, unknown>>} */
export const translations = {
  es: {
    brand: 'Spectra RRHH',
    brandTagline: 'Gestioná el ciclo completo del colaborador desde un solo lugar.',
    version: 'v0.6.0-rrhh-flow-ia-premium',
    navGroups: {
      home: 'Inicio',
      workspace: 'Espacio de trabajo',
      people: 'Personas',
      processes: 'Procesos',
      culture: 'Cultura',
      development: 'Desarrollo',
      intelligence: 'Intelligence',
      system: 'Sistema',
    },
    nav: {
      dashboard: 'Dashboard',
      employees: 'Colaboradores',
      employeeLifecycle: 'Ciclo del colaborador',
      orgChart: 'Organigrama',
      leaves: 'Licencias',
      expenses: 'Rendición de gastos',
      personalFinance: 'Mis Finanzas',
      awards: 'Premios',
      records: 'Legajo Digital',
      documents: 'Documentos',
      employeePortal: 'Portal colaborador',
      attendance: 'Asistencia / Jornada',
      payrollReceipts: 'Recibos',
      communications: 'Comunicaciones',
      notifications: 'Notificaciones',
      feed: 'Muro',
      chat: 'Chat',
      compliance: 'Compliance',
      performance: 'Evaluaciones',
      recruiting: 'Reclutamiento',
      training: 'Capacitaciones',
      surveys: 'Clima y encuestas',
      tasks: 'Tareas',
      reports: 'Reportes',
      peopleIntelligence: 'People Intelligence',
      settings: 'Configuración',
    },
    sidebar: {
      collapse: 'Colapsar menú',
      expand: 'Expandir menú',
      navPiBadge: 'Intelligence IA',
      navPiBadgeShort: 'IA',
    },
    navBadge: {
      active: 'Activo',
      next: 'Próximo',
    },
    productStatus: {
      active: 'Activo',
      prepared: 'Preparado',
      soon: 'Próximamente',
    },
    header: {
      themeLight: 'Claro',
      themeDark: 'Oscuro',
      themeSwitchToDark: 'Cambiar a modo oscuro',
      themeSwitchToLight: 'Cambiar a modo claro',
      demoBadge: 'Demo',
      userFallback: 'Usuario',
      online: 'Online',
      onlineTitle: 'Servicio operativo',
      flowPreparedTitle: 'Integración con Spectra Flow/Auth',
      flowPreparedShort: 'Flow/Auth preparado',
      flowSoon: 'Conectado a Spectra Flow próximamente',
      breadcrumbHome: 'Inicio',
    },
    time: {
      relative: {
        justNow: 'Ahora',
        minutesAgo: 'Hace {n} min',
        todayAt: 'Hoy · {time}',
        yesterday: 'Ayer',
      },
    },
    notifyBell: {
      aria: 'Centro de notificaciones',
      title: 'Notificaciones',
      subtitle: 'Centro unificado para RRHH y colaboradores (demo local).',
      markAllRead: 'Marcar todas como leídas',
      markRead: 'Marcar leída',
      emptyTitle: 'No tenés notificaciones pendientes',
      emptyBody: 'Cuando publiques comunicaciones u haya alertas, vas a verlas acá y en Comunicaciones.',
      emptyCta: 'Ir a Comunicaciones',
      sectionToday: 'Hoy',
      sectionEarlier: 'Anteriores',
      priority: 'Prioridad',
      ctaOpen: 'Abrir',
      filter_all: 'Todas',
      filter_unread: 'No leídas',
      filter_comms: 'Comunicaciones',
      filter_tasks: 'Tareas',
      filter_leaves: 'Licencias',
      filter_docs: 'Documentos',
      filter_expenses: 'Gastos',
      filter_flow_ia: 'Flow IA',
      ctaOpenComms: 'Ver comunicación',
      ctaReviewDoc: 'Revisar documento',
      ctaOpenLeave: 'Ver licencia',
      ctaOpenExpense: 'Ver gasto',
      ctaOpenFlowIa: 'Abrir Flow IA',
      ctaOpenLifecycle: 'Ver ciclo del colaborador',
      ctaOpenTraining: 'Ver capacitación',
      ctaOpenSurvey: 'Abrir encuesta',
      sysPublishedTitle: 'Comunicación publicada: {title}',
      sysPublishedBody: 'Disponible en Comunicados y en este centro.',
      seed: {
        comms1Title: 'Nueva comunicación interna publicada',
        comms1Body: 'Revisá el tablero de Comunicaciones para leer el aviso completo.',
        docs1Title: 'Documento pendiente de revisión',
        docs1Body: 'Hay un ítem en Legajo Digital que requiere tu firma o revisión.',
        leave1Title: 'Licencia actualizada',
        leave1Body: 'El estado de tu solicitud cambió. Abrí Licencias para ver el detalle.',
        exp1Title: 'Gasto con novedades',
        exp1Body: 'Una rendición tiene actualización de estado.',
        flow1Title: 'Recomendación de Flow IA',
        flow1Body: 'Nueva sugerencia contextual basada en tus pantallas frecuentes.',
        task1Title: 'Tarea de onboarding / offboarding',
        task1Body: 'Hay un paso pendiente en el ciclo del colaborador.',
        train1Title: 'Capacitación asignada',
        train1Body: 'Te asignaron una capacitación obligatoria o recomendada.',
        survey1Title: 'Encuesta de clima disponible',
        survey1Body: 'Participá en el pulso de clima cuando puedas.',
      },
    },
    rc: {
      demoEnvironmentNotice:
        'Este entorno está preparado para demo comercial. Las conexiones reales de Email, Storage, Flow/Auth y Finance deben validarse antes de uso productivo con clientes.',
    },
    flowIa: flowIaEs,
    dashboard: {
      title: 'Dashboard RRHH',
      subtitle:
        'Centralizá legajos, recibos, licencias, asistencia, comunicaciones y talento. People Intelligence ayuda a priorizar riesgos y próximos pasos de RRHH.',
      rcEyebrow: 'Spectra RRHH · People OS',
      rcHeaderLine: 'Entorno local — datos de demostración y rol simulado.',
      rcHeaderLineProd: 'People OS con Flow IA contextual y operación multi-tenant.',
      heroTitle: 'Gestión Humana Inteligente',
      heroLead:
        'Centralizá colaboradores, licencias, gastos, cultura y bienestar financiero en una sola plataforma.',
      badgeDemo: 'Presentación comercial',
      badgeCommercialDemo: 'Demo comercial',
      badgeFlowIa: 'Flow IA',
      badgeRC: 'People OS',
      badgeVersionShort: 'v0.3.5',
      badgeFlow: 'Preparado para Spectra Flow/Auth',
      ctaEmployees: 'Ver colaboradores',
      ctaExpenses: 'Revisar gastos',
      commandCenter: 'People Command Center',
      kpiExpensesApprove: 'Gastos por aprobar',
      avgFinancialScore: 'Score financiero (demo)',
      priorityActions: 'Acciones prioritarias',
      priority1: 'Aprobar licencias pendientes',
      priority2: 'Revisar rendiciones',
      priority3: 'Reconocer colaboradores destacados',
      priority4: 'Completar datos de legajo',
      priority5: 'Preparar conexión con Flow/Auth',
      strategicCardLegajos: 'Legajos digitales',
      strategicCardLegajosDesc: 'Historial único del colaborador.',
      strategicCardPortal: 'Portal colaborador',
      strategicCardPortalDesc: 'Autogestión y solicitudes.',
      strategicCardRecibos: 'Recibos digitales',
      strategicCardRecibosDesc: 'Liquidaciones firmadas y trazables.',
      strategicCardPI: 'People Intelligence',
      strategicCardPIDesc: 'Alertas, riesgo y sugerencias.',
      strategicCardWellbeing: 'Bienestar financiero',
      strategicCardWellbeingDesc:
        'Privado y seguro: Mis Finanzas suma tabs de presupuesto, metas y proyección personal (demo). La empresa no ve tus movimientos.',
      strategicCardFinance: 'Integración Finance',
      strategicCardFinanceDesc: 'Sincronización de gastos aprobados.',
      strategicCardReports: 'Reportes ejecutivos',
      strategicCardReportsDesc: 'Pack mensual y cortes por área para dirección.',
      activeEmployees: 'Colaboradores activos',
      newHires: 'Altas del mes',
      pendingLeaves: 'Licencias pendientes',
      pendingExpenses: 'Gastos pendientes',
      unpaidExpenses: 'Gastos sin pagar',
      climate: 'Clima laboral',
      employeeMonth: 'Empleado del mes',
      upcomingLeaves: 'Próximas licencias',
      recentExpenses: 'Gastos recientes',
      recentAwards: 'Reconocimientos recientes',
      insights: 'People insights',
      insightRetention: 'Retención proyectada estable con foco en onboarding remoto.',
      insightSkills: 'Skills en demanda: liderazgo, datos y diseño de producto.',
      insightPending1: 'solicitudes requieren aprobación',
      insightPending2: 'rendiciones pendientes de pago',
      insightWellbeing: 'El bienestar financiero mejora con presupuestos activos.',
      insightFlow: 'Próximo paso: conectar Flow/Auth para roles reales.',
      noLeaves: 'No hay licencias próximas.',
      noExpenses: 'Sin gastos recientes.',
      noAwards: 'Sin premios recientes.',
      pendingActions: 'Acciones pendientes',
      upcomingEvents: 'Próximos eventos',
      recentActivity: 'Actividad reciente',
      strategicModules: 'Módulos estratégicos',
      strategicHint: 'Explorá el roadmap de capacidades enterprise.',
      commsPanelTitle: 'Comunicación interna y operativa',
      commsPanelSubtitle: 'Resumen demo: alertas, tareas y mensajes preparados sin envío real.',
      commsCritical: 'Notificaciones críticas',
      commsTasks: 'Tareas RRHH pendientes',
      commsEmailsPrepared: 'Emails RRHH preparados',
      commsRecentBulletins: 'Comunicados recientes',
      commsNextActions: 'Próximas acciones de comunicación',
      commsInternalInd: 'Comunicación interna activa',
      commsLinkNotifications: 'Abrir notificaciones',
      commsLinkCommunications: 'Abrir comunicaciones',
      commsEmailsRecent: 'Enviados / simulados (log)',
      commsEmailLogTitle: 'Últimos emails RRHH (registro)',
      commsNext1: 'Preparar recordatorio de documentación vía Emails RRHH.',
      commsNext2: 'Publicar comunicado de políticas o beneficios.',
      commsNext3: 'Sincronizar tareas críticas con managers en Notificaciones.',
      commsInternalStatus: 'Activo (demo)',
      strategicRecords: 'Historial único y documentación del colaborador.',
      strategicDocs: 'Vencimientos, visibilidad y storage seguro.',
      strategicTraining: 'Capacitación continua con certificación.',
      strategicReports: 'People analytics y export ejecutivo.',
      strategicPi: 'IA aplicada a talento y riesgo.',
      eventPlaceholder: 'Sin eventos programados. Integrá calendario en versiones futuras.',
      activityPlaceholder: 'La actividad reciente aparecerá aquí con auditoría y notificaciones.',
      bulletLeaves: 'licencias con estado pendiente requieren aprobación.',
      bulletExpenses: 'rendiciones en curso o pendientes de pago.',
      bulletWellbeing: 'El bienestar financiero mejora con presupuestos activos.',
      bulletFlow: 'Próximo paso: conectar Flow/Auth para roles y permisos reales.',
      noEom: 'Sin destacado este período.',
      docsStripTitle: 'Documentación RRHH',
      docsStripCta: 'Legajo Digital',
      docKpiTotal: 'Documentos',
      docKpiPending: 'Pendientes',
      docKpiExpired: 'Vencidos',
      docKpiApproved: 'Aprobados',
      docKpiCompliance: 'Compliance',
      kpiTotalEmployees: 'Total colaboradores',
      kpiPresentToday: 'Presentes hoy',
      kpiCommsPublished: 'Comunicaciones publicadas',
      kpiPiScore: 'People Intelligence (reglas v1)',
      kpiPendingExpenseAmount: 'Gastos pendientes (monto)',
      rrhhNextTitle: 'Próximas acciones RRHH',
      rrhhActionDocs: 'Revisar documentos vencidos o pendientes',
      rrhhActionLeaves: 'Aprobar licencias',
      rrhhActionExpenses: 'Revisar rendiciones',
      rrhhActionComms: 'Publicar comunicación interna',
      rrhhActionAttendance: 'Controlar asistencia de hoy',
      rrhhActionLifecycle: 'Abrir ciclo del colaborador',
      rrhhActionOnboardComplete: 'Completar onboarding de nuevos ingresos',
      rrhhActionOffboardBlocked: 'Cerrar offboarding bloqueado',
      peopleOpsTitle: 'People Operations',
      peopleOpsSubtitle: 'Onboarding, offboarding y tareas críticas del ciclo (demo + datos tenant).',
      peopleKpiOnboard: 'Onboardings activos',
      peopleKpiOffboard: 'Offboardings activos',
      peopleKpiHrTasks: 'Tareas RRHH pendientes',
      peopleKpiDocs: 'Documentación pendiente',
      peopleKpiIncoming: 'Próximas incorporaciones',
      extraKpisTitle: 'Indicadores operativos extendidos',
      extraKpisHint: 'Incluye datos de asistencia, comunicaciones y People Intelligence v1 (reglas).',
      execSystemBadge: 'Sistema demo operativo',
      kpiGridTitle: 'Indicadores ejecutivos',
      kpiActiveShort: 'Activos',
      kpiDocsPair: 'Docs pendientes / vencidos',
      kpiDocMissingFile: 'Docs. requeridos sin archivo',
      kpiPayslipsAvail: 'Recibos disponibles',
      kpiPayslipsDraft: 'Recibos borrador',
      kpiStorageRuntime: 'Estado storage',
      securityCardTitle: 'Seguridad y permisos',
      securityCardSubtitle: 'Estado de gobernanza de acceso (demo + catálogo local).',
      securityRolesLabel: 'Roles definidos',
      securityPermsLabel: 'Permisos preparados',
      securityFlowLabel: 'Flow/Auth',
      securityRiskLabel: 'Riesgos de configuración',
      securityRolesValue: '6 en catálogo',
      securityPermsValue: 'Matriz lista',
      securityFlowValue: 'Integración siguiente etapa',
      securityFlowStatus: 'Pendiente',
      securityRiskValue: 'Revisar rol simulado y demo',
      securityRiskBadge: 'Revisar',
      securityCta: 'Abrir roles y permisos',
      storageStatus_metadata_only: 'Solo metadata',
      storageStatus_ready: 'Spaces listo',
      storageStatus_missing_config: 'Falta configuración',
      piSnapshotTitle: 'People Intelligence · snapshot',
      piSnapshotRules: 'Intelligence v1 basada en reglas.',
      piSnapshotNext: 'Próxima etapa: IA contextual integrada.',
      activitySectionTitle: 'Actividad reciente',
      activityColDocs: 'Documentos',
      activityColComms: 'Comunicados',
      activityColLeaves: 'Licencias',
      activityColExpenses: 'Gastos',
      activityEmpty: 'Sin ítems recientes en esta categoría.',
      activityDemoHint: 'Vista alimentada con datos del tenant (demo).',
      rrhhActionsSubtitle: 'Accesos directos para operar el ciclo RRHH.',
      suiteTitle: 'Suite RRHH',
      suiteSubtitle: 'Estado visual de los módulos principales del tenant (demo + datos cargados).',
      suiteLifecycle: 'Ciclo del colaborador',
      suiteLegajo: 'Legajo y documentos',
      suiteAttendance: 'Asistencia',
      suiteLeaves: 'Licencias',
      suiteExpenses: 'Rendición de gastos',
      suiteEvaluations: 'Evaluaciones',
      suiteTraining: 'Capacitaciones',
      suiteClimate: 'Clima y encuestas',
      suiteComms: 'Comunicaciones',
      suitePF: 'Mis Finanzas',
      suiteStatusActive: 'Activo',
      suiteStatusPrepared: 'Preparado',
      risksTitle: 'Riesgos prioritarios',
      risksBody: 'Consolidación demo: priorizá documentación crítica, ausentismo y cierres de ciclo antes del cierre de mes.',
      risk1: 'Documentación vencida o sin aprobar en colaboradores clave.',
      risk2: 'Licencias superpuestas en equipos operativos.',
      risk3: 'Gastos pendientes de sincronizar con Finance.',
      risk4: 'Evaluaciones de desempeño con etapa manager pendiente.',
      risk5: 'Permisos y Spectra Flow/Auth en preparación — validar antes de producción.',
      periodLabel: 'Período',
      execSummaryTitle: 'Resumen ejecutivo',
      execSummarySubtitle: 'Indicadores clave para la reunión de liderazgo.',
      execActive: 'Colaboradores activos',
      execCompliance: 'Compliance documental',
      execClimate: 'Clima promedio',
      execAttendance: 'Asistencia hoy',
      execTasks: 'Tareas críticas',
      execPi: 'People Intelligence',
      recommendedTitle: 'Acciones recomendadas',
      recommended1: 'Regularizar documentos críticos y vencimientos en Legajo Digital.',
      recommended2: 'Aprobar o responder licencias pendientes.',
      recommended3: 'Revisar tareas vencidas en Notificaciones.',
      recommended4: 'Enviar recordatorios operativos desde Emails RRHH (modo simulación o preparado).',
      recommended5: 'Completar onboarding de nuevas incorporaciones.',
      suitePortal: 'Portal colaborador',
      suitePI: 'People Intelligence',
      suiteReports: 'Reportes ejecutivos',
      moduleStateTitle: 'Estado del módulo',
      moduleStateVersion: 'Versión',
      moduleStateProduct: 'Estado',
      moduleStateProductValue: 'Release Candidate',
      moduleStateMode: 'Modo',
      moduleStateModeValue: 'Demo / producción controlada',
      moduleStateEmail: 'Email RRHH',
      moduleStateEmailDemo: 'Configurado demo / pendiente validación real',
      moduleStateEmailReal: 'Canal real configurado (validar dominios y políticas)',
      moduleStateStorage: 'Storage documental',
      moduleStateStorageMeta: 'Configurado demo (metadata-only)',
      moduleStateStorageReady: 'Spaces listo / pendiente validación productiva',
      moduleStateStoragePending: 'Pendiente validación de bucket y credenciales',
      moduleStateFlow: 'Flow/Auth',
      moduleStateFlowValue: 'Preparado / pendiente conexión real',
      moduleStateFinance: 'Finance',
      moduleStateFinanceValue: 'Preparado / pendiente sync real',
      moduleStateCta: 'Ver checklist en Configuración',
      opsHrTitle: 'Estado operativo de RRHH',
      opsHrSubtitle: 'Accesos rápidos a los pilares del tenant (vista demo + datos cargados).',
      opsHrLink: 'Abrir',
      opsLegajo: 'Legajo',
      opsAttendance: 'Asistencia',
      opsLeaves: 'Licencias',
      opsExpenses: 'Gastos',
      opsComms: 'Comunicaciones',
      opsEval: 'Evaluaciones',
      opsTraining: 'Capacitaciones',
      opsClimate: 'Clima',
      opsReports: 'Reportes',
      demoReadyTitle: 'Listo para demo',
      demoReadySubtitle: 'Checklist visual para la presentación comercial.',
      demoCk1: 'Datos demo cargados en el tenant',
      demoCk2: 'Módulos principales visibles y navegables',
      demoCk3: 'Emails RRHH preparados (sin envío real hasta validación)',
      demoCk4: 'Storage documental preparado (metadata o Spaces según config)',
      demoCk5: 'Roles y permisos definidos en catálogo',
      demoCk6: 'Flow/Auth preparado conceptualmente',
      demoCk7: 'Reportes ejecutivos disponibles en catálogo',
      nextRealTitle: 'Próximos pasos antes de cliente real',
      nextReal1: 'Validar credenciales Email/Resend reales y dominios.',
      nextReal2: 'Validar bucket DigitalOcean Spaces y políticas de retención.',
      nextReal3: 'Conectar Spectra Flow/Auth y tenant_id productivo.',
      nextReal4: 'Validar tenant_id y segregación de datos.',
      nextReal5: 'Definir permisos finales y matriz por rol.',
      nextReal6: 'Probar mobile y tablet con usuarios piloto.',
      nextReal7: 'Plan de backup, rollback y ventana de corte.',
      proOrgHealthTitle: 'Salud organizacional',
      proOrgHealthLead: 'Indicador compuesto orientativo (compliance, Intelligence y clima; demo).',
      proOrgHealthBadge: 'Score People OS',
      proExecKpiTitle: 'KPIs ejecutivos',
      proExecKpiLead: 'Lectura rápida para mesa chica: headcount, operación y talento.',
      proHeadcount: 'Headcount activo',
      proAbsentee: 'Ausentismo estimado',
      proAbsenteeHint: 'Derivado de asistencia y licencias (demo).',
      proLeavesPen: 'Licencias pendientes',
      proClimateShort: 'Clima laboral',
      proEvalsPen: 'Evaluaciones en curso',
      proTrainGap: 'Capacitación en foco',
      proExpensesPen: 'Gastos en pipeline',
      proSmartAlerts: 'Alertas inteligentes',
      proAlert1: 'Documentación crítica con vencimiento próximo.',
      proAlert2: 'Sincronización Finance pendiente en rendiciones aprobadas.',
      proAlert3: 'Tareas críticas en Notificaciones sin cierre.',
      proNextExecTitle: 'Próximas acciones sugeridas',
      proNextExec1: 'Priorizar Legajo y recibos antes del cierre de nómina.',
      proNextExec2: 'Resolver licencias que impactan cobertura y SLA.',
      proNextExec3: 'Publicar comunicado interno de cierre de mes.',
      proRisksExecTitle: 'Riesgos detectados (vista consolidada)',
      proRisksExecLead: 'Señales cruzadas del tenant (demo + datos cargados).',
      proOppsExecTitle: 'Oportunidades de mejora',
      proOppsExecLead: 'Impulsos de cultura y eficiencia con alto retorno percibido.',
      finWellbeingAggregatedNote:
        'El bienestar financiero mostrado aquí es agregado para la demo; los movimientos personales siguen en Mis Finanzas del colaborador.',
    },
    dashboard93: {
      heroEyebrow: 'Consola RRHH',
      greetingMorning: 'Buen día, {name}',
      greetingAfternoon: 'Buenas tardes, {name}',
      greetingEvening: 'Buenas noches, {name}',
      heroStable: 'Tu organización está estable.',
      heroAttention: 'Hay {n} temas que requieren atención.',
      heroFlowCta: 'Abrir Flow IA',
      statusStable: 'Estado general: estable',
      statusWatch: 'Estado general: monitorear pendientes',
      kpiSectionTitle: 'Indicadores clave',
      kpiCritical: 'Pendientes críticos',
      kpiDocHealth: 'Salud documental',
      prioritiesTitle: 'Requiere atención',
      prioritiesLead: 'Pendientes operativos con impacto inmediato.',
      prioritiesEmpty: 'No hay pendientes críticos en este momento.',
      priorityLeaves: 'Licencias pendientes',
      priorityDocs: 'Documentos por vencer o revisar',
      priorityOnboarding: 'Onboarding en curso',
      priorityMissingFile: 'Documentos sin archivo',
      priorityExpenses: 'Rendiciones pendientes',
      priorityCtaReview: 'Revisar',
      priorityCtaOpen: 'Abrir',
      priorityCtaTrack: 'Seguir',
      priorityCtaFix: 'Regularizar',
      priorityCtaApprove: 'Aprobar',
      flowIaLabel: 'Flow IA',
      suggestionBadge: 'Sugerencia de Flow IA',
      flowIaSeeMore: 'Ver más',
      flowIaGoModule: 'Ir al módulo',
      flowIaEmpty: 'Sin señales críticas en este momento.',
      flowIaEmptyAction: 'Podés consultar Flow IA para profundizar.',
      flowIaDocsDue: 'Detecté {n} documento(s) próximos a vencer o en revisión.',
      flowIaDocsDueAction: 'Priorizá Legajo Digital antes del cierre.',
      flowIaLeavesPending: 'Hay {n} licencia(s) pendientes de resolución.',
      flowIaLeavesPendingAction: 'Revisá cobertura y aprobaciones.',
      flowIaMgrLeaves: 'Tenés {n} licencia(s) esperando aprobación.',
      flowIaMgrLeavesAction: 'Tu equipo necesita una respuesta.',
      flowIaExpenses: 'Hay {n} rendición(es) en curso.',
      flowIaExpensesAction: 'Alineá con Finance si corresponde.',
      flowIaPiAction: 'Ver detalle en People Intelligence.',
      flowIaStable: 'Operación estable — sin alertas críticas.',
      flowIaStableAction: 'Flow IA sigue monitoreando el tenant.',
      activityTitle: 'Actividad reciente',
      activityLead: 'Cronología compacta del tenant.',
      activityEmpty: 'Sin actividad reciente para mostrar.',
      activityError: 'No se pudo cargar actividad extendida.',
      activityOpen: 'Ver',
      activityDoc: 'Documento: {title}',
      activityComm: 'Comunicación: {title}',
      activityLeave: 'Licencia: {name}',
      activityExpense: 'Gasto: {name}',
      activityAward: 'Reconocimiento: {title}',
      activityLifecycle: 'Ciclo: {action}',
      legacyAnalyticsSummary: 'Analítica completa y módulos legacy',
    },
    hub93: {
      greeting: 'Hola, {name}',
      navAria: 'Accesos personales',
      navShift: 'Mi jornada',
      navVacation: 'Mis vacaciones',
      navDocs: 'Mis documentos',
      navRequests: 'Mis solicitudes',
      quickTitle: 'Accesos rápidos',
      quickLeave: 'Solicitar licencia',
      quickPayslips: 'Ver recibos',
      quickUpload: 'Subir documento',
      quickAttendance: 'Ver asistencia',
      statusTitle: 'Mi estado',
      statusVacationAvail: 'Próxima licencia',
      statusScheduled: 'Programada',
      statusNone: 'Sin programar',
      statusNextLeave: 'Fecha inicio',
      statusDocsPending: 'Documentos pendientes',
      statusAttendanceToday: 'Asistencia hoy',
      statusAttendanceOpen: 'Consultar',
      statusAttendanceHint: 'Registrá entrada desde Asistencia',
      newsTitle: 'Novedades',
      newsSeeAll: 'Ver comunicaciones',
      newsEmpty: 'Sin novedades recientes.',
      newsError: 'No se pudieron cargar comunicaciones.',
      newsOpen: 'Abrir',
      teamTitle: 'Mi equipo',
      teamError: 'No se pudo cargar información del equipo.',
      teamMembers: 'Miembros',
      teamAbsences: 'Licencias pendientes',
      teamPendingExp: 'Gastos pendientes',
      teamCtaApprovals: 'Revisar aprobaciones',
      flowGo: 'Ir',
      flowDocPending: 'Tenés {n} documento(s) pendiente(s) de firma o carga.',
      flowDocPendingAction: 'Completalos desde tu legajo.',
      flowExpPending: 'Tenés {n} rendición(es) en curso.',
      flowExpPendingAction: 'Seguí el estado en Mis gastos.',
      flowMgrLeaves: 'Tenés {n} licencia(s) esperando tu aprobación.',
      flowMgrLeavesAction: 'Revisá las solicitudes del equipo.',
      flowRrhhDefault: 'Operación estable en tu perfil.',
      flowRrhhDefaultAction: 'Volvé al dashboard operativo cuando lo necesites.',
      flowAllClear: 'Estás al día con tus pendientes.',
      flowAllClearAction: 'Flow IA te avisará si aparece algo nuevo.',
      alertsAria: 'Alertas personales',
      cultureTitle: 'Comunidad',
      cultureEvents: 'Próximos eventos',
      cultureEventsEmpty: 'No hay eventos próximos.',
      cultureRecognitions: 'Reconocimientos recientes',
      cultureRecognitionsEmpty: 'Todavía no hay reconocimientos recientes.',
      cultureAnniversaries: 'Aniversarios próximos',
      cultureAnniversariesEmpty: 'Sin aniversarios en los próximos 30 días.',
      cultureBirthdays: 'Cumpleaños próximos',
      cultureBirthdaysEmpty: 'Sin cumpleaños próximos.',
      cultureFeed: 'Novedades del Muro',
      cultureFeedLink: 'Ver Muro',
      cultureFeedEmpty: 'Todavía no hay publicaciones en el Muro.',
      cultureError: 'No se pudo cargar la comunidad.',
    },
    feedWall: {
      title: 'Muro',
      lead: 'Novedades del equipo — simple, claro y en tu tenant.',
      composerAria: 'Nueva publicación',
      composerPlaceholder: 'Compartí una novedad…',
      audienceLabel: 'Audiencia',
      audienceOrg: 'Toda la organización',
      audienceTeam: 'Mi equipo',
      audienceDepartment: 'Mi área',
      publish: 'Publicar',
      flowIaImprove: 'Mejorar con Flow IA',
      flowIaImprovePrompt: 'Mejorá la redacción de este borrador para el Muro. Sé claro, cercano y breve. Devolvé solo el texto mejorado.',
      empty: 'Todavía no hay publicaciones.',
      emptyCta: 'Crear primera publicación',
      errorLoad: 'No se pudo cargar el Muro.',
      errorPublish: 'No se pudo publicar.',
      errorFlowIa: 'Flow IA no está disponible ahora.',
      forbidden: 'No tenés permiso para ver el Muro.',
      loadMore: 'Cargar más',
      pinned: 'Fijado',
      comment: 'Comentar',
      commentPlaceholder: 'Escribí un comentario…',
      commentSubmit: 'Enviar',
      moreComments: 'Ver más comentarios',
      reactLike: 'Me gusta',
      reactCelebrate: 'Celebrar',
      reactSupport: 'Apoyar',
      kindNormal: 'Publicación',
      kindRecognition: 'Reconocimiento',
      kindCelebration: 'Celebración',
      kindEvent: 'Evento',
      kindCommunication: 'Comunicación',
      asideTitle: 'Accesos',
      asideComms: 'Comunicaciones formales',
      asideCelebrations: 'Reconocimientos',
      asidePortal: 'Portal colaborador',
      asideHint: 'El Muro es interactivo; Communications sigue siendo el canal editorial.',
    },
    chatPage: {
      title: 'Chat',
      lead: 'Conversaciones internas de tu tenant — simple y seguro.',
      forbidden: 'No tenés permiso para ver el Chat.',
      empty: 'Todavía no tenés conversaciones.',
      errorLoad: 'No se pudo cargar el Chat.',
      searchPeople: 'Buscar personas…',
      directLabel: 'Conversación directa',
      unnamed: 'Conversación',
      pickConversation: 'Elegí una conversación o buscá a alguien para empezar.',
      back: 'Volver',
      composerPlaceholder: 'Escribí un mensaje…',
      send: 'Enviar',
      edited: 'editado',
      flowIaSummarize: 'Resumir con Flow IA',
      flowIaSummarizePrompt:
        'Resumí esta conversación de trabajo en 3-5 viñetas claras. Sé breve y neutral. Devolvé solo el resumen.',
      flowIaImprove: 'Mejorar mensaje con Flow IA',
      flowIaImprovePrompt:
        'Mejorá la redacción de este mensaje de chat. Sé claro y profesional. Devolvé solo el texto mejorado.',
    },
    employees: {
      title: 'Colaboradores',
      subtitle: 'Directorio premium con responsables, antigüedad y accesos rápidos al ciclo operativo.',
      summaryActive: 'Activos',
      summaryOnboarding: 'Onboarding',
      summaryManagers: 'Líderes',
      summaryAreas: 'Áreas',
      filtersPlaceholder: 'Filtros por estado y área — próximamente',
      search: 'Buscar por nombre, email o área…',
      new: 'Nuevo colaborador',
      edit: 'Editar',
      delete: 'Eliminar',
      empty: 'No hay colaboradores.',
      modalTitle: 'Colaborador',
      fieldFirstName: 'Nombre',
      fieldLastName: 'Apellido',
      fieldEmail: 'Email',
      fieldPhone: 'Teléfono',
      fieldArea: 'Área',
      fieldDepartment: 'Departamento',
      fieldPosition: 'Puesto',
      fieldStatus: 'Estado',
      fieldEmploymentType: 'Tipo de contrato',
      save: 'Guardar',
      cancel: 'Cancelar',
      legajoHint: 'Legajo preparado',
      viewLegajo: 'Ver legajo',
      columns: {
        name: 'Nombre',
        email: 'Email',
        area: 'Área',
        manager: 'Responsable',
        tenure: 'Ingreso',
        status: 'Estado',
        position: 'Puesto',
        legajo: 'Legajo',
        lifecycle: 'Ciclo',
        progress: 'Progreso doc.',
      },
      viewLifecycle: 'Ciclo',
      manager: 'Responsable',
      tenure: 'Antigüedad',
      quickAttendance: 'Asistencia',
      quickEval: 'Evaluaciones',
      timelineTitle: 'Últimos eventos',
      timelineEmpty: 'Todavía no hay actividad registrada.',
      timeline1: 'Alta registrada en Administración.',
      timeline2: 'Documento cargado en Legajo Digital.',
      timeline3: 'Licencia aprobada por People Ops.',
      timeline4: 'Evaluación de desempeño completada.',
      timeline5: 'Capacitación obligatoria asignada.',
      timeline6: 'Reconocimiento spot bonus publicado.',
      viewProfilePremium: 'Ficha premium',
      profilePremiumClose: 'Cerrar ficha',
      empProfileTitle: 'Perfil del colaborador',
      empProfileLead: 'Vista unificada para People Ops (demo + datos del directorio).',
      empProfileDemoNote: 'Algunos bloques combinan datos reales del listado con métricas ilustrativas.',
      empProfilePersonal: 'Datos personales y contacto',
      empProfileWork: 'Datos laborales',
      empProfileStatus: 'Estado',
      empProfileArea: 'Área',
      empProfileRole: 'Rol / puesto',
      empProfileSeniority: 'Seniority',
      empProfileSeniorityDemo: 'Semi senior (demo)',
      empProfileStart: 'Fecha de ingreso',
      empProfileTenure: 'Antigüedad',
      empProfileManager: 'Manager',
      empProfileDocs: 'Documentos',
      empProfileDocsHint: 'Ver Legajo para estados, vencimientos y firma.',
      empProfileLeaves: 'Licencias',
      empProfileLeavesHint: 'Ver módulo Licencias para solicitudes y aprobaciones.',
      empProfileEval: 'Evaluaciones',
      empProfileEvalHint: 'Ver Desempeño para ciclos y feedback.',
      empProfileTrain: 'Capacitaciones',
      empProfileTrainHint: 'Ver Capacitaciones para asignaciones y compliance.',
      empProfileComms: 'Comunicaciones',
      empProfileCommsHint: 'Comunicados internos y lecturas (demo).',
      empProfileExpenses: 'Gastos / rendiciones',
      empProfileExpensesHint: 'Ver Rendiciones para montos y aprobadores.',
      empProfileTimeline: 'Timeline del colaborador',
      empProfileNext: 'Próximas acciones sugeridas',
      empProfileNext1: 'Confirmar documentación crítica al día.',
      empProfileNext2: 'Coordinar 1:1 con manager en la próxima quincena.',
      empProfileNext3: 'Revisar capacitaciones obligatorias pendientes.',
      tenureMonthShort: 'meses (aprox.)',
      tenureUnderOne: '< 1 mes (aprox.)',
    },
    identityLink: {
      title: 'Cuenta Spectra',
      statusLabel: 'Estado de vinculación',
      invite: 'Invitar a Spectra',
      linkExisting: 'Vincular usuario existente',
      resend: 'Reenviar invitación',
      suspend: 'Suspender acceso',
      revoke: 'Revocar vínculo',
      retrySync: 'Reintentar sincronización',
      confirmTitle: 'Confirmar vínculo',
      confirmLead: 'Ingresá el identificador de usuario Flow. El backend validará permisos y tenant.',
      flowUserId: 'Flow user ID',
      confirmLink: 'Confirmar vínculo',
      actionOk: 'Operación registrada correctamente.',
      actionError: 'No se pudo completar la operación.',
      status: {
        unlinked: 'Sin cuenta',
        invited: 'Invitación pendiente',
        pending_claim: 'Confirmación pendiente',
        linked: 'Vinculado',
        suspended: 'Suspendido',
        revoked: 'Revocado',
      },
    },
    analytics: {
      workforceTitle: 'Fuerza laboral',
      workforceLead: 'Headcount y distribución por área a partir de colaboradores registrados.',
      asOf: 'Corte',
      source: 'Fuente',
      kpiTotal: 'Headcount total',
      kpiActive: 'Activos',
      kpiHires30: 'Altas últimos 30 días',
      byArea: 'Distribución por área',
      headcountTrend: 'Evolución de headcount',
      period: 'Período',
      headcount: 'Headcount',
      errorLoad: 'No pudimos cargar analíticas.',
      insufficientData: 'Datos insuficientes para analíticas de fuerza laboral.',
    },
    reportsPage: {
      eyebrow: 'Intelligence & reporting',
      title: 'Reportes RRHH',
      lead: 'Resumen operativo y analíticas autorizadas para tu tenant.',
      navLabel: 'Secciones de reportes',
      tabSummary: 'Resumen',
      tabAnalytics: 'Analíticas',
      atAGlanceLeadProd: 'Indicadores calculados desde datos reales del tenant (sin finanzas personales).',
      agActive: 'Colaboradores activos',
      catalogTitle: 'Catálogo de reportes',
      openAnalytics: 'Abrir analíticas de fuerza laboral →',
      note: 'Los datos mostrados son de demostración. La exportación real se habilitará sin fricción cuando el motor de reporting esté conectado.',
      atAGlanceTitle: 'Panorama ejecutivo (demo)',
      atAGlanceLead:
        'Números ilustrativos para mesa directiva y People Ops. Mis Finanzas es privado del colaborador y no entra en estos cortes.',
      agHeadcount: 'Headcount por área (estable)',
      agAbsenteeism: 'Ausentismo período',
      agLeaves: 'Licencias en curso',
      agPerf: 'Evaluaciones activas',
      agClimate: 'Clima laboral (pulso)',
      agTraining: 'Capacitación / compliance',
      agExpenses: 'Rendiciones en pipeline',
      agDocs: 'Documentos pendientes revisión',
      agComms: 'Comunicaciones internas',
      agExecSignals: 'Señales Intelligence',
      rcLine: 'v0.3.5 — Catálogo ejecutivo con Flow IA y lectura responsive.',
      badgeRC: 'v0.3.5',
      execPackTitle: 'Pack ejecutivo para dirección',
      execPackLead: 'Cuatro lecturas sugeridas para una reunión de mesa chica.',
      preview: 'Vista previa',
      previewHint: 'La vista previa interactiva se habilitará con el motor de reporting.',
      exportHint: 'La exportación masiva llegará en la siguiente etapa sin rearmar permisos.',
      badgeCatalog: 'Catálogo demo',
      kpisTitle: 'Resumen del catálogo',
      kpiAvailable: 'Reportes disponibles',
      kpiUpdated: 'Última actualización',
      kpiUpdatedValue: 'Hoy',
      kpiAreas: 'Áreas cubiertas',
      kpiRisks: 'Riesgos detectados',
      kpiRisksValue: 'Bajo control',
      catalogSectionTitle: 'Reportes listos para revisión',
      metaFreq: 'Frecuencia',
      metaAudience: 'Público recomendado',
      previewSoon: 'Ver preview',
      exportSoon: 'Exportar próximamente',
      footerHint: '¿Necesitás otro corte? People Intelligence consolida alertas mientras definimos el motor de reporting.',
      linkPi: 'Ir a People Intelligence',
      linkDash: 'Volver al dashboard',
      privacyOrgTitle: 'Privacidad y alcance de reportes',
      privacyOrgBody:
        'Los reportes de empresa consolidan headcount, asistencia, licencias, clima, capacitación y gastos aprobados. Mis Finanzas es privado del colaborador y no forma parte de estos cortes.',
      execInsightsTitle: 'Insights ejecutivos (demo)',
      execInsight1: 'Headcount estable con leve concentración en Comercial y Soporte.',
      execInsight2: 'Ausentismo dentro de parámetros; monitorear solapamientos de licencias.',
      execInsight3: 'Clima estable con oportunidad de pulso post-release.',
      execInsight4: 'Capacitación obligatoria con brecha cerrable en 30 días.',
      execInsight5: 'Gastos con pipeline limpio hacia Finance cuando se active sync real.',
      execInsight6: 'People Intelligence como capa de lectura cruzada sin exponer datos personales sensibles.',
      freqWeekly: 'Semanal',
      freqBiweekly: 'Quincenal',
      freqMonthly: 'Mensual',
      freqQuarterly: 'Trimestral',
      freqSemiannual: 'Semestral',
      audienceOps: 'Operaciones y líderes de equipo',
      audienceManagers: 'Managers y líneas de negocio',
      audienceCompliance: 'Compliance y People Ops',
      audienceFinance: 'Finanzas y control de gastos',
      audienceLeadership: 'Dirección y cultura',
      audiencePeople: 'People Operations',
      audienceDirection: 'Mesa directiva',
      statusReady: 'Listo para preview',
      statusPrepared: 'En preparación',
      items: {
        attendance: {
          title: 'Reporte de asistencia',
          desc: 'Presentismo, remoto, tardanzas y horas por área.',
        },
        leaves: {
          title: 'Reporte de licencias',
          desc: 'Solicitudes, aprobaciones y solapamientos proyectados.',
        },
        docs: {
          title: 'Reporte documental',
          desc: 'Compliance, vencimientos y documentos sin archivo en storage.',
        },
        expenses: {
          title: 'Reporte de gastos',
          desc: 'Rendiciones por estado, monto y sincronización con Finance.',
        },
        climate: {
          title: 'Reporte de clima',
          desc: 'eNPS, participación y comentarios cualitativos agregados.',
        },
        performance: {
          title: 'Reporte de desempeño',
          desc: 'Ciclos, calibraciones y brechas por competencia.',
        },
        training: {
          title: 'Reporte de capacitaciones',
          desc: 'Asignaciones, avance y cumplimiento regulatorio.',
        },
        monthly: {
          title: 'Reporte mensual RRHH',
          desc: 'Corte mensual de personas, riesgos, documentación y acciones prioritarias.',
        },
        culture: {
          title: 'Reporte de cultura',
          desc: 'Reconocimientos, valores vividos y señales de colaboración entre áreas.',
        },
        audit: {
          title: 'Reporte de auditoría y permisos',
          desc: 'Matriz de accesos, cambios sensibles y trazabilidad para compliance.',
        },
        executive: {
          title: 'Reporte ejecutivo mensual',
          desc: 'Resumen único para dirección: personas, riesgos y acciones.',
        },
      },
    },
    legajo: {
      heroTitle: 'Legajo Digital y Documentos',
      heroSubtitle: 'Centralizá documentación laboral, recibos, certificados y compliance documental.',
      suiteEyebrow: 'Suite documental',
      metadataHint:
        'Los archivos reales se guardan en storage externo cuando Spaces o S3 está activo. MongoDB conserva solo metadata y estados de workflow.',
      secureStorageBanner:
        'Preparado para Spaces/S3: la base de datos no almacena archivos pesados ni base64; solo metadata, estados y referencias seguras.',
      kpiPending: 'Pendientes',
      kpiExpired: 'Vencidos',
      kpiApproved: 'Aprobados',
      kpiReceipts: 'Recibos disponibles',
      kpiCompliance: 'Compliance documental',
      kpiMissingFile: 'Docs. requeridos sin archivo',
      tabsAria: 'Secciones del legajo',
      tab_documentos: 'Documentos',
      tab_recibos: 'Recibos digitales',
      tab_vencimientos: 'Vencimientos',
      tab_compliance: 'Compliance',
      tab_storage: 'Storage',
      catalogTitle: 'Tipos de documento laboral',
      catalogSubtitle: 'Referencia estándar Spectra RRHH. El estado en tabla refleja metadata cargada.',
      catalog_dni: 'DNI / Identificación',
      catalog_contract: 'Contrato laboral',
      catalog_labor: 'Alta laboral',
      catalog_bank: 'CBU / datos bancarios',
      catalog_cert_med: 'Certificados médicos',
      catalog_cv: 'CV',
      catalog_policies: 'Políticas firmadas',
      catalog_evaluation: 'Evaluaciones',
      catalog_training: 'Capacitaciones',
      receiptColPeriod: 'Período',
      receiptColEmployee: 'Colaborador',
      receiptColArea: 'Área',
      receiptColStatus: 'Estado',
      receiptColIssued: 'Emisión',
      receiptColFile: 'Archivo',
      receiptColActions: 'Acciones',
      receiptFileMeta: 'Solo metadata',
      receiptActionMeta: 'Ver metadata',
      receiptActionSign: 'Solicitar firma',
      receiptActionAvailable: 'Marcar disponible',
      receiptStatus_available: 'Disponible',
      receiptStatus_pending: 'Pendiente',
      receiptStatus_draft: 'Borrador',
      receiptStatus_signed: 'Firmado',
      receiptStatus_observed: 'Observado',
      receiptStatus_archived: 'Archivado',
      receiptKpiAvailable: 'Recibos disponibles',
      receiptKpiDraft: 'Borradores',
      receiptKpiSigned: 'Firmados',
      receiptKpiObserved: 'Observados',
      receiptKpiLastPeriod: 'Último período',
      receiptsStorageNote:
        'Los recibos se guardan como metadata y archivo en object storage cuando DigitalOcean Spaces está activo (RRHH_STORAGE_MODE=spaces).',
      receiptColGross: 'Bruto',
      receiptColNet: 'Neto',
      receiptActionAttach: 'Adjuntar recibo',
      newPayslip: 'Nuevo recibo',
      payslipFieldMonth: 'Mes (1–12)',
      payslipFieldYear: 'Año',
      payslipFieldGross: 'Bruto (ARS)',
      payslipFieldNet: 'Neto (ARS)',
      docUploadTitle: 'Adjuntar archivo al documento',
      receiptUploadTitle: 'Adjuntar archivo al recibo',
      uploadHintMetadataOnly:
        'El storage real no está activado. Se registrará metadata del archivo (nombre, tamaño, MIME), no el binario.',
      uploadHintReady: 'Podés adjuntar el archivo. Se validará tamaño y tipo MIME antes de subir a Spaces.',
      uploadMaxLabel: 'Tamaño máximo:',
      uploadTypesLabel: 'Tipos permitidos:',
      downloadUnavailable: 'Enlace de descarga no disponible en este modo.',
      docType_identification: 'Identificación / DNI',
      docType_contract: 'Contrato laboral',
      docType_labor_registration: 'Alta laboral',
      docType_bank_account: 'CBU / Datos bancarios',
      docType_medical_certificate: 'Certificado médico',
      docType_cv: 'CV',
      docType_policy: 'Política firmada',
      docType_payslip: 'Recibo de sueldo',
      docType_training_certificate: 'Certificado de capacitación',
      docType_performance_review: 'Evaluación de desempeño',
      docType_other: 'Otro',
      filterEmployee: 'Colaborador',
      expiringTitle: 'Próximos vencimientos',
      expColDoc: 'Documento',
      expColEmployee: 'Colaborador',
      expColDue: 'Vencimiento',
      expColUrgency: 'Urgencia',
      expColNext: 'Próxima acción',
      urgency_high: 'Alta',
      urgency_medium: 'Media',
      urgency_low: 'Baja',
      nextRenewMedical: 'Coordinar renovación con prestador y RRHH.',
      nextUpdateId: 'Solicitar copia actualizada al colaborador.',
      nextResignPolicies: 'Enviar pack de políticas para firma electrónica.',
      complianceGlobalPct: 'Cumplimiento global',
      complianceCompleteEmployees: 'Colaboradores completos',
      compliancePendingEmployees: 'Colaboradores con pendientes',
      complianceCriticalMissing: 'Documentos críticos faltantes',
      complianceMissingBinary: 'Sin archivo en storage',
      complianceTabIntro: 'Vista ejecutiva calculada sobre metadata y reglas demo.',
      complianceBlockTitle: 'Compliance documental',
      complianceBlockBody:
        'Indicadores calculados sobre metadata del tenant. Los archivos reales se conectarán a S3/DigitalOcean Spaces.',
      filterStatus: 'Estado',
      filterType: 'Tipo de documento',
      filterRequired: 'Requerido',
      allRequired: 'Todos (requerido)',
      requiredYes: 'Solo requeridos',
      requiredNo: 'No requeridos',
      allEmployees: 'Todos los colaboradores',
      allStatuses: 'Todos los estados',
      allTypes: 'Todos los tipos',
      newDocument: 'Nuevo documento',
      editDocument: 'Editar documento',
      emptyTitle: 'Sin documentos con los filtros actuales',
      emptyDesc: 'Creá un registro de metadata o ajustá los filtros. El almacenamiento binario llegará con S3/Spaces.',
      colEmployee: 'Colaborador',
      colType: 'Tipo',
      colTitle: 'Título',
      colStatus: 'Estado',
      colRequired: 'Requerido',
      colExpires: 'Vencimiento',
      colStorage: 'Storage',
      colFileState: 'Estado archivo',
      actionMeta: 'Ver metadata',
      actionAttach: 'Adjuntar archivo',
      actionEdit: 'Editar',
      actionApprove: 'Aprobar',
      actionReject: 'Rechazar',
      actionPending: 'Marcar pendiente',
      actionExpired: 'Marcar vencido',
      actionDownload: 'Obtener enlace',
      rejectPrompt: 'Motivo del rechazo (opcional)',
      rejectDefault: 'Rechazado por RRHH',
      modalNote: 'Solo metadata: sin upload binario en esta versión.',
      modalNoteV23:
        'Creación de registro documental (metadata). Para archivo binario usá “Adjuntar archivo”; con Spaces activo se sube a object storage.',
      fieldEmployee: 'Colaborador',
      fieldType: 'Tipo de documento',
      fieldTitle: 'Título',
      fieldDescription: 'Descripción',
      fieldRequired: 'Documento requerido',
      fieldStatus: 'Estado',
      fieldExpires: 'Vencimiento (opcional)',
      fieldStorage: 'Proveedor de almacenamiento',
      fieldUrl: 'URL externa (opcional)',
      fieldFileName: 'Nombre de archivo (opcional)',
      metaTitle: 'Metadata del documento',
      closeMeta: 'Cerrar',
      yes: 'Sí',
      no: 'No',
      status: {
        pending: 'Pendiente',
        uploaded: 'Subido',
        approved: 'Aprobado',
        rejected: 'Rechazado',
        expired: 'Vencido',
      },
      storage: {
        pending: 'Pendiente',
        s3: 'Amazon S3',
        digitalocean_spaces: 'DigitalOcean Spaces',
        external_url: 'URL externa',
        metadata_only: 'Solo metadata',
      },
      fileState: {
        pending: 'Sin archivo',
        metadata_only: 'Solo metadata (sin binario)',
        uploaded: 'Archivo en Spaces',
        external: 'URL externa',
        error: 'Error storage',
        spaces_ready: 'Preparado para Spaces',
      },
      storageTabTitle: 'Configuración de storage documental',
      storageTabIntro:
        'MongoDB guarda metadata y referencias. Los PDF e imágenes van a DigitalOcean Spaces (S3 compatible) cuando el modo spaces esté activo y completo.',
      storageFieldMode: 'Modo',
      storageFieldStatus: 'Estado runtime',
      storageFieldProvider: 'Proveedor configurado',
      storageFieldMax: 'Tamaño máximo de subida',
      storageFieldMimes: 'MIME permitidos',
      storageFieldBucket: 'Bucket configurado',
      storageFieldCreds: 'Credenciales configuradas',
      storageFutureNote:
        'Próximos pasos: URLs firmadas con auditoría, permisos por rol y políticas de retención documental.',
      storageMode_metadata_only: 'Solo metadata',
      storageMode_spaces: 'Spaces / S3',
      storageRuntime_metadata_only: 'Solo metadata (sin subida remota)',
      storageRuntime_ready: 'Listo para subir archivos',
      storageRuntime_missing_config: 'Falta configuración (variables DO_SPACES_*)',
      storageProvider_digitalocean_spaces: 'DigitalOcean Spaces',
    },
    orgChart: {
      title: 'Organigrama',
      subtitle: 'Vista por área y departamento.',
      manager: 'Líder',
      team: 'Equipo',
      empty: 'Sin datos de organigrama.',
    },
    leaves: {
      title: 'Licencias',
      subtitle: 'Solicitudes, aprobaciones y calendario de ausencias.',
      heroLead: 'Vista operativa de licencias con KPIs, filtros y calendario de cobertura.',
      suiteHeroEyebrow: 'Ausencias y cobertura',
      suiteHeroTitle: 'Licencias y tiempo fuera',
      filterType: 'Tipo de licencia',
      filterStatus: 'Estado',
      filterEmployee: 'Colaborador',
      filterPeriod: 'Período (mes)',
      kpiUpcoming: 'Próximas ausencias',
      kpiApprovedMonth: 'Aprobadas este mes',
      calTitle: 'Calendario operativo (semana)',
      calHint: 'Vista simplificada de cobertura; integración calendario completa en roadmap.',
      impactLow: 'Impacto bajo',
      impactMed: 'Impacto medio',
      impactHigh: 'Impacto alto',
      summaryPending: 'Pendientes',
      summaryApproved: 'Aprobadas',
      summaryRejected: 'Rechazadas',
      summaryDays: 'Días solicitados',
      summaryUpcoming: 'Próximas ausencias',
      upcomingTitle: 'Próximas ausencias en calendario',
      upcomingEmpty: 'No hay ausencias aprobadas o pendientes con fecha futura.',
      new: 'Nueva solicitud',
      approve: 'Aprobar',
      reject: 'Rechazar',
      empty: 'No hay solicitudes.',
      type: 'Tipo',
      dates: 'Fechas',
      days: 'Días',
      status: 'Estado',
      employee: 'Colaborador',
      pickEmployee: 'Colaborador',
      dateFrom: 'Desde',
      dateTo: 'Hasta',
      reason: 'Motivo',
      loading: 'Cargando licencias…',
      errorLoad: 'No se pudieron cargar las licencias.',
    },
    expenses: {
      title: 'Rendición de gastos',
      subtitle: 'Flujo demo con estados y Finance.',
      financeBanner: 'Preparado para sincronizar con Spectra Finance.',
      financeFlowLong:
        'Este flujo está preparado para sincronizar gastos aprobados con Spectra Finance.',
      timelineTitle: 'Flujo de aprobación',
      timelineDraft: 'Borrador',
      timelineSent: 'Enviado',
      timelineManager: 'Manager',
      timelineFinance: 'Finanzas',
      timelinePaid: 'Pagado',
      metricsManager: 'Pendiente manager',
      metricsFinance: 'Pendiente finanzas',
      summaryPending: 'Pendientes',
      summaryApproved: 'Aprobados',
      summaryUnpaid: 'Sin pagar',
      summaryTotal: 'Total rendido',
      new: 'Nuevo gasto',
      syncFinance: 'Sincronizar con Finance',
      financeSync: 'Sincronización Finance',
      notSynced: 'No sincronizado',
      prepared: 'Preparado',
      pending: 'Pendiente',
      synced: 'Sincronizado',
      error: 'Error',
      empty: 'Sin rendiciones.',
      amount: 'Monto',
      category: 'Categoría',
      status: 'Estado',
      submit: 'Enviar',
      managerApprove: 'Aprobar manager',
      financeApprove: 'Aprobar finanzas',
      markPaid: 'Marcar pagado',
      reject: 'Rechazar',
      heroLead: 'Flujo de aprobación demo: montos, categorías y estados listos para integrar con Spectra Finance.',
      suiteHeroEyebrow: 'Finance-ready',
      suiteHeroTitle: 'Rendición de gastos',
      kpiPendingSync: 'Pendiente de sincronizar con Finance',
      status_draft: 'Borrador',
      status_submitted: 'Enviada',
      status_manager_approved: 'Aprobada por manager',
      status_finance_approved: 'Aprobada por finanzas',
      status_rejected: 'Rechazada',
      status_paid: 'Pagada',
      status_cancelled: 'Cancelada',
      financeReadyTitle: 'Preparado para Spectra Finance',
      financeReady1: 'Gasto aprobado y listo para export',
      financeReady2: 'Revisión finanzas en bandeja unificada',
      financeReady3: 'Sincronización futura vía API',
      financeReady4: 'Reembolso / pago registrado en Finance',
      kpiTotal: 'Total rendido',
      kpiPendingApproval: 'Pendiente de aprobación',
      kpiApprovedFinance: 'Aprobado (finanzas)',
      kpiPaid: 'Pagado',
      kpiRejected: 'Rechazado',
      fieldEmployee: 'Colaborador',
      fieldCurrency: 'Moneda',
      fieldDescription: 'Descripción',
      fieldExpenseDate: 'Fecha del gasto',
      amountCentsHint: 'Monto en centavos (ej. 10000 = ARS 100,00)',
      financeNextTitle: 'Integración futura con Spectra Finance',
      financeNextLead:
        'Preparado para sincronizar rendiciones aprobadas: metadata, estado y finance_sync_status quedarán alineados con Finance.',
      loading: 'Cargando rendiciones…',
      mobileCardsTitle: 'Vista móvil',
      mobileCardsHint: 'Misma información en tarjetas compactas para pantallas chicas.',
      nextActionsTitle: 'Próximas acciones sugeridas',
      nextAction1: 'Cerrar pendientes de manager antes del cierre.',
      nextAction2: 'Validar categorías y montos para Finance.',
      nextAction3: 'Marcar pagados cuando el pago esté acreditado.',
    },
    personalFinance: {
      title: 'Mis Finanzas',
      subtitle: 'Tu panel financiero personal.',
      heroTitle: 'Bienestar financiero personal',
      heroLead:
        'Tus movimientos son privados. La empresa no ve tus ingresos, gastos ni deudas personales.',
      privacyHighlight:
        'Tus movimientos son privados. La empresa no ve tus ingresos, gastos ni deudas personales.',
      privacyCompany:
        'Estos datos son privados del colaborador y no visibles para la empresa.',
      privacyBannerMain:
        'Tus datos financieros personales son privados. La empresa no puede ver tus ingresos, gastos, deudas ni movimientos.',
      privacyLearnMore: 'Cómo protegemos tus datos',
      privacyLearnBody:
        'Solo vos podés ver y editar tus movimientos. RRHH no usa esta información para decisiones laborales. Flow IA no incluye montos salvo que lo pidas en esta pantalla.',
      pfSecureHint: 'Los movimientos se guardan asociados a tu usuario; no hay listados administrativos globales.',
      tipsTitle: 'Consejos financieros',
      tip1: 'Definí un presupuesto mensual y revisalo cada semana.',
      tip2: 'Separá gastos fijos y variables para anticipar el mes.',
      tip3: 'Creá una meta de ahorro y automatizá un débito recurrente.',
      goalsTitle: 'Metas',
      goalPlaceholder: 'Definí metas de ahorro y seguimiento en próximas versiones.',
      sectionBudgetTitle: 'Presupuesto mensual',
      budgetUsed: 'Usado',
      budgetAvailable: 'Disponible',
      budgetExceeded: 'Excedido',
      budgetDemoHint: 'Distribución ilustrativa basada en tus movimientos del mes.',
      goalsDetailTitle: 'Metas de ahorro',
      goalEmergency: 'Fondo de emergencia (6 meses de gastos)',
      goalVacation: 'Vacaciones',
      goalEducation: 'Educación',
      goalDebt: 'Cancelación de deuda',
      recoBlockTitle: 'Recomendaciones de bienestar financiero',
      reco1: 'Reservá al menos 10% de tus ingresos para ahorro sistemático.',
      reco2: 'Revisá gastos recurrentes (streaming, apps) cada trimestre.',
      reco3: 'Priorizá fondo de emergencia antes de inversiones de mayor riesgo.',
      recurringIn: 'Ingresos recurrentes',
      recurringOut: 'Gastos recurrentes',
      categorySplit: 'Gastos por categoría',
      debt: 'Deuda',
      roadmapTitle: 'Próximamente',
      roadmapBody:
        'Educación financiera, metas guiadas y alertas inteligentes para fortalecer hábitos saludables.',
      privacySection: 'Privacidad',
      privacyTitle: 'Privacidad',
      privacyBody:
        'Esta información es privada del colaborador. La empresa no ve tus movimientos personales.',
      incomeMonth: 'Ingresos del mes',
      expenseMonth: 'Gastos del mes',
      balance: 'Saldo estimado',
      savings: 'Ahorro estimado',
      healthScore: 'Salud financiera',
      transactions: 'Movimientos',
      newIncome: 'Nuevo ingreso',
      newExpense: 'Nuevo movimiento',
      fieldType: 'Tipo',
      fieldAmountCents: 'Monto (centavos)',
      fieldCurrency: 'Moneda',
      fieldCategory: 'Categoría personal',
      fieldDescription: 'Descripción',
      empty: 'Sin movimientos.',
      loading: 'Cargando tu información financiera…',
      summaryTitle: 'Resumen financiero personal',
      summaryIntro: 'Una vista clara de salud, balance y hábitos del mes.',
      summaryHealth: 'Salud financiera',
      summaryBalance: 'Balance estimado',
      summaryCompare: 'Ingresos vs gastos',
      summarySavingsLine: 'Ahorro estimado',
      summaryNextTitle: 'Próximos movimientos y recomendaciones',
      wellbeingTitle: 'Bienestar financiero',
      wellbeingSubtitle: 'Score integral basado en ingresos, gastos y ahorro (demo).',
      metricBalanceMonth: 'Saldo del mes',
      metricIncomeApprox: 'Ingresos aproximados',
      metricExpenseApprox: 'Gastos aproximados',
      metricSavingsApprox: 'Ahorro estimado',
      metricDebtGap: 'Brecha vs ingresos',
      emptySummaryTitle: 'Sin datos para mostrar',
      emptySummaryDesc: 'Cuando cargues movimientos vas a ver tendencias y recomendaciones aquí.',
      barIncome: 'Ingresos',
      barExpense: 'Gastos',
      txType: {
        income: 'Ingreso',
        expense: 'Gasto',
      },
      pfTabSummary: 'Resumen',
      pfTabBudget: 'Presupuesto',
      pfTabMovements: 'Movimientos',
      pfTabGoals: 'Metas de ahorro',
      pfTabRecurring: 'Recurrentes',
      pfTabDebts: 'Deudas / Compromisos',
      pfTabProjection: 'Proyección',
      pfTabReco: 'Recomendaciones',
      pfDemoNote:
        'Presupuesto extendido y ajustes locales se guardan en tu navegador (demo). Metas y deudas de ejemplo no se envían a reportes de empresa.',
      pfCreateBudget: 'Crear presupuesto',
      pfBudgetMonthlyTotal: 'Presupuesto mensual total (ARS, orientativo)',
      pfBudgetFieldArs: 'Monto mensual (ARS)',
      pfColCategory: 'Categoría',
      pfColBudgeted: 'Presupuestado',
      pfColSpent: 'Gastado',
      pfColAvailable: 'Disponible',
      pfColPct: '% usado',
      pfColStatus: 'Estado',
      pfColActions: 'Acciones',
      pfStatusOk: 'Bien',
      pfStatusWarn: 'Atención',
      pfStatusOver: 'Excedido',
      pf503020Title: 'Regla 50 / 30 / 20',
      pf503020Need: 'Necesidades (50%)',
      pf503020Want: 'Deseos (30%)',
      pf503020Save: 'Ahorro / deuda (20%)',
      pf503020Ideal: 'Objetivo',
      pf503020Your: 'Tu reparto estimado (demo)',
      pfCat_housing: 'Vivienda',
      pfCat_food: 'Alimentación',
      pfCat_transport: 'Transporte',
      pfCat_health: 'Salud',
      pfCat_education: 'Educación',
      pfCat_services: 'Servicios',
      pfCat_debts: 'Deudas',
      pfCat_leisure: 'Ocio',
      pfCat_savings: 'Ahorro',
      pfCat_other: 'Otros',
      pfBudgetModalTitle: 'Presupuesto mensual (demo)',
      pfBudgetModalLead: 'Ajustá montos en pesos argentinos por categoría. Se guarda en tu navegador (demo); no en el servidor.',
      pfBudgetModalClose: 'Listo',
      pfBudgetPersistHint: 'Los montos del presupuesto se guardan solo en tu navegador (demo).',
      pfBudgetToastSaved: 'Presupuesto guardado en tu dispositivo',
      pfBudgetResetConfirm: '¿Restablecer el presupuesto demo a valores sugeridos y quitar categorías extra?',
      pfBudgetResetDone: 'Presupuesto restablecido',
      pfBudgetResetBtn: 'Restablecer demo',
      pfBudgetAddCategory: 'Agregar categoría',
      pfBudgetExtraLabel: 'Nombre de la categoría',
      pfBudgetExtraCap: 'Tope mensual (ARS)',
      pfBudgetDeleteRow: 'Quitar',
      pfBudgetEditCap: 'Editar tope',
      pfBudgetConfirmDeleteExtra: '¿Quitar esta categoría del presupuesto?',
      pfBudgetEstIncome: 'Ingreso mensual estimado (demo, local)',
      pfBudgetEstExpense: 'Gasto mensual estimado (demo, local)',
      pfAlertsTitle: 'Alertas orientativas',
      pfAlertHighSpend: 'Gasto del mes alto respecto a ingresos estimados.',
      pfAlertLowSave: 'Ahorro estimado bajo: conviene revisar prioridades.',
      pfAlertOverBudget: 'Gastos superan ingresos del período (déficit estimado).',
      pfAlertDebtDue: 'Hay un compromiso con vencimiento próximo (demo).',
      pfDistTitle: 'Distribución simple del gasto',
      pfDistNeeds: 'Necesidades',
      pfDistWants: 'Deseos',
      pfDistSaveDebt: 'Ahorro / amortización',
      pfScoreTitle: 'Score de salud financiera',
      pfPrivacyRepeat: 'Estos datos son privados del colaborador y no visibles para la empresa.',
      pfFilterAll: 'Todos',
      pfFilterIncome: 'Solo ingresos',
      pfFilterExpense: 'Solo gastos',
      pfFilterCategory: 'Categoría',
      pfFilterMonth: 'Mes',
      pfFilterMonthAll: 'Todos los meses',
      pfBtnLoadIncome: 'Cargar ingreso',
      pfBtnLoadExpense: 'Cargar gasto',
      pfTableDate: 'Fecha',
      pfTableType: 'Tipo',
      pfTableCategory: 'Categoría',
      pfTableAmount: 'Monto',
      pfGoalTarget: 'Objetivo',
      pfGoalSaved: 'Acumulado',
      pfGoalPct: 'Progreso',
      pfGoalDue: 'Fecha objetivo',
      pfGoalMonthly: 'Aporte mensual sugerido',
      pfGoalStatus: 'Estado',
      pfGoalStatusOn: 'En curso',
      pfGoalStatusRisk: 'En riesgo',
      pfGoalStatusDone: 'Completada',
      pfNewGoal: 'Nueva meta',
      pfGoalInsight: 'Para avanzar hacia esta meta, un aporte orientativo es de {amount}/mes.',
      pfRecurKpiIn: 'Ingresos fijos',
      pfRecurKpiOut: 'Gastos fijos',
      pfRecurKpiMargin: 'Margen disponible',
      pfRecurKpiSubs: 'Suscripciones activas (demo)',
      pfRecurAlertsTitle: 'Alertas recurrentes',
      pfRecurAlertHigh: 'Gasto fijo elevado respecto al ingreso fijo (demo).',
      pfRecurAlertSub: 'Revisá suscripciones con bajo uso.',
      pfRecurAlertDue: 'Compromiso con vencimiento próximo.',
      pfDebtStrategyTitle: 'Estrategia sugerida (orientativa)',
      pfDebtAvalanche: 'Método avalancha: priorizar mayor tasa o costo financiero.',
      pfDebtSnowball: 'Método bola de nieve: cerrar primero deudas chicas para liberar flujo.',
      pfDebtHint:
        'Si la tasa es alta, suele ser razonable priorizar el costo financiero; si necesitás liberar efectivo rápido, el enfoque por montos chicos puede ayudar. No constituye asesoramiento personalizado.',
      pfProjTitle: 'Proyección a 3 meses (demo)',
      pfProjBalance: 'Saldo estimado',
      pfProjSavings: 'Ahorro acumulado',
      pfProjCommit: 'Compromisos',
      pfProjDeficitRisk: 'Riesgo de déficit',
      pfProjScenarioCons: 'Conservador',
      pfProjScenarioBase: 'Base',
      pfProjScenarioOpt: 'Optimista',
      pfProjSimSave: 'Si ahorro {amount} más por mes…',
      pfProjSimCut: 'Si reduzco gastos un {pct}%…',
      pfRecoMbaTitle: 'Recomendaciones de bienestar financiero',
      pfRecoMba1: 'Aumentar fondo de emergencia de forma gradual.',
      pfRecoMba2: 'Revisar gastos recurrentes y suscripciones.',
      pfRecoMba3: 'Separar presupuesto por categorías y revisarlo semanalmente.',
      pfRecoMba4: 'Automatizar un ahorro fijo al inicio del mes.',
      pfRecoMba5: 'Evitar endeudamiento caro sin plan de amortización.',
      pfRecoMba6: 'Priorizar educación financiera y hábitos de registro.',
      pfRecoMba7: 'Definir una meta mensual concreta y medible.',
      pfRecoFlowHint: 'Usá Flow IA arriba para preguntas puntuales sobre tu situación (sin datos sensibles en el chat).',
      pfDisclaimerAdvice:
        'Las recomendaciones y simulaciones son orientativas y no constituyen asesoramiento financiero profesional ni legal.',
      pfPrivacyModule:
        'Mis Finanzas es un espacio privado del colaborador. La empresa no ve tus movimientos, metas ni deudas personales.',
      pfPrivacyAggregates: 'Futuro: reportes agregados y anonimizados para la organización, sin datos individuales.',
      pfDemoGoalPurchase: 'Compra importante',
      pfDebtInstallment: 'Cuota orientativa',
      pfDebtRateLabel: 'Tasa (demo)',
      pfDebtDueLabel: 'Vencimiento',
      pfDebtProgressLabel: 'Progreso pagado',
      pfDebtPriorityLabel: 'Prioridad',
      pfDebtPri_high: 'Alta',
      pfDebtPri_medium: 'Media',
      pfProjScenarioCol: 'Escenario',
      pfProjMonthN: 'Mes {n}',
      pfRecur_salary: 'Sueldo',
      pfRecur_extra: 'Adicional / bonos',
      pfRecur_freelance: 'Freelance / honorarios',
      pfRecur_rent: 'Alquiler / hipoteca',
      pfRecur_utilities: 'Servicios (luz, gas, internet)',
      pfRecur_subs: 'Suscripciones digitales',
      pfRecur_loan: 'Préstamos / tarjetas',
      pfRecur_insurance: 'Seguros',
    },
    awards: {
      title: 'Premios',
      subtitle: 'Reconocimientos y cultura de feedback.',
      heroTitle: 'Cultura y reconocimiento',
      heroLead: 'Celebrá logros visibles y alineados a la estrategia.',
      cultureLine:
        'Reconocer a tiempo fortalece cultura, retención y compromiso.',
      categoriesTitle: 'Categorías',
      catInnovation: 'Innovación',
      catAttitude: 'Actitud',
      catTeamwork: 'Compañerismo',
      catPerformance: 'Performance',
      catLeadership: 'Liderazgo',
      employeeMonth: 'Empleado del mes',
      recent: 'Reconocimientos recientes',
      new: 'Nuevo premio',
      empty: 'Sin premios.',
      points: 'puntos',
    },
    pi: {
      title: 'People Intelligence',
      subtitle: 'Señales operativas, riesgo documental y acciones sugeridas.',
      rcLine: 'v0.3.5 — People Intelligence, Executive Brief y Flow IA con mejor contraste.',
      privacyFinances: 'Los datos financieros personales del colaborador son privados y no se cruzan con otros módulos sin política explícita.',
      commercialHint:
        'Preparado para operar integrado con Spectra Flow, Finance y Spaces cuando las conexiones reales estén validadas.',
      docRiskTitle: 'Riesgo documental',
      docRiskIntro: 'Vista rápida sobre vencimientos y pendientes de documentación RRHH (demo).',
      nextActionTitle: 'Próxima acción recomendada',
      recOk: 'Cartera documental estable. Mantener revisiones periódicas.',
      recBusy:
        'Hay {expired} documentos vencidos y {pending} pendientes. Priorizar contrato laboral, DNI y constancia CBU antes del cierre mensual.',
      metricsLine: '{total} documentos · {approved} aprobados · compliance {compliance}%',
      roadmapNote:
        'Intelligence v1 basada en reglas. Flow IA contextual ya disponible por pantalla; la próxima etapa profundiza con datos reales del tenant y modelos entrenados.',
      rulesBanner:
        'Intelligence v1 basada en reglas sobre datos del tenant (demo). Sin modelo de machine learning todavía.',
      flowIaBanner:
        'Flow IA contextual: consultá recomendaciones por esta pantalla. Intelligence v1 sigue basada en reglas; la próxima etapa profundiza con datos reales del tenant.',
      execBriefTitle: 'Executive Brief · Salud organizacional',
      execBriefLead:
        'Vista ejecutiva consolidada (demo) para dirección y People Ops. Señales por área; sin datos personales de Mis Finanzas.',
      orgHealthLabel: 'Salud organizacional',
      orgHealthHint: 'Compuesto por compliance, Intelligence y clima (ponderación demo).',
      areaInsightsTitle: 'Lectura por área (demo)',
      areaInsight1: 'Comercial: licencias y cobertura en foco pre-cierre.',
      areaInsight2: 'Soporte: tardanzas puntuales en turno mañana.',
      areaInsight3: 'Producto: carga percibida alta en última encuesta.',
      areaInsight4: 'People: onboarding con documentación bancaria pendiente.',
      rotRiskTitle: 'Riesgo de rotación (simulado)',
      rotRiskValue: 'Medio-bajo · señal estable',
      rotRiskHint: 'Indicador orientativo; no es predicción individual.',
      burnoutTitle: 'Riesgo de burnout (simulado)',
      burnoutValue: 'Medio en 2 áreas',
      burnoutHint: 'Revisar carga en releases y apoyo a managers.',
      trainingGapTitle: 'Brechas de capacitación',
      trainingGapBody: '12 colaboradores con compliance obligatorio al 78% — cerrar brecha antes del Q2.',
      climateAlertTitle: 'Alertas de clima y participación',
      climateAlertBody: 'Leve baja de participación en el último pulso; conviene comunicado de cierre de loop.',
      piFlowIaCta: 'Analizar con Flow IA',
      piFlowIaCtaHint: 'Desplazá hasta el asistente para preguntas con contexto de esta pantalla.',
      rulesTitle: 'People Intelligence v1 · basada en reglas',
      whatHrNowTitle: 'Qué debería hacer RRHH ahora',
      whatHrNowIntro: 'Prioridades prácticas según las señales del panel (demo):',
      whatHrNow1: 'Regularizar documentos críticos (DNI, contrato, CBU) antes de cierre de nómina.',
      whatHrNow2: 'Aprobar licencias que condicionan cobertura operativa.',
      whatHrNow3: 'Revisar ausentismo y llegadas tarde recurrentes por área.',
      whatHrNow4: 'Cerrar evaluaciones de desempeño pendientes del ciclo activo.',
      whatHrNow5: 'Impulsar encuesta de clima o pulso para validar carga y liderazgo.',
      nextIaBanner: 'Próxima etapa: IA contextual integrada con Spectra Flow y datos operativos.',
      scoreLabel: 'Score Intelligence',
      metric_documents_pending: 'Documentación pendiente',
      metric_documents_expired: 'Documentación vencida',
      metric_pending_leaves: 'Licencias pendientes',
      metric_pending_expense_reports: 'Rendiciones en aprobación',
      metric_pending_expense_cents: 'Monto pendiente (centavos)',
      metric_late_arrivals_week: 'Llegadas tarde (7 días)',
      metric_awards_count: 'Reconocimientos',
      metric_compliance_rate: 'Compliance documental (%)',
      metric_documents_missing_file: 'Documentos requeridos sin archivo',
      metric_payslips_available: 'Recibos disponibles',
      metric_payslips_draft: 'Recibos en borrador',
      metric_payslips_signed: 'Recibos firmados',
      metric_payslips_observed: 'Recibos observados',
      riskLabel: 'Nivel de riesgo',
      riskHigh: 'Alto',
      riskMedium: 'Medio',
      riskLow: 'Bajo',
      metricsTitle: 'Métricas agregadas',
      insightsTitle: 'Insights',
      insightsEmpty: 'Sin insights disponibles.',
      recTitle: 'Recomendaciones',
      recEmpty: 'Sin recomendaciones adicionales.',
      lifecycleSectionTitle: 'Ciclo del colaborador',
      lifecycleInsight1: 'Onboardings bloqueados',
      lifecycleInsight2: 'Documentación crítica pendiente',
      lifecycleInsight3: 'Riesgos de offboarding',
      lifecycleInsight4: 'Tareas vencidas de RRHH',
      lifecycleRec1: 'Hay 2 onboardings con documentación pendiente. Priorizá contrato y datos bancarios antes del primer día.',
      lifecycleRec2: 'Revisá offboardings con accesos o equipo pendiente para reducir riesgo operativo y de seguridad.',
      lifecycleRec3: 'Asigná tareas vencidas a responsables concretos y cerrá el loop en la semana.',
      lifecycleCta: 'Abrir módulo Ciclo del colaborador',
      dimensionsTitle: 'Insights por dimensión',
      dim_docs: 'Documentación: priorizar vencimientos y contratos sin firma.',
      dim_attendance: 'Asistencia: picos de tardanza en Soporte y Comercial.',
      dim_leaves: 'Licencias: solapamientos en semanas de release.',
      dim_expenses: 'Gastos: rendiciones listas para Finance con sync pendiente.',
      dim_perf: 'Desempeño: 12 evaluaciones en etapa manager.',
      dim_train: 'Capacitación: compliance obligatorio al 78%.',
      dim_climate: 'Clima: eNPS estable, carga percibida media-alta.',
      dim_culture: 'Cultura: reconocimientos alineados a colaboración.',
      dim_wellbeing: 'Bienestar financiero: score medio del portal colaborador.',
      dimWellbeingTitle: 'Bienestar financiero (colaborador)',
      dimWellbeingScoreDemo: 'Salud financiera promedio agregada del portal (demo, sin identidades).',
      dimWellbeingRiskDemo:
        'Riesgo financiero personal solo a nivel agregado y anonimizado en esta vista; RRHH no accede a montos ni deudas individuales.',
      dimWellbeingPrivacy:
        'Los movimientos privados de Mis Finanzas no se exponen a RRHH; cualquier reporte futuro sería agregado y con política explícita.',
      dim_comms: 'Comunicación y tareas: comunicados en borrador, emails preparados y recordatorios.',
      dim_governance:
        'Gobernanza RRHH: roles base definidos en catálogo; sincronización con Spectra Flow/Auth pendiente. Validar permisos antes de clientes reales y separar acceso colaborador, manager, RRHH y finanzas.',
      commsSectionTitle: 'Comunicación y tareas',
      commsInsight1: 'Hay comunicados pendientes de publicar en el tablero interno.',
      commsInsight2: 'Hay tareas críticas vencidas o con fecha límite en las próximas 48 horas.',
      commsInsight3: 'Hay colaboradores con documentación pendiente que requieren email operativo.',
      commsInsight4: 'Hay baja participación en la última encuesta de clima.',
      commsRec1: 'Enviar recordatorio de documentación desde Emails RRHH (preparado, sin envío real).',
      commsRec2: 'Publicar el comunicado semanal o programarlo en Comunicados.',
      commsRec3: 'Priorizar tareas críticas en Notificaciones y asignar responsable.',
      commsRec4: 'Preparar email de onboarding para nuevos ingresos con plantilla “Bienvenida”.',
      risksBlockTitle: 'Riesgos detectados',
      risk_doc: 'Riesgo documental: certificados y políticas desactualizadas.',
      risk_ops: 'Riesgo operativo: cobertura en vacaciones Q1.',
      risk_culture: 'Riesgo cultural: participación baja en última encuesta.',
      risk_finance: 'Riesgo financiero: gastos aprobados sin sincronizar.',
      risk_perf: 'Riesgo de desempeño: feedback manager pendiente >14 días.',
      risk_governance: 'Riesgo de gobernanza: permisos aún no sincronizados con Spectra Flow/Auth — validar antes de go-live.',
      oppsTitle: 'Oportunidades',
      opp_train: 'Plan de capacitación en liderazgo para nuevos managers.',
      opp_awards: 'Reconocimientos públicos en Comercial por cierre trimestral.',
      opp_wellbeing: 'Webinar de finanzas personales y presupuesto.',
      opp_onboard: 'Kit de onboarding unificado con checklist digital.',
      opp_climate: 'Pulso de clima ligero post-release para detectar carga temprana.',
      opp_recognition: 'Reconocimiento público alineado a valores de colaboración.',
      recActionsTitle: 'Recomendaciones accionables',
      recAction1: 'Enviar emails RRHH operativos (plantillas preparadas, validar modo real).',
      recAction2: 'Cerrar onboarding de nuevas incorporaciones con checklist digital.',
      recAction3: 'Revisar documentos críticos y vencimientos en Legajo Digital.',
      recAction4: 'Aprobar o responder licencias que condicionan cobertura.',
      recAction5: 'Revisar gastos pendientes y alinear con Finance cuando corresponda.',
      recAction6: 'Completar evaluaciones de desempeño en etapa manager.',
    },
    portal: {
      title: 'Portal colaborador',
      subtitleHeader: 'Autogestión, legajo y bienestar en un solo lugar.',
      welcomePrefix: 'Hola,',
      subtitle: 'Tu espacio unificado de autogestión Spectra.',
      quickActions: 'Acciones rápidas',
      nextLeave: 'Próxima licencia o ausencia',
      noLeave: 'No tenés licencias próximas registradas.',
      financeSummary: 'Resumen financiero personal',
      noFinance: 'Sin datos de finanzas personales todavía.',
      recentDocs: 'Últimos documentos del legajo',
      emptyDocs: 'Sin documentos recientes.',
      payslips: 'Recibos (metadata)',
      recentExpenses: 'Últimas rendiciones',
      emptyExpenses: 'Sin rendiciones recientes.',
      awards: 'Reconocimientos recibidos',
      emptyAwards: 'Sin reconocimientos recientes.',
      heroRoleFallback: 'Colaborador',
      tileDocs: 'Mis documentos',
      tileLeaves: 'Mis licencias',
      tileExpenses: 'Mis gastos',
      tileFinance: 'Mis finanzas',
      tileAwards: 'Reconocimientos',
      quickActionsHint: 'Atajos para lo que más usás en Spectra RRHH.',
      weekTitle: 'Mi semana',
      weekSubtitle: 'Próximos pendientes y recordatorios personales.',
      weekLeave: 'Próxima ausencia',
      weekTraining: 'Capacitación asignada',
      weekDoc: 'Documento pendiente',
      weekExpense: 'Gasto a corregir',
      weekComms: 'Comunicación interna',
      weekNone: 'Sin ítems destacados.',
      weekTrainingDemo: 'Onboarding Spectra — módulo compliance (72% avance).',
      weekCommsDemo: 'Política de trabajo remoto 2026 — lectura recomendada.',
      statusWorking: 'Estado laboral',
      privacyFinance: 'Tus finanzas personales son privadas: la empresa no ve el detalle de movimientos.',
      errorUnauth: 'Iniciá sesión en Spectra Flow para acceder al portal.',
      errorForbidden: 'No tenés permiso para ver el portal del colaborador.',
      errorNotLinked: 'Tu cuenta de Spectra Flow todavía no está vinculada a un colaborador de esta empresa.',
      errorLinkSuspended: 'Tu acceso al portal está suspendido. Contactá a People Ops.',
      errorNotFound: 'No encontramos tu perfil de colaborador en este tenant.',
      errorServer: 'El servicio no está disponible. Intentá de nuevo en unos minutos.',
      errorNetwork: 'No pudimos conectar con el servidor. Revisá tu conexión.',
      errorUnknown: 'No pudimos cargar el portal.',
      supportId: 'ID de soporte',
      emptyPayslips: 'Sin recibos disponibles.',
    },
    attendance: {
      title: 'Asistencia / Jornada',
      subtitle: 'KPIs diarios, registros y filtros. Listo para reloj biométrico o integración externa.',
      kpiPresent: 'Presentes hoy',
      kpiLate: 'Llegadas tarde',
      kpiAbsent: 'Ausentes',
      kpiWeekHours: 'Horas estimadas (semana)',
      kpiRemote: 'Remoto hoy',
      weekSummaryTitle: 'Resumen semanal (datos cargados)',
      weekSummaryEmpty: 'Sin registros en el rango visible. Ajustá filtros o cargá asistencia.',
      weekSummaryHint: 'Las horas y estados se calculan sobre los registros listados abajo.',
      filterEmployee: 'Colaborador',
      filterStatus: 'Estado',
      allEmployees: 'Todos',
      allStatuses: 'Todos',
      quickDemo: 'Registrar demo (tarde)',
      newRecord: 'Nuevo registro',
      futureHint: 'Próximos pasos: check-in móvil, geofencing y sincronización con Spectra Flow.',
      colEmployee: 'Colaborador',
      colDate: 'Fecha',
      colIn: 'Entrada',
      colOut: 'Salida',
      colStatus: 'Estado',
      colHours: 'Horas',
      colNotes: 'Observación',
      empty: 'Sin registros de asistencia. Creá uno manual o usá el demo.',
      modalTitle: 'Registrar asistencia',
      pickEmployee: 'Seleccionar…',
      delete: 'Eliminar',
      confirmDelete: '¿Eliminar este registro?',
      demoNote: 'Registro demo — tarde',
      kpiCompliance: 'Cumplimiento jornada',
      insightsTitle: 'Insights de asistencia',
      insightLate: 'Llegadas tarde recurrentes en turno mañana (Soporte).',
      insightArea: 'Ausencias concentradas en Comercial la semana post-feriado.',
      insightHours: 'Jornada incompleta en 4 registros sin check-out.',
      clockHint: 'Preparado para integración con reloj biométrico o proveedor de asistencia externo.',
      status: {
        present: 'Presente',
        late: 'Tarde',
        absent: 'Ausente',
        remote: 'Remoto',
        leave: 'Licencia',
      },
    },
    communications: {
      title: 'Comunicaciones RRHH',
      subtitle:
        'Gestioná comunicados internos, emails operativos, plantillas y seguimiento de mensajes a colaboradores.',
      modeBanner:
        'Canal operativo RRHH: simulación, preparado o envío real según configuración. Separado de campañas de email marketing comercial. Resend u otros proveedores pueden estar en modo demo.',
      tab_bulletins: 'Comunicados',
      tab_emails: 'Emails RRHH',
      tab_templates: 'Plantillas',
      tab_history: 'Historial',
      tab_audiences: 'Audiencias',
      kpiPublished: 'Comunicados publicados',
      kpiEmailsPrepared: 'Emails preparados',
      kpiTemplatesActive: 'Plantillas activas',
      kpiPendingMsgs: 'Mensajes pendientes',
      kpiReadRate: 'Tasa estimada de lectura',
      kpiReadRateValue: '81%',
      kpiReadRateHint: 'Estimación demo (aperturas simuladas)',
      premiumStripTitle: 'Canales y audiencias',
      premiumStripLead:
        'Comunicación interna preparada: tablero, email operativo y plantillas (sin envío masivo real en esta versión).',
      premiumChannelPortal: 'Portal colaborador',
      premiumChannelEmail: 'Email RRHH',
      premiumChannelBoard: 'Tablero interno',
      premiumAudienceAll: 'Toda la organización',
      premiumAudienceManagers: 'Liderazgo',
      premiumAudienceNewHires: 'Nuevas incorporaciones',
      featuredTitle: 'Comunicados destacados',
      actionView: 'Ver',
      actionEdit: 'Editar',
      actionArchive: 'Archivar',
      actionPublish: 'Publicar',
      whereSeeBannerTitle: '¿Dónde veo mi comunicación de prueba?',
      whereSeeBannerLead:
        'Publicá en “Comunicados”, buscá el aviso en la bandeja de abajo y revisá el ícono de campanilla (pestaña Comunicaciones). La lectura en portal se simula en esta demo.',
      internalPublishBanner:
        'Publicación interna activa. El envío por email real queda sujeto a la configuración del proveedor en el servidor.',
      recipientEstPrefix: 'Destinatarios estimados (demo):',
      commNotFoundToast:
        'Abrimos Comunicaciones. No encontramos el detalle exacto, pero podés verla en el historial o en la lista.',
      commSelectedBanner: 'Comunicación seleccionada',
      actionDuplicate: 'Duplicar',
      timelineCreated: 'Creada',
      timelineUpdated: 'Editada',
      timelinePublished: 'Publicada',
      timelineViewed: 'Vista por colaborador (demo)',
      emailSoftWarning:
        'No pudimos completar la operación con el proveedor. Tu acción quedó registrada en la interfaz; probá modo simulación o revisá la configuración del servidor.',
      tplNew: 'Nueva plantilla',
      tplEdit: 'Editar plantilla',
      tplDuplicate: 'Duplicar',
      tplDelete: 'Eliminar',
      tplPreview: 'Vista previa',
      tplSave: 'Guardar',
      tplClose: 'Cerrar',
      tplName: 'Nombre visible',
      tplCategory: 'Categoría plantilla',
      tplChannel: 'Canal',
      tplStatus: 'Estado',
      tplVars: 'Variables',
      tplPreviewText: 'Texto de vista previa',
      tplConfirmDelete: '¿Eliminar esta plantilla?',
      tplToastSaved: 'Plantilla guardada',
      tplCatWelcome: 'Bienvenida',
      tplCatReceipt: 'Recibo',
      tplCatLeave: 'Licencia',
      tplCatTraining: 'Capacitación',
      tplCatClimate: 'Clima',
      tplCatGeneral: 'General',
      tplCatInternal: 'Comunicación interna',
      viewModalTitle: 'Detalle del comunicado',
      viewClose: 'Cerrar',
      editModalTitle: 'Editar comunicado',
      emailPanelTitle: 'Emails RRHH',
      emailPanelLead: 'Prepará mensajes operativos. El motor de envío se conectará en una etapa posterior.',
      emailPanelLeadResend:
        'Envío controlado desde esta pantalla: en simulación se registra el log sin Resend; en modo real se usa Resend con variables de entorno seguras.',
      emailRefresh: 'Actualizar estado',
      emailProviderState: 'Motor de correo',
      emailModeLabel: 'Modo',
      emailModeReal: 'Real (Resend)',
      emailModeSimulation: 'Simulación',
      emailConfigStatus_ready: 'Envío real activo',
      emailConfigStatus_simulation: 'Modo simulación (sin envío real)',
      emailConfigStatus_missing_api_key: 'Falta configurar RESEND_API_KEY',
      emailConfigStatus_disabled: 'Deshabilitado (RRHH_EMAIL_ENABLED=false)',
      emailSimBanner:
        'El envío real está deshabilitado o en simulación. Se guardará un log simulado al pulsar “Simular envío”.',
      emailDisabledBanner: 'RRHH_EMAIL_ENABLED está en false: solo se pueden registrar borradores o simulaciones.',
      emailMissingKeyBanner:
        'No hay API key de Resend en el servidor: no se puede enviar real. Configurá RESEND_API_KEY de forma segura.',
      emailGenericBanner: 'Revisá la configuración del servidor para habilitar Resend.',
      emailRealBanner:
        'Atención: el modo real está activo. Cada envío genera un correo real al destinatario. No se usa Email Marketing comercial.',
      emailComposeTitle: 'Enviar email RRHH',
      fieldToEmail: 'Correo destinatario',
      fieldToName: 'Nombre destinatario',
      fieldHtmlBody: 'Cuerpo HTML',
      fieldTextBody: 'Cuerpo texto plano',
      emailPreviewTitle: 'Vista previa',
      emailReloadTemplate: 'Aplicar plantilla',
      emailPrepareSave: 'Preparar (sin enviar)',
      emailSendReal: 'Enviar email',
      emailSendSimulated: 'Simular envío',
      emailRealAck: 'Confirmo que deseo enviar un correo real al destinatario indicado.',
      emailRealAckMissing: 'Marcá la confirmación para enviar un correo real.',
      emailPrepareOk: 'Preparado guardado en el historial.',
      emailSendOk: 'Operación completada.',
      emailSendErr: 'No se pudo completar la operación. Revisá datos y configuración.',
      emailRecentLogTitle: 'Últimos registros',
      emailColDate: 'Fecha',
      emailColProvider: 'Proveedor',
      emailColError: 'Error',
      emailLogEmpty: 'Sin registros todavía.',
      emailProvider: {
        resend: 'Resend',
        simulation: 'Simulación',
      },
      emailLogStatus: {
        draft: 'Borrador',
        prepared: 'Preparado',
        simulated: 'Simulado',
        sent: 'Enviado',
        failed: 'Fallido',
      },
      emailPrepare: 'Preparar email',
      emailDisclaimerTitle: 'Importante',
      emailDisclaimer:
        'Este módulo está preparado para envío real por Resend/Spectra Email Engine en una próxima etapa. En esta versión no se envían emails reales.',
      emailColTo: 'Destinatario',
      emailColStatus: 'Estado',
      emailColUpdated: 'Actualizado',
      emailModalTitle: 'Preparar email RRHH',
      emailModalNote: 'No se envía correo real; en demo solo se agrega a la tabla como preparado en esta sesión.',
      emailSavePrepared: 'Guardar como preparado',
      emailUntitled: '(sin asunto)',
      fieldRecipient: 'Destinatario',
      recipient_employee: 'Colaborador',
      recipient_area: 'Área',
      recipient_all: 'Todos',
      recipient_area_demo: 'Área (ejemplo)',
      recipient_employee_demo: 'Colaborador (ejemplo)',
      fieldSubject: 'Asunto',
      fieldEmailType: 'Tipo de email',
      fieldTemplate: 'Plantilla',
      fieldMessage: 'Mensaje',
      fieldPriority: 'Prioridad',
      priority_low: 'Baja',
      priority_normal: 'Media',
      priority_high: 'Alta',
      emailType: {
        welcome: 'Bienvenida',
        docs_pending: 'Documentación pendiente',
        doc_expired: 'Documento vencido',
        leave_approved: 'Licencia aprobada',
        leave_rejected: 'Licencia rechazada',
        expense_approved: 'Gasto aprobado',
        expense_rejected: 'Gasto rechazado',
        eval_pending: 'Evaluación pendiente',
        training_assigned: 'Capacitación asignada',
        climate_survey: 'Encuesta de clima',
        general_comms: 'Comunicado general',
      },
      emailStatus: {
        draft: 'Borrador',
        prepared: 'Preparado',
        queued: 'En cola',
        sent_sim: 'Enviado simulado',
        failed_sim: 'Fallido simulado',
      },
      templateChannel: 'Canal',
      templateStatus: 'Estado',
      templateVars: 'Variables disponibles',
      templateEdited: 'Última edición',
      tplActive: 'Activa',
      tplDraft: 'Borrador',
      chEmail: 'Email',
      chPortal: 'Portal',
      chInternal: 'Interno',
      template: {
        tpl_welcome: 'Bienvenida al colaborador',
        tpl_docs_pending: 'Documentación pendiente',
        tpl_doc_expired: 'Documento vencido',
        tpl_leave_ok: 'Licencia aprobada',
        tpl_leave_rej: 'Licencia rechazada',
        tpl_exp_ok: 'Rendición de gasto aprobada',
        tpl_eval_pending: 'Evaluación pendiente',
        tpl_training: 'Capacitación asignada',
        tpl_climate_survey: 'Encuesta de clima disponible',
        tpl_urgent_comms: 'Comunicado urgente',
      },
      templatePreview: {
        tpl_welcome: 'Hola {{employee_name}}, te damos la bienvenida a {{company_name}}…',
        tpl_docs_pending: 'Tenés documentación pendiente: {{document_name}} antes del {{due_date}}.',
        tpl_doc_expired: 'El documento {{document_name}} está vencido; regularizá con RRHH.',
        tpl_leave_ok: 'Tu licencia para las fechas {{leave_dates}} fue aprobada.',
        tpl_leave_rej: 'Tu solicitud de licencia no pudo aprobarse en este momento.',
        tpl_exp_ok: 'Tu rendición fue aprobada. Gracias por la gestión.',
        tpl_eval_pending: '{{employee_name}}, tenés una evaluación pendiente con {{manager_name}}.',
        tpl_training: 'Se te asignó el curso {{course_name}}. Ingresá al portal.',
        tpl_climate_survey: 'Participá en la encuesta: {{survey_link}}',
        tpl_urgent_comms: 'Mensaje urgente de RRHH para {{employee_name}}.',
      },
      historyTitle: 'Historial de mensajes',
      historyLeadResend:
        'Registros de emails RRHH (simulados o enviados por Resend). Los datos se guardan en MongoDB cuando está disponible.',
      historyLead:
        'Vista de auditoría demo. Los estados “simulado” o “preparado” no implican envío real de correo desde esta versión.',
      historyTo: 'Destinatario',
      historyChannel: 'Canal',
      historyActor: 'Responsable',
      histTypeEmail: 'Email',
      histTypePortal: 'Portal',
      channelEmail: 'Email',
      channelPortal: 'Portal',
      channelInternal: 'Interno',
      histPrepared: 'Preparado',
      histQueued: 'En cola',
      histSentSim: 'Enviado simulado',
      histReadSim: 'Leído simulado',
      histFailedSim: 'Fallido simulado',
      histResultOk: 'Resultado simulado: entregado',
      histResultRead: 'Resultado simulado: leído',
      histResultPending: 'Pendiente de motor de envío',
      histResultQueue: 'En cola (motor futuro)',
      histResultFail: 'Fallo simulado (revisión)',
      audiencesIntro: 'Audiencias sugeridas para segmentar comunicaciones con criterio y trazabilidad.',
      audienceCountSuffix: 'personas aprox.',
      audienceChannels: 'Canales sugeridos',
      audienceName: {
        aud_all: 'Todos los colaboradores',
        aud_managers: 'Managers',
        aud_new_hires: 'Nuevos ingresos',
        aud_docs_pending: 'Colaboradores con documentación pendiente',
        aud_leaves_upcoming: 'Colaboradores con licencias próximas',
        aud_eval_pending: 'Colaboradores con evaluaciones pendientes',
        aud_area_tech: 'Área Tecnología',
        aud_area_sales: 'Área Comercial',
        aud_area_admin: 'Área Administración',
        aud_area_people: 'Área People',
      },
      audUseAll: 'Comunicados institucionales, clima, beneficios.',
      audChAll: 'Portal, email, tablero interno',
      audUseMgr: 'Lineamientos, aprobaciones, desempeño de equipo.',
      audChMgr: 'Email, portal, reuniones',
      audUseNew: 'Onboarding, accesos, bienvenida.',
      audChNew: 'Email, portal, tareas',
      audUseDocs: 'Recordatorios de compliance y firma.',
      audChDocs: 'Email, portal',
      audUseLeave: 'Coordinación de cobertura y calendario.',
      audChLeave: 'Email, portal',
      audUseEval: 'Cierres de ciclo y feedback.',
      audChEval: 'Portal, email',
      audUseTech: 'Comunicaciones técnicas o de infraestructura.',
      audUseSales: 'Comunicaciones comerciales o metas.',
      audUseAdmin: 'Procesos administrativos y back office.',
      audUsePeople: 'Políticas internas de People.',
      audChArea: 'Email, portal, interno',
      audChPeople: 'Interno, email',
      audActive: 'Activa',
      variablesBlockTitle: 'Variables disponibles',
      variablesBlockBody:
        'En plantillas podés usar marcadores que luego el motor reemplazará con datos reales del colaborador (cuando esté conectado).',
      variablesList:
        'Nombre del colaborador|Nombre del manager|Nombre del documento|Rango de fechas de licencia|Nombre de la empresa|Fecha límite|Nombre del curso|Enlace a encuesta',
      filterCategory: 'Categoría',
      filterStatus: 'Estado',
      all: 'Todos',
      new: 'Nuevo comunicado',
      empty: 'Todavía no hay comunicados. Creá el primero para el equipo.',
      publish: 'Publicar',
      archive: 'Archivar',
      unarchive: 'Volver a borrador',
      delete: 'Eliminar',
      confirmDelete: '¿Eliminar este comunicado?',
      modalTitle: 'Nuevo comunicado',
      fieldTitle: 'Título',
      fieldCategory: 'Categoría',
      fieldAudience: 'Audiencia',
      fieldStatus: 'Estado inicial',
      fieldSummary: 'Resumen',
      fieldBody: 'Contenido',
      fieldAuthor: 'Autor',
      publishToFeed: 'Publicar también en Muro',
      publishToFeedHint: 'Solo aplica cuando el estado es Publicado.',
      notPublished: 'Sin publicar',
      category: {
        general: 'General',
        rrhh: 'RRHH',
        compliance: 'Compliance',
        benefits: 'Beneficios',
        urgent: 'Urgente',
      },
      audience: { all: 'Todos', managers: 'Managers', employees: 'Colaboradores' },
      status: { draft: 'Borrador', published: 'Publicado', archived: 'Archivado' },
      feedTitle: 'Feed interno',
    },
    permissions: {
      sectionTitle: 'Roles y permisos RRHH',
      matrixTitle: 'Matriz de permisos por módulo',
      matrixLegend: '✓ = permitido en el rol simulado · — = no · Demo de solo lectura.',
      matrixModuleCol: 'Módulo / área',
      matrixReadOnly: 'La matriz refleja el rol seleccionado arriba. En producción se sincronizará con Spectra Flow/Auth.',
      mockRoleLabel: 'Rol simulado (demo)',
      internalCode: 'Código interno',
      source_local_demo: 'Fuente: demo local',
      source_flow_future: 'Fuente: Flow (futuro)',
      profilesTitle: 'Perfiles recomendados',
      profileFor: 'Para quién',
      profileModules: 'Módulos típicos',
      profileRestrictions: 'Restricciones',
      profileRisk: 'Riesgo / nota',
      flowSectionTitle: 'Integración futura con Spectra Flow/Auth Center',
      flowIntro: 'Spectra Flow centralizará identidad, tenants y habilitación de módulos. RRHH aplicará permisos finos localmente.',
      flowBullet1: 'Flow será la fuente central de usuarios y sesiones.',
      flowBullet2: 'Flow entregará tenant_id y contexto multi-tenant.',
      flowBullet3: 'Flow validará que el módulo RRHH esté habilitado para el tenant.',
      flowBullet4: 'Flow entregará rol global y roles por módulo.',
      flowBullet5: 'RRHH aplicará permisos localmente (module_role / module_permissions).',
      flowBullet6: 'RRHH no será dueño aislado de usuarios: delega identidad a Flow.',
      flowBullet7: 'Email y Storage pueden seguir en modo demo; no asume conexión productiva.',
      flowNoSso: 'SSO real y validación de tokens contra Flow/Auth no están activados en esta versión.',
      flowChainAria: 'Cadena de gobernanza de acceso',
      flowStepLogin: 'Login Flow',
      flowStepValidate: 'Validar módulo',
      flowStepTenant: 'Contexto tenant',
      flowStepRole: 'Rol RRHH',
      flowStepPerms: 'Permisos módulo',
      flowStepGuards: 'Guards UI/API',
      requirePermBadge: 'Requiere permiso',
      action_view: 'Ver',
      action_create: 'Crear',
      action_edit: 'Editar',
      action_approve: 'Aprobar',
      action_reject: 'Rechazar',
      action_delete: 'Eliminar',
      action_export: 'Exportar',
      action_admin: 'Administrar',
      module_dashboard: 'Dashboard',
      module_employee_portal: 'Portal Colaborador',
      module_employees: 'Colaboradores',
      module_lifecycle: 'Ciclo del colaborador',
      module_org_chart: 'Organigrama',
      module_legajo: 'Legajo Digital',
      module_payslips: 'Recibos',
      module_attendance: 'Asistencia',
      module_leaves: 'Licencias',
      module_expenses: 'Rendición de gastos',
      module_personal_finance: 'Mis Finanzas',
      module_communications: 'Comunicaciones',
      module_rrhh_emails: 'Emails RRHH',
      module_evaluations: 'Evaluaciones',
      module_training: 'Capacitaciones',
      module_surveys: 'Clima y encuestas',
      module_awards: 'Premios / reconocimientos',
      module_feed: 'Muro social',
      module_chat: 'Chat interno',
      module_flow_ia: 'Flow IA',
      module_people_intelligence: 'People Intelligence',
      module_notifications: 'Notificaciones y tareas',
      module_settings: 'Configuración',
      roleTitle_rrhh_admin: 'Administrador RRHH',
      roleDesc_rrhh_admin: 'Acceso completo al módulo RRHH.',
      roleTitle_rrhh_manager: 'Manager RRHH',
      roleDesc_rrhh_manager: 'Gestiona colaboradores, licencias, documentos, evaluaciones y comunicaciones.',
      roleTitle_rrhh_operator: 'Operador RRHH',
      roleDesc_rrhh_operator: 'Carga y actualiza información operativa.',
      roleTitle_employee_self_service: 'Portal Colaborador',
      roleDesc_employee_self_service: 'Acceso limitado al portal, documentos propios, licencias, gastos y finanzas personales.',
      roleTitle_finance_reviewer: 'Revisor Finanzas',
      roleDesc_finance_reviewer: 'Revisa rendiciones de gastos y estados vinculables a Finance.',
      roleTitle_viewer: 'Lector',
      roleDesc_viewer: 'Solo consulta información permitida.',
      profile_hr_team_title: 'Equipo RRHH completo',
      profile_hr_team_for: 'Administradores y leads de People Operations.',
      profile_hr_team_modules: 'Todos los módulos operativos y configuración acotada.',
      profile_hr_team_restrictions: 'Debe alinearse con políticas corporativas y segregación de funciones.',
      profile_hr_team_risk: 'Alto impacto: auditar cambios masivos y exportaciones.',
      profile_area_manager_title: 'Manager de área',
      profile_area_manager_for: 'Líderes con equipo a cargo.',
      profile_area_manager_modules: 'Colaboradores, licencias, asistencia, evaluaciones, comunicaciones.',
      profile_area_manager_restrictions: 'Sin acceso a configuración global ni datos financieros ajenos.',
      profile_area_manager_risk: 'Medio: revisar aprobaciones de licencias y datos sensibles.',
      profile_self_service_title: 'Colaborador autoservicio',
      profile_self_service_for: 'Cada colaborador en el portal.',
      profile_self_service_modules: 'Portal, legajo propio, licencias, gastos, finanzas personales.',
      profile_self_service_restrictions: 'Sin visibilidad de datos de terceros ni panel ejecutivo.',
      profile_self_service_risk: 'Bajo si el portal está bien acotado.',
      profile_finance_title: 'Finanzas / reembolsos',
      profile_finance_for: 'Equipo finance y aprobadores de gastos.',
      profile_finance_modules: 'Rendición de gastos, lectura de comunicaciones, exportaciones.',
      profile_finance_restrictions: 'Sin alta/baja de colaboradores ni legajo completo salvo lo necesario.',
      profile_finance_risk: 'Medio: validar límites de aprobación y trazabilidad.',
      profile_exec_read_title: 'Dirección / solo lectura',
      profile_exec_read_for: 'C-level y staff de staff con necesidad de visibilidad.',
      profile_exec_read_modules: 'Dashboard, People Intelligence, reportes y lecturas puntuales.',
      profile_exec_read_restrictions: 'Sin acciones destructivas ni aprobaciones operativas.',
      profile_exec_read_risk: 'Bajo; vigilar exportaciones masivas.',
      profile_audit_title: 'Auditoría / compliance',
      profile_audit_for: 'Auditores internos o externos.',
      profile_audit_modules: 'Lectura amplia, exportaciones, trazas de comunicaciones y legajo.',
      profile_audit_restrictions: 'Sin edición de datos maestros salvo flujos formales.',
      profile_audit_risk: 'Controlar retención y confidencialidad de exportaciones.',
      permDeniedBadge: 'Requiere permiso',
    },
    notifications: {
      title: 'Notificaciones y tareas RRHH',
      subtitle: 'Priorizá alertas operativas y tareas con responsables claros (vista demo).',
      subTabsAria: 'Notificaciones o tareas',
      sub_notifications: 'Notificaciones',
      sub_tasks: 'Tareas',
      kpiPending: 'Pendientes',
      kpiOverdue: 'Vencidas',
      kpiCritical: 'Críticas',
      kpiDone: 'Completadas',
      kpiWeek: 'Próximas 7 días',
      listTitle: 'Notificaciones',
      tasksTitle: 'Tareas RRHH',
      due: 'Vencimiento',
      priority_high: 'Alta',
      priority_medium: 'Media',
      priority_low: 'Baja',
      status_pending: 'Pendiente',
      status_in_progress: 'En progreso',
      status_completed: 'Completada',
      status_overdue: 'Vencida',
      actionComplete: 'Marcar como completada',
      actionSnooze: 'Posponer',
      actionOrigin: 'Ver origen',
      actionAssign: 'Asignar responsable',
      actionAssignHint: 'Demo: asignación en roadmap',
      notifItemDocExpired: 'Documento vencido — revisión urgente',
      notifItemLeavePending: 'Licencia pendiente de aprobación',
      notifItemExpensePending: 'Gasto pendiente de aprobación',
      notifItemEvalPending: 'Evaluación pendiente',
      notifItemTrainingOverdue: 'Capacitación vencida',
      notifItemSurveyLow: 'Encuesta con baja participación',
      notifItemOnboard: 'Onboarding incompleto',
      notifItemOffboard: 'Offboarding bloqueado',
      notifItemFlowPrep: 'Preparar validación de Spectra Flow/Auth antes de go-live',
      taskDemo1: 'Revisar documentos vencidos de Ana Martínez',
      taskDemo2: 'Aprobar licencia de Martín Gómez',
      taskDemo3: 'Completar onboarding de Sofía Herrera',
      taskDemo4: 'Revisar gasto pendiente de Diego Rojas',
      taskDemo5: 'Publicar encuesta de clima mensual',
      taskDemo6: 'Cerrar evaluación Q2',
      taskDemo7: 'Asignar capacitación obligatoria',
      taskPerm1: 'Revisar matriz de permisos RRHH',
      taskPerm2: 'Definir responsables por módulo',
      taskPerm3: 'Validar acceso de Portal Colaborador',
      taskPerm4: 'Revisar permisos de Finanzas / reembolsos',
      taskPerm5: 'Preparar integración Flow/Auth',
      todayTitle: 'Qué debería resolver RRHH hoy',
      todayIntro: 'Cinco acciones priorizadas según señales operativas (demo):',
      today1: 'Desbloquear offboarding con accesos y equipos pendientes.',
      today2: 'Enviar recordatorio de documentación a colaboradores en riesgo.',
      today3: 'Publicar o programar el comunicado semanal de People.',
      today4: 'Resolver tareas críticas vencidas de licencias y gastos.',
      today5: 'Preparar email de onboarding para nuevos ingresos de la semana.',
      colTask: 'Tarea',
      colOwner: 'Responsable',
      colModule: 'Módulo origen',
      colDue: 'Fecha límite',
      colPriority: 'Prioridad',
      colStatus: 'Estado',
      emptySection: 'Sin ítems en esta categoría.',
      rcHint: 'Vista demo: agrupamos alertas como en un centro de control productivo.',
      secCritical: 'Tareas críticas',
      secOverdue: 'Tareas vencidas',
      secUpcoming: 'Tareas próximas (7 días)',
      secIntegration: 'Tareas de integración',
      secOther: 'Otras alertas operativas',
      tasksSecCritical: 'Tareas críticas',
      tasksSecOverdue: 'Tareas vencidas',
      tasksSecUpcoming: 'Tareas próximas (7 días)',
      tasksSecIntegration: 'Tareas de integración (Flow/Auth y permisos)',
      tasksSecOther: 'Tareas operativas y seguimiento',
      tasksSecOtherHint: 'Backlog operativo fuera de la ventana crítica inmediata.',
      preClientTitle: 'Antes de activar cliente real',
      preClientLead: 'Checklist de go-live responsable (sin automatismos en esta versión):',
      preClient1: 'Validar dominios y remitentes de Email RRHH.',
      preClient2: 'Validar bucket y políticas de Storage documental.',
      preClient3: 'Probar flujo de login y roles con Spectra Flow/Auth.',
      preClient4: 'Revisar matriz de permisos y segregación de funciones.',
      preClient5: 'Cargar datos maestros reales con plan de migración.',
      preClient6: 'Probar mobile y tablet con casos reales.',
      preClient7: 'Definir backup, rollback y responsables de guardia.',
    },
    settings: {
      title: 'Configuración',
      subtitle: 'Preferencias del módulo y integraciones.',
      heroEyebrow: 'Espacio de administración',
      heroTitle: 'Configuración del tenant',
      heroLead: 'Gestioná idioma, módulos visibles, roles, integraciones y storage documental con una vista clara por pestañas.',
      tabsAria: 'Secciones de configuración',
      tab_general: 'General',
      tab_modules: 'Módulos visibles',
      tab_roles: 'Roles y permisos',
      tab_integrations: 'Integraciones',
      tab_storage: 'Storage documental',
      tab_security: 'Seguridad y Flow/Auth',
      tab_lifecycle: 'Ciclo del colaborador',
      tab_comms: 'Comunicaciones y email',
      tab_release: 'Release checklist',
      integrationFlowCenterTitle: 'Spectra Flow/Auth Center',
      commsEmailTitle: 'Email y comunicaciones RRHH',
      commsEmailLead:
        'RRHH usa emails operativos y transaccionales para colaboradores. No se mezcla con Email Marketing comercial.',
      commsLiveTitle: 'Estado en tiempo real (servidor)',
      commsProviderLabel: 'Proveedor',
      commsStatusRuntime: 'Estado',
      commsModeRuntime: 'Modo',
      commsFromRuntime: 'Remitente configurado',
      commsReplyRuntime: 'Reply-to',
      commsReplyYes: 'Configurado',
      commsReplyNo: 'No configurado',
      commsStateLabel: 'Estado actual',
      commsStateValue: 'Preparado — sin envío real de correo en esta versión.',
      commsEngineLabel: 'Motor futuro',
      commsEngineValue: 'Resend o Spectra Email Engine (activación futura).',
      commsFromLabel: 'Remitente RRHH',
      commsFromValue: 'Configurable en una etapa posterior (dominio verificado).',
      commsReplyLabel: 'Reply-to',
      commsReplyValue: 'Cola dedicada RRHH — configuración futura.',
      commsLogsLabel: 'Logs de envío',
      commsLogsValue: 'Auditoría y trazabilidad cuando el motor esté conectado.',
      commsMarketingSepTitle: 'Separación con marketing',
      commsMarketingSepBody:
        'Las campañas comerciales de marketing no comparten remitente ni listas con comunicaciones transaccionales de RRHH.',
      sectionGeneral: 'General',
      sectionLanguage: 'Idioma',
      sectionCurrency: 'Moneda',
      pfWellbeingTitle: 'Mis Finanzas / bienestar financiero',
      pfWellbeingStatus: 'Estado del módulo',
      pfWellbeingStatusActive: 'Activo para colaboradores',
      pfWellbeingPrivacy1: 'Privacidad: datos personales del colaborador; la empresa no accede a movimientos individuales.',
      pfWellbeingPrivacy2: 'Metas, deudas y presupuestos demo permanecen en el espacio privado del usuario.',
      pfWellbeingFuture1: 'Futuro: reportes agregados y anonimizados para la organización (sin datos personales).',
      pfWellbeingFuture2: 'Futuro: educación financiera, beneficios y nudges saludables.',
      sectionModules: 'Módulos visibles',
      sectionIntegrations: 'Integraciones',
      sectionFlow: 'Integración Flow/Auth',
      sectionFinance: 'Integración Finance',
      flowIaCardTitle: 'Flow IA',
      flowIaCardIntro:
        'Flow IA entrega recomendaciones contextuales por pantalla. Si la IA real no está activa, el sistema usa recomendaciones basadas en reglas. Las API keys se configuran solo en el servidor.',
      flowIaStatusLabel: 'Estado actual',
      flowIaModeActive: 'Activo (IA real)',
      flowIaModeFallback: 'Fallback (reglas)',
      flowIaModePendingKey: 'Pendiente API key',
      flowIaModeDisabled: 'Desactivado (solo reglas)',
      flowIaProviderLabel: 'Proveedor configurado',
      flowIaModelLabel: 'Modelo',
      flowIaModuleVersionLabel: 'Versión módulo',
      flowIaLoading: 'Consultando estado…',
      flowIaErr: 'No se pudo leer el estado de Flow IA.',
      sectionSecurity: 'Seguridad',
      tenantMode: 'Modo tenant',
      tenantPrepared: 'Preparado para multi-tenant con Flow/Auth.',
      storagePending: 'Spaces / S3: pendiente en roadmap.',
      storageFuture: 'Almacenamiento futuro (S3 / Spaces)',
      storageHint: 'Documentos y adjuntos en roadmap enterprise.',
      defaultLang: 'Idioma por defecto',
      defaultCurrency: 'Moneda por defecto',
      visibleModules: 'Módulos visibles',
      flowAuth: 'Integración Flow/Auth',
      finance: 'Integración Finance',
      flowPrepared: 'Preparada (validación futura contra Auth Center).',
      financePrepared: 'Preparada para sincronización de gastos.',
      architectureNote:
        'Spectra RRHH está preparado para heredar tenant_id, roles y permisos desde Spectra Flow/Auth Center.',
      sectionRoles: 'Roles y permisos RRHH',
      rolesIntro: 'Vista demostrativa de capacidades por rol. No reemplaza políticas reales de seguridad.',
      rolesSyncNote: 'En producción, estos permisos se sincronizarán con Spectra Flow/Auth Center.',
      sectionFutureIntegrations: 'Integraciones futuras',
      integFlowLabel: 'Spectra Flow / Auth',
      integFinanceLabel: 'Spectra Finance',
      integStorageLabel: 'Object storage (S3 / Spaces)',
      integClockLabel: 'Reloj o asistencia externa',
      integSignLabel: 'Firma digital / documentos',
      integStatusPending: 'Pendiente',
      integFlowDesc:
        'Identidad, SSO y permisos corporativos centralizados en Spectra Flow / Auth Center (activación futura).',
      integFinanceDesc: 'Sincronización de rendiciones aprobadas y estados con Spectra Finance.',
      integStorageDesc: 'Archivos en DigitalOcean Spaces o Amazon S3; base de datos solo con metadata y claves.',
      integClockDesc: 'Marcaciones desde relojes físicos o proveedores de asistencia externos.',
      integSignDesc: 'Flujos de firma electrónica y custodia documental para contratos y anexos.',
      integrationsFlagPrepared: 'Preparado (demo)',
      integrationsFlagOff: 'Sin flag activo',
      integrationFlowCenterIntro:
        'Flujo futuro de gobernanza: identidad centralizada en Flow, permisos aplicados en RRHH. Sin SSO real en esta versión.',
      integrationFlowStep1: 'El usuario inicia sesión en Spectra Flow.',
      integrationFlowStep2: 'Flow valida el tenant.',
      integrationFlowStep3: 'Flow valida que el módulo RRHH esté habilitado.',
      integrationFlowStep4: 'Flow entrega el rol global.',
      integrationFlowStep5: 'Flow entrega roles por módulo.',
      integrationFlowStep6: 'RRHH aplica permisos locales y guards.',
      integrationFlowStep7: 'RRHH muestra datos del tenant con políticas vigentes.',
      integrationFlowStatusTitle: 'Estado de preparación',
      integrationFlowNoSso:
        'SSO, tokens y conexión en tiempo real contra Auth Center no están activados. Esta vista documenta el contrato esperado.',
      flowRcStatusConceptual: 'Preparado conceptualmente',
      flowRcStatusEndpoint: 'Endpoint futuro',
      flowRcStatusTenant: 'Tenant_id ready',
      flowRcStatusModuleRoles: 'Roles por módulo ready',
      flowRcStatusGranular: 'Permisos granulares ready',
      flowRcStatusPendingReal: 'Pendiente conexión real',
      releaseModuleStateTitle: 'Estado del módulo',
      releaseModuleStateLead: 'Resumen visible para demo comercial. No se muestran secretos ni API keys.',
      releaseRowVersion: 'Versión',
      releaseRowState: 'Estado',
      releaseRowStateValue: 'Final polish / demo-ready',
      releaseRowMode: 'Modo',
      releaseRowModeValue: 'Demo / producción controlada',
      releaseRowEmail: 'Email RRHH',
      releaseRowStorage: 'Storage',
      releaseRowFlow: 'Flow/Auth',
      releaseRowFinance: 'Finance',
      releaseEmailDemo: 'Configurado demo / pendiente validación real',
      releaseEmailReal: 'Canal real detectado — validar políticas antes de cliente',
      releaseStorageMeta: 'Metadata-only / demo',
      releaseStorageSpaces: 'Spaces listo en configuración — validar bucket productivo',
      releaseStoragePending: 'Pendiente validación de credenciales y bucket',
      releaseFlowPrepared: 'Preparado / pendiente conexión real',
      releaseFinancePrepared: 'Preparado / pendiente sync real',
      releaseChecklistTitle: 'Release checklist',
      releaseCheckEmailTitle: 'Email RRHH',
      releaseCheckEmailPending: 'Pendiente: validar credenciales reales antes de cliente.',
      releaseCheckStorageTitle: 'Storage documental',
      releaseCheckStoragePending: 'Pendiente: validar bucket real y políticas de retención.',
      releaseCheckFlowTitle: 'Flow/Auth',
      releaseCheckFlowCurrent: 'Estado actual: preparado conceptualmente.',
      releaseCheckFlowPending: 'Pendiente: conexión real y validación de tokens.',
      releaseCheckFinanceTitle: 'Finance',
      releaseCheckFinanceCurrent: 'Estado actual: preparado para sync de rendiciones.',
      releaseCheckFinancePending: 'Pendiente: sync real de gastos aprobados.',
      releaseCheckMobileTitle: 'Mobile',
      releaseCheckMobileCurrent: 'Estado: QA visual en curso en RC.',
      releaseCheckMobilePending: 'Pendiente: QA final en dispositivos reales.',
      releaseCheckPermTitle: 'Permisos',
      releaseCheckPermCurrent: 'Estado: matriz definida en catálogo.',
      releaseCheckPermPending: 'Pendiente: conectar con Auth Center en producción.',
      connectorsSectionTitle: 'Conectores e integraciones',
      connectorEmailTitle: 'Email RRHH / Resend',
      connectorEmailDesc: 'Estado demo o canal real según variables de entorno. Separado de Email Marketing comercial.',
      connectorEmailUsage: 'Uso: comunicaciones operativas y transaccionales.',
      connectorStorageTitle: 'Storage / DigitalOcean Spaces',
      connectorStorageDesc: 'Metadata-only, demo o Spaces según configuración actual.',
      connectorStorageUsage: 'Uso: documentos, recibos y contratos con URLs firmadas.',
      connectorFlowTitle: 'Spectra Flow/Auth',
      connectorFlowDesc: 'Identidad, tenants y roles corporativos centralizados (activación futura).',
      connectorFlowUsage: 'Uso: usuarios, tenants, roles y permisos globales.',
      connectorFinanceTitle: 'Spectra Finance',
      connectorFinanceDesc: 'Sincronización de rendiciones y estados de pago (preparado).',
      connectorFinanceUsage: 'Uso: rendiciones, reembolsos y gastos aprobados.',
      connectorSignTitle: 'Firma digital',
      connectorSignDesc: 'Roadmap: firma electrónica y custodia para contratos y recibos.',
      connectorSignUsage: 'Uso futuro: contratos, recibos y documentos sensibles.',
      connectorClockTitle: 'Asistencia externa',
      connectorClockDesc: 'Roadmap: relojes biométricos y proveedores de marcación.',
      connectorClockUsage: 'Uso futuro: reloj, biométrico e integraciones de jornada.',
      connectorStateFuture: 'Futuro / no activo',
      connectorStatePrepared: 'Preparado (sin sync real)',
      connectorStateEmailDemo: 'Demo / simulación',
      connectorStateEmailReal: 'Canal real configurado',
      connectorStateStorageMeta: 'Metadata-only',
      connectorStateStorageReady: 'Spaces operativo',
      connectorStateStoragePending: 'Pendiente configuración',
      storageCardCurrentTitle: 'Estado actual (metadata-only)',
      storageCardFutureTitle: 'Próximo: object storage',
      storageDocRuleTitle: 'Arquitectura recomendada',
      securityFlowHint:
        'En producción, sesiones y políticas de acceso se alinearán con Spectra Flow / Auth Center y RBAC por tenant.',
      sectionStorageDocumental: 'Storage documental',
      storageApiTitle: 'Estado en tiempo real (API /api/storage/config)',
      storageApiLoading: 'Consultando configuración de storage…',
      storageApiMode: 'Modo',
      storageApiStatus: 'Estado',
      storageApiEnabled: 'Subidas remotas habilitadas',
      storageApiBucket: 'Bucket configurado',
      storageApiCreds: 'Credenciales configuradas',
      storageApiMax: 'Máximo por archivo',
      storageApiMimes: 'MIME permitidos',
      storageApiVarsBody:
        'Para activar Spaces: RRHH_STORAGE_MODE=spaces, DO_SPACES_KEY, DO_SPACES_SECRET, DO_SPACES_BUCKET, DO_SPACES_ENDPOINT (y opcional DO_SPACES_PUBLIC_BASE_URL). Nunca exponer secretos en el frontend.',
      storageDocState: 'Estado actual: solo metadata en MongoDB (sin archivos pesados en la base).',
      storageDocFuture: 'Proveedor futuro: DigitalOcean Spaces / Amazon S3.',
      storageDocRule: 'Regla: archivos en object storage; Mongo persiste metadata, claves y permisos.',
      matrixCaption: 'Matriz de permisos por módulo (solo lectura, demo)',
      matrixLegend: 'R: lectura · W: escritura · —: sin acceso',
      matrixRoleCol: 'Rol',
      modEmployees: 'Colaboradores',
      modLegajo: 'Legajo',
      modDocuments: 'Documentos',
      modAttendance: 'Asistencia',
      modLeaves: 'Licencias',
      modExpenses: 'Gastos',
      modPersonalFinance: 'Finanzas personales',
      modCommunications: 'Comunicaciones',
      modAwards: 'Premios',
      modSettings: 'Configuración',
      roleRrhhAdmin: 'rrhh_admin',
      roleRrhhManager: 'rrhh_manager',
      roleRrhhOperator: 'rrhh_operator',
      roleSelfService: 'employee_self_service',
      roleFinanceReviewer: 'finance_reviewer',
      roleViewer: 'viewer',
      version: 'Versión',
      lifecycleFlowsTitle: 'Flujos de ciclo del colaborador',
      lifecycleFlowsIntro:
        'Vista de referencia de flujos estándar Spectra. En producción se orquestará con Flow/Auth, Spaces y tareas automáticas.',
      lifecycleOnTitle: 'Onboarding estándar',
      lifecycleOnBody:
        'Checklist de ingreso: datos personales, identificación, datos bancarios, contrato, alta laboral, equipo, accesos, inducción, capacitación obligatoria y reunión con manager.',
      lifecycleOffTitle: 'Offboarding estándar',
      lifecycleOffBody:
        'Aviso de baja, documentación final, devolución de equipo, baja de accesos, liquidación, entrevista de salida, certificados y cierre de legajo.',
      lifecycleDocsTitle: 'Documentos requeridos por etapa',
      lifecycleDocsBody:
        'Onboarding: DNI, CBU, contrato firmado. Operación: certificados y actualizaciones. Offboarding: finiquitos, certificados laborales y cierre documental.',
      lifecycleAutoTitle: 'Tareas automáticas (roadmap)',
      lifecycleAutoBody:
        'Recordatorios a managers, creación de tareas RRHH al cambiar estado del colaborador y webhooks hacia Finance y Legajo.',
      lifecycleIntegTitle: 'Integración futura con Flow/Auth y Spaces',
      lifecycleIntegBody:
        'SSO, permisos por rol y adjuntos en object storage sin cargar binarios en Mongo; misma línea arquitectónica que el resto del tenant.',
    },
    evaluations: {
      eyebrow: 'People suite',
      heroTitle: 'Evaluaciones de desempeño',
      heroLead: 'Seguimiento de ciclos, responsables y próximas acciones. Datos de demostración hasta conectar workflow real.',
      demoBanner: 'MVP visual · datos demo en frontend',
      kpiActive: 'Evaluaciones activas',
      kpiPending: 'Pendientes',
      kpiCompleted: 'Completadas',
      kpiAvg: 'Promedio desempeño',
      cycleTitle: 'Ciclo de evaluación',
      cycleIntro: 'Flujo estándar Spectra para alinear expectativas y feedback.',
      cycleObjectives: 'Objetivos',
      cycleObjectivesDesc: 'Definición compartida de metas y competencias del período.',
      cycleSelf: 'Autoevaluación',
      cycleSelfDesc: 'Reflexión del colaborador con evidencias y logros.',
      cycleManager: 'Evaluación manager',
      cycleManagerDesc: 'Calibración y comentarios constructivos del líder directo.',
      cycleFeedback: 'Feedback final',
      cycleFeedbackDesc: 'Cierre formal, plan de acción y registro en legajo.',
      listTitle: 'Bandeja de evaluaciones',
      managerLabel: 'Manager',
      scoreLabel: 'Score',
      scorePending: 'Score pendiente',
      status_draft: 'Borrador',
      status_pending_employee: 'Pendiente colaborador',
      status_pending_manager: 'Pendiente manager',
      status_completed: 'Completada',
      status_archived: 'Archivada',
      nextActionManager: 'Enviar consolidado a RRHH',
      nextActionSelf: 'Completar autoevaluación',
      nextActionArchive: 'Archivar en legajo digital',
      nextActionPublish: 'Publicar ciclo a managers',
      nextActionNone: 'Sin acción requerida',
    },
    trainings: {
      eyebrow: 'People suite',
      heroTitle: 'Capacitaciones',
      heroLead: 'Catálogo, modalidad y progreso por curso. Vista demo hasta LMS integrado.',
      demoBanner: 'MVP visual · datos demo en frontend',
      kpiActive: 'Cursos activos',
      kpiEnrolled: 'Inscripciones',
      kpiCompleted: 'Cursos completados',
      kpiHours: 'Horas de formación (est.)',
      pathTitle: 'Ruta de aprendizaje recomendada',
      pathIntro: 'Secuencia sugerida para roles operativos y líderes en formación.',
      path1: 'Onboarding Spectra + compliance esencial',
      path2: 'Liderazgo y feedback (live + práctica)',
      path3: 'Deep dive técnico o bienestar según plan de carrera',
      catalogTitle: 'Catálogo y estado',
      progress: 'Progreso',
      participants: 'Participantes',
      modality_async: 'Asincrónico',
      modality_live: 'En vivo',
      modality_blended: 'Mixto',
      status_active: 'Activo',
      status_draft: 'Borrador',
      cat_onboarding: 'Onboarding',
      cat_compliance: 'Compliance',
      cat_leadership: 'Liderazgo',
      cat_technical: 'Técnica',
      cat_wellness: 'Bienestar',
    },
    climate: {
      eyebrow: 'People suite',
      heroTitle: 'Clima y encuestas',
      heroLead: 'Pulso del equipo, eNPS y encuestas activas. Datos ilustrativos para validar UX ejecutiva.',
      demoBanner: 'MVP visual · datos demo en frontend',
      kpiClimate: 'Clima promedio',
      kpiResponses: 'Respuestas recibidas',
      kpiActive: 'Encuestas activas',
      kpiEnps: 'eNPS / pulso',
      pulseTitle: 'Pulso organizacional',
      pulseIntro: 'Indicadores sintéticos por dimensión (demo).',
      pulse_motivation: 'Motivación',
      pulse_workload: 'Carga laboral',
      pulse_leadership: 'Liderazgo',
      pulse_communication: 'Comunicación',
      listTitle: 'Encuestas',
      audience: 'Audiencia',
      closes: 'Cierre',
      responses: 'Respuestas',
      score: 'Score',
      status_active: 'Activa',
      status_draft: 'Borrador',
      status_closed: 'Cerrada',
      aud_all: 'Toda la empresa',
      aud_managers: 'Managers',
      aud_employees: 'Colaboradores',
      risksTitle: 'Riesgos culturales (demo)',
      risksBody:
        'Cuando la carga percibida sube y la motivación baja, conviene coordinar con líderes un plan de alivio y comunicación transparente.',
    },
    lifecycle: {
      title: 'Ciclo del colaborador',
      subtitle:
        'Gestioná onboarding, cambios internos, documentación, tareas y offboarding desde un solo lugar.',
      eyebrow: 'People Operations',
      demoBanner: 'Vista demo · datos de ejemplo en frontend',
      tabsAria: 'Secciones del ciclo',
      tab_onboarding: 'Onboarding',
      tab_offboarding: 'Offboarding',
      tab_timeline: 'Timeline',
      loadError: 'No se pudieron cargar los casos de ciclo de vida.',
      emptyOnboard: 'No hay procesos de onboarding activos.',
      emptyOffboard: 'No hay procesos de offboarding activos.',
      emptyTimeline: 'Sin eventos de ciclo de vida registrados.',
      onboardTitle: 'Onboarding',
      offboardTitle: 'Offboarding',
      timelineTitle: 'Línea de tiempo',
      heroTitle: 'Ciclo del colaborador',
      statOnboardActive: 'Onboardings activos',
      statOffboardActive: 'Offboardings activos',
      statAvgProgress: 'Progreso promedio',
      loading: 'Cargando…',
      timelineRailHint: 'Línea de tiempo visual con hitos clave del colaborador (demo).',
      kpiOnboarding: 'Onboardings activos',
      kpiDocsPending: 'Pendientes de documentación',
      kpiTasksOverdue: 'Tareas vencidas',
      kpiOffboarding: 'Offboardings activos',
      kpiAvgProgress: 'Progreso promedio',
      flowStatus_pending: 'Pendiente',
      flowStatus_in_progress: 'En progreso',
      flowStatus_blocked: 'Bloqueado',
      flowStatus_completed: 'Completado',
      progress: 'Progreso',
      nextAction: 'Próxima acción',
      manager: 'Manager',
      startDate: 'Fecha de ingreso',
      endDate: 'Fecha de egreso',
      checklistOnTitle: 'Checklist de ingreso',
      checklistOffTitle: 'Checklist de egreso',
      checkOnboard_personal: 'Datos personales',
      checkOnboard_idDoc: 'DNI / identificación',
      checkOnboard_bank: 'CBU / datos bancarios',
      checkOnboard_contract: 'Contrato',
      checkOnboard_laborReg: 'Alta laboral',
      checkOnboard_equipment: 'Equipo / herramientas',
      checkOnboard_access: 'Accesos',
      checkOnboard_induction: 'Inducción inicial',
      checkOnboard_mandatoryTraining: 'Capacitación obligatoria',
      checkOnboard_managerMeet: 'Reunión con manager',
      checkOff_notice: 'Aviso de baja',
      checkOff_finalDocs: 'Documentación final',
      checkOff_equipmentReturn: 'Devolución de equipo',
      checkOff_accessRevoke: 'Baja de accesos',
      checkOff_settlement: 'Liquidación final',
      checkOff_exitInterview: 'Entrevista de salida',
      checkOff_certificates: 'Certificados',
      checkOff_fileClose: 'Cierre de legajo',
      onboardNextBank: 'Completar CBU y datos bancarios en legajo.',
      onboardNextContract: 'Bloqueado: falta contrato firmado y alta en sistema.',
      onboardNextNone: 'Onboarding completo. Archivar checklist en legajo.',
      onboardNextPersonal: 'Iniciar carga de datos personales y documentación.',
      offboardNextAccess: 'Pendiente: revocar accesos corporativos.',
      offboardNextFile: 'Cerrar legajo digital y certificados finales.',
      emptyOnboarding: 'No hay colaboradores en onboarding con los filtros actuales.',
      focusFilter: 'Filtro desde colaboradores',
      alert_access_pending: 'Accesos pendientes',
      alert_equipment_pending: 'Equipo no devuelto',
      alert_docs_pending: 'Documentación final pendiente',
      evType_hire_created: 'Alta',
      evType_contract_uploaded: 'Contrato',
      evType_doc_approved: 'Documento',
      evType_leave_requested: 'Licencia',
      evType_expense_submitted: 'Gasto',
      evType_eval_completed: 'Evaluación',
      evType_training_assigned: 'Capacitación',
      evType_award: 'Reconocimiento',
      evType_area_change: 'Cambio interno',
      evType_offboard_started: 'Baja',
      evHireCreated: 'Alta de colaborador creada en Spectra RRHH.',
      evContract: 'Contrato laboral cargado en metadata (pendiente firma digital).',
      evDocApproved: 'Documento de identidad aprobado por RRHH.',
      evLeave: 'Licencia solicitada y en revisión.',
      evExpense: 'Rendición de gastos enviada a aprobación.',
      evEval: 'Evaluación de desempeño completada.',
      evTraining: 'Capacitación obligatoria asignada al colaborador.',
      evAward: 'Reconocimiento publicado en cultura.',
      evArea: 'Cambio de área registrado en organigrama.',
      evOffboard: 'Proceso de baja iniciado — checklist de egreso activo.',
      stageOperational: 'En operación',
      stageOnboarding: 'Onboarding',
      stageOffboarding: 'Offboarding',
      stageInactive: 'Inactivo',
    },
    comingSoon: {
      badge: 'Próximamente',
      badgePrepared: 'Preparado',
      genericSubtitle: 'Módulo en roadmap enterprise — activación en versiones futuras.',
      valueProposition:
        'Cada módulo se activa con el mismo estándar Spectra: permisos, auditoría y experiencia unificada.',
      preparedNote:
        'Este módulo forma parte del roadmap Spectra RRHH y se habilitará en versiones posteriores.',
      featuresTitle: 'Capacidades previstas',
      records: {
        title: 'Legajos',
        category: 'Personas',
        desc: 'Historial completo del colaborador: documentos, contratos, licencias, evaluaciones y reconocimientos.',
        f1: 'Vista única del colaborador con línea de tiempo',
        f2: 'Contratos y anexos versionados',
        f3: 'Integración con licencias y desempeño',
        f4: 'Auditoría y permisos por rol',
      },
      documents: {
        title: 'Documentos',
        category: 'Personas',
        desc: 'Gestión documental con vencimientos, visibilidad y futuro storage S3/Spaces.',
        f1: 'Carpetas por colaborador y área',
        f2: 'Alertas de vencimiento',
        f3: 'Firma y trazabilidad',
        f4: 'Storage seguro en la nube',
      },
      employeePortal: {
        title: 'Portal colaborador',
        category: 'Personas',
        desc: 'Autogestión de datos, solicitudes, documentos, beneficios y finanzas personales.',
        f1: 'Actualización de datos personales',
        f2: 'Solicitudes y seguimiento',
        f3: 'Beneficios y nómina resumida',
        f4: 'Mis Finanzas integrado',
      },
      attendance: {
        title: 'Asistencia',
        category: 'Procesos',
        desc: 'Registro de jornada, remoto/presencial, ausencias y reportes.',
        f1: 'Marcación y políticas de horario',
        f2: 'Remoto vs presencial',
        f3: 'Integración con licencias',
        f4: 'Reportes para líderes',
      },
      payrollReceipts: {
        title: 'Recibos',
        category: 'Procesos',
        desc: 'Recibos digitales, firma y trazabilidad.',
        f1: 'Recibos por período',
        f2: 'Firma electrónica',
        f3: 'Historial descargable',
        f4: 'Cumplimiento legal',
      },
      communications: {
        title: 'Comunicaciones',
        category: 'Cultura',
        desc: 'Anuncios internos, segmentación por área y confirmación de lectura.',
        f1: 'Anuncios segmentados',
        f2: 'Confirmación de lectura',
        f3: 'Plantillas ejecutivas',
        f4: 'Programación de envíos',
      },
      feed: {
        title: 'Feed interno',
        category: 'Cultura',
        desc: 'Cultura, novedades, cumpleaños, logros y reconocimientos.',
        f1: 'Muro de cultura',
        f2: 'Cumpleaños y aniversarios',
        f3: 'Logros de equipo',
        f4: 'Integración con premios',
      },
      compliance: {
        title: 'Compliance',
        category: 'Cultura',
        desc: 'Canal interno, incidentes, seguimiento y auditoría.',
        f1: 'Canal confidencial',
        f2: 'Gestión de incidentes',
        f3: 'Workflow de revisión',
        f4: 'Reportes para compliance',
      },
      performance: {
        title: 'Desempeño',
        category: 'Desarrollo',
        desc: 'Evaluaciones, feedback, competencias y planes de mejora.',
        f1: 'Ciclos de evaluación',
        f2: 'Feedback 360',
        f3: 'Competencias y OKRs',
        f4: 'Planes de desarrollo',
      },
      recruiting: {
        title: 'Reclutamiento',
        category: 'Desarrollo',
        desc: 'Vacantes, candidatos, entrevistas y conversión a colaborador.',
        f1: 'Pipeline de candidatos',
        f2: 'Entrevistas y scorecards',
        f3: 'Ofertas y aprobaciones',
        f4: 'Alta automática en RRHH',
      },
      training: {
        title: 'Capacitaciones',
        category: 'Desarrollo',
        desc: 'Cursos, progreso, certificados y vencimientos.',
        f1: 'Catálogo de cursos',
        f2: 'Progreso por colaborador',
        f3: 'Certificados',
        f4: 'Recordatorios de vencimiento',
      },
      surveys: {
        title: 'Encuestas',
        category: 'Desarrollo',
        desc: 'Clima laboral, eNPS, satisfacción y formularios internos.',
        f1: 'eNPS y clima',
        f2: 'Formularios drag & drop',
        f3: 'Anonimato configurable',
        f4: 'Dashboard de resultados',
      },
      tasks: {
        title: 'Tareas',
        category: 'Procesos',
        desc: 'Onboarding, offboarding, checklists y responsables.',
        f1: 'Plantillas por proceso',
        f2: 'Asignación y SLA',
        f3: 'Checklists de onboarding',
        f4: 'Seguimiento por área',
      },
      reports: {
        title: 'Reportes',
        category: 'Intelligence',
        desc: 'People analytics, rotación, ausentismo, gastos y performance.',
        f1: 'KPIs de RRHH',
        f2: 'Rotación y retención',
        f3: 'Ausentismo y licencias',
        f4: 'Exportación ejecutiva',
      },
      peopleIntelligence: {
        title: 'People Intelligence',
        category: 'Intelligence',
        desc: 'Insights IA, riesgo de rotación, sugerencias y alertas.',
        f1: 'Alertas de riesgo',
        f2: 'Sugerencias accionables',
        f3: 'Benchmark interno',
        f4: 'Integración con datos operativos',
      },
    },
    status: {
      onboarding: 'Onboarding',
      inactive: 'Inactivo',
      offboarding: 'Offboarding',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
      draft: 'Borrador',
      submitted: 'Enviada',
      manager_approved: 'Aprobada por manager',
      finance_approved: 'Aprobada por finanzas',
      paid: 'Pagado',
    },
    common: {
      loading: 'Cargando…',
      error: 'Error al cargar datos',
      retry: 'Reintentar',
      actions: 'Acciones',
      suitePrivacyLine: 'Datos personales protegidos; roles preparados para Spectra Flow/Auth; documentos listos para storage seguro.',
      emptyPremiumHint: 'Todavía no hay datos en esta vista. Cargá información o usá el modo demo para la presentación.',
    },
    leaveTypes: {
      vacation: 'Vacaciones',
      sick: 'Enfermedad',
      study: 'Estudio',
      personal: 'Personal',
      remote: 'Trabajo remoto',
      other: 'Otro',
    },
    leaveStatuses: {
      pending: 'Pendiente',
      approved: 'Aprobada',
      rejected: 'Rechazada',
      cancelled: 'Cancelada',
    },
    expenseCategories: {
      travel: 'Viajes / Traslados',
      meals: 'Comidas',
      office: 'Oficina',
      training: 'Capacitación',
      health: 'Salud',
      equipment: 'Equipamiento',
      other: 'Otros',
    },
    employmentType: {
      full_time: 'Tiempo completo',
      part_time: 'Medio tiempo',
      contractor: 'Contratista',
      intern: 'Pasante',
    },
    employeeStatus: {
      active: 'Activo',
      onboarding: 'Onboarding',
      inactive: 'Inactivo',
      offboarding: 'Offboarding',
    },
  },
  en: {
    brand: 'Spectra HR',
    brandTagline: 'Run the full employee lifecycle from one place.',
    version: 'v0.6.0-rrhh-flow-ia-premium',
    navGroups: {
      home: 'Home',
      workspace: 'Workspace',
      people: 'People',
      processes: 'Processes',
      culture: 'Culture',
      development: 'Development',
      intelligence: 'Intelligence',
      system: 'System',
    },
    nav: {
      dashboard: 'Dashboard',
      employees: 'People',
      employeeLifecycle: 'Employee lifecycle',
      orgChart: 'Org chart',
      leaves: 'Time off',
      expenses: 'Expense reports',
      personalFinance: 'My finances',
      awards: 'Awards',
      records: 'Digital records',
      documents: 'Documents',
      employeePortal: 'Employee portal',
      attendance: 'Attendance / Shifts',
      payrollReceipts: 'Payslips',
      communications: 'Communications',
      notifications: 'Notifications',
      feed: 'Wall',
      chat: 'Chat',
      compliance: 'Compliance',
      performance: 'Performance reviews',
      recruiting: 'Recruiting',
      training: 'Training',
      surveys: 'Climate & surveys',
      tasks: 'Tasks',
      reports: 'Reports',
      peopleIntelligence: 'People Intelligence',
      settings: 'Settings',
    },
    sidebar: {
      collapse: 'Collapse sidebar',
      expand: 'Expand sidebar',
      navPiBadge: 'Intelligence IA',
      navPiBadgeShort: 'IA',
    },
    navBadge: {
      active: 'Active',
      next: 'Next',
    },
    productStatus: {
      active: 'Active',
      prepared: 'Ready',
      soon: 'Coming soon',
    },
    header: {
      themeLight: 'Light',
      themeDark: 'Dark',
      themeSwitchToDark: 'Switch to dark mode',
      themeSwitchToLight: 'Switch to light mode',
      demoBadge: 'Demo',
      userFallback: 'Usuario',
      online: 'Online',
      onlineTitle: 'Service operational',
      flowPreparedTitle: 'Spectra Flow/Auth integration',
      flowPreparedShort: 'Flow/Auth ready',
      flowSoon: 'Spectra Flow connection coming soon',
      breadcrumbHome: 'Home',
    },
    time: {
      relative: {
        justNow: 'Just now',
        minutesAgo: '{n} min ago',
        todayAt: 'Today · {time}',
        yesterday: 'Yesterday',
      },
    },
    notifyBell: {
      aria: 'Notification center',
      title: 'Notifications',
      subtitle: 'Unified hub for HR and employees (local demo).',
      markAllRead: 'Mark all as read',
      markRead: 'Mark as read',
      emptyTitle: 'You have no pending notifications',
      emptyBody: 'When you publish announcements or alerts appear, they will show here and in Communications.',
      emptyCta: 'Go to Communications',
      sectionToday: 'Today',
      sectionEarlier: 'Earlier',
      priority: 'Priority',
      ctaOpen: 'Open',
      filter_all: 'All',
      filter_unread: 'Unread',
      filter_comms: 'Communications',
      filter_tasks: 'Tasks',
      filter_leaves: 'Time off',
      filter_docs: 'Documents',
      filter_expenses: 'Expenses',
      filter_flow_ia: 'Flow IA',
      ctaOpenComms: 'View announcement',
      ctaReviewDoc: 'Review document',
      ctaOpenLeave: 'View time off',
      ctaOpenExpense: 'View expense',
      ctaOpenFlowIa: 'Open Flow IA',
      ctaOpenLifecycle: 'View lifecycle',
      ctaOpenTraining: 'View training',
      ctaOpenSurvey: 'Open survey',
      sysPublishedTitle: 'Announcement published: {title}',
      sysPublishedBody: 'Available under Communications and in this center.',
      seed: {
        comms1Title: 'New internal announcement published',
        comms1Body: 'Open Communications to read the full notice.',
        docs1Title: 'Document pending review',
        docs1Body: 'There is an item in Digital Records awaiting your signature or review.',
        leave1Title: 'Time off updated',
        leave1Body: 'Your request status changed. Open Time off for details.',
        exp1Title: 'Expense update',
        exp1Body: 'An expense report has a status update.',
        flow1Title: 'Flow IA recommendation',
        flow1Body: 'A new contextual suggestion based on your frequent screens.',
        task1Title: 'Onboarding / offboarding task',
        task1Body: 'There is a pending step in the employee lifecycle.',
        train1Title: 'Training assigned',
        train1Body: 'A mandatory or recommended training was assigned to you.',
        survey1Title: 'Climate survey available',
        survey1Body: 'Participate in the pulse survey when you can.',
      },
    },
    rc: {
      demoEnvironmentNotice:
        'This environment is ready for commercial demos. Real Email, Storage, Flow/Auth, and Finance connections must be validated before production use with customers.',
    },
    flowIa: flowIaEn,
    dashboard: {
      title: 'HR Dashboard',
      subtitle:
        'Centralize records, payslips, time off, attendance, communications, and talent. People Intelligence helps prioritize HR risks and next steps.',
      rcEyebrow: 'Spectra HR · People OS',
      rcHeaderLine: 'Local environment — demo data and simulated role.',
      rcHeaderLineProd: 'People OS with contextual Flow IA and multi-tenant operations.',
      heroTitle: 'Intelligent Human Management',
      heroLead:
        'Centralize people, time off, expenses, culture, and financial wellbeing in one platform.',
      badgeDemo: 'Commercial preview',
      badgeCommercialDemo: 'Commercial demo',
      badgeFlowIa: 'Flow IA',
      badgeRC: 'People OS',
      badgeVersionShort: 'v0.3.5',
      badgeFlow: 'Ready for Spectra Flow/Auth',
      ctaEmployees: 'View people',
      ctaExpenses: 'Review expenses',
      commandCenter: 'People Command Center',
      kpiExpensesApprove: 'Expenses awaiting approval',
      avgFinancialScore: 'Financial score (demo)',
      priorityActions: 'Priority actions',
      priority1: 'Approve pending time off',
      priority2: 'Review expense reports',
      priority3: 'Recognize standout contributors',
      priority4: 'Complete employee file data',
      priority5: 'Prepare Flow/Auth connection',
      strategicCardLegajos: 'Digital employee files',
      strategicCardLegajosDesc: 'Single source of truth for each person.',
      strategicCardPortal: 'Employee portal',
      strategicCardPortalDesc: 'Self-service and requests.',
      strategicCardRecibos: 'Digital payslips',
      strategicCardRecibosDesc: 'Signed, traceable payroll documents.',
      strategicCardPI: 'People Intelligence',
      strategicCardPIDesc: 'Alerts, risk signals, and suggestions.',
      strategicCardWellbeing: 'Financial wellbeing',
      strategicCardWellbeingDesc:
        'Private and secure: My Finances adds budget, goals, and personal projection tabs (demo). The company cannot see your transactions.',
      strategicCardFinance: 'Finance integration',
      strategicCardFinanceDesc: 'Sync approved expenses to Finance.',
      strategicCardReports: 'Executive reports',
      strategicCardReportsDesc: 'Monthly pack and area cuts for leadership.',
      activeEmployees: 'Active employees',
      newHires: 'New hires (month)',
      pendingLeaves: 'Pending leaves',
      pendingExpenses: 'Pending expenses',
      unpaidExpenses: 'Unpaid expenses',
      climate: 'Engagement score',
      employeeMonth: 'Employee of the month',
      upcomingLeaves: 'Upcoming leaves',
      recentExpenses: 'Recent expenses',
      recentAwards: 'Recent recognition',
      insights: 'People insights',
      insightRetention: 'Projected retention is stable with a focus on remote onboarding.',
      insightSkills: 'In-demand skills: leadership, data, and product design.',
      insightPending1: 'requests need approval',
      insightPending2: 'expense reports awaiting payment',
      insightWellbeing: 'Financial wellbeing improves with active budgets.',
      insightFlow: 'Next step: connect Flow/Auth for real roles and permissions.',
      noLeaves: 'No upcoming leaves.',
      noExpenses: 'No recent expenses.',
      noAwards: 'No recent awards.',
      pendingActions: 'Pending actions',
      upcomingEvents: 'Upcoming events',
      recentActivity: 'Recent activity',
      strategicModules: 'Strategic modules',
      strategicHint: 'Explore the enterprise capability roadmap.',
      commsPanelTitle: 'Internal & operational comms',
      commsPanelSubtitle: 'Demo snapshot: alerts, tasks, and prepared messages (no real send).',
      commsCritical: 'Critical notifications',
      commsTasks: 'Pending HR tasks',
      commsEmailsPrepared: 'Prepared HR emails',
      commsRecentBulletins: 'Recent announcements',
      commsNextActions: 'Next communication actions',
      commsInternalInd: 'Internal comms pulse',
      commsLinkNotifications: 'Open notifications',
      commsLinkCommunications: 'Open communications',
      commsEmailsRecent: 'Sent / simulated (log)',
      commsEmailLogTitle: 'Latest HR email log',
      commsNext1: 'Prepare a documentation reminder via HR emails.',
      commsNext2: 'Publish a policy or benefits announcement.',
      commsNext3: 'Align critical tasks with managers in Notifications.',
      commsInternalStatus: 'Active (demo)',
      strategicRecords: 'Single employee history and documentation.',
      strategicDocs: 'Expiry, visibility, and secure storage.',
      strategicTraining: 'Continuous learning with certification.',
      strategicReports: 'People analytics and executive export.',
      strategicPi: 'AI for talent signals and risk.',
      eventPlaceholder: 'No scheduled events. Calendar integration is on the roadmap.',
      activityPlaceholder: 'Recent activity will appear here with audit trails and notifications.',
      bulletLeaves: 'leave requests are pending approval.',
      bulletExpenses: 'expense reports are in progress or awaiting payment.',
      bulletWellbeing: 'Financial wellbeing improves with active budgets.',
      bulletFlow: 'Next step: connect Flow/Auth for real roles and permissions.',
      noEom: 'No spotlight for this period.',
      docsStripTitle: 'HR documentation',
      docsStripCta: 'Digital records',
      docKpiTotal: 'Documents',
      docKpiPending: 'Pending',
      docKpiExpired: 'Expired',
      docKpiApproved: 'Approved',
      docKpiCompliance: 'Compliance',
      kpiTotalEmployees: 'Total employees',
      kpiPresentToday: 'Present today',
      kpiCommsPublished: 'Published announcements',
      kpiPiScore: 'People Intelligence (rules v1)',
      kpiPendingExpenseAmount: 'Pending expenses (amount)',
      rrhhNextTitle: 'Next HR actions',
      rrhhActionDocs: 'Review expired or pending documents',
      rrhhActionLeaves: 'Approve time off',
      rrhhActionExpenses: 'Review expense reports',
      rrhhActionComms: 'Publish an internal announcement',
      rrhhActionAttendance: 'Review today’s attendance',
      rrhhActionLifecycle: 'Open employee lifecycle',
      rrhhActionOnboardComplete: 'Complete onboarding for new hires',
      rrhhActionOffboardBlocked: 'Close blocked offboarding',
      peopleOpsTitle: 'People Operations',
      peopleOpsSubtitle: 'Onboarding, offboarding, and critical lifecycle tasks (demo + tenant data).',
      peopleKpiOnboard: 'Active onboardings',
      peopleKpiOffboard: 'Active offboardings',
      peopleKpiHrTasks: 'Pending HR tasks',
      peopleKpiDocs: 'Pending documentation',
      peopleKpiIncoming: 'Upcoming hires',
      extraKpisTitle: 'Extended operational KPIs',
      extraKpisHint: 'Includes attendance, communications, and People Intelligence v1 (rules-based).',
      execSystemBadge: 'Demo environment online',
      kpiGridTitle: 'Executive KPIs',
      kpiActiveShort: 'Active',
      kpiDocsPair: 'Pending / expired docs',
      kpiDocMissingFile: 'Required docs without file',
      kpiPayslipsAvail: 'Payslips available',
      kpiPayslipsDraft: 'Payslip drafts',
      kpiStorageRuntime: 'Storage status',
      securityCardTitle: 'Security & permissions',
      securityCardSubtitle: 'Access governance status (demo + local catalog).',
      securityRolesLabel: 'Roles defined',
      securityPermsLabel: 'Permissions prepared',
      securityFlowLabel: 'Flow/Auth',
      securityRiskLabel: 'Configuration risks',
      securityRolesValue: '6 in catalog',
      securityPermsValue: 'Matrix ready',
      securityFlowValue: 'Next-stage integration',
      securityFlowStatus: 'Pending',
      securityRiskValue: 'Review simulated role & demo',
      securityRiskBadge: 'Review',
      securityCta: 'Open roles & permissions',
      storageStatus_metadata_only: 'Metadata only',
      storageStatus_ready: 'Spaces ready',
      storageStatus_missing_config: 'Missing config',
      piSnapshotTitle: 'People Intelligence · snapshot',
      piSnapshotRules: 'Intelligence v1 is rules-based.',
      piSnapshotNext: 'Next: contextual AI integrated.',
      activitySectionTitle: 'Recent activity',
      activityColDocs: 'Documents',
      activityColComms: 'Announcements',
      activityColLeaves: 'Time off',
      activityColExpenses: 'Expenses',
      activityEmpty: 'No recent items in this lane.',
      activityDemoHint: 'Fed with tenant (demo) data.',
      rrhhActionsSubtitle: 'Direct links to run the HR cycle.',
      suiteTitle: 'HR suite',
      suiteSubtitle: 'Visual status of core tenant modules (demo + loaded data).',
      suiteLifecycle: 'Employee lifecycle',
      suiteLegajo: 'Records & documents',
      suiteAttendance: 'Attendance',
      suiteLeaves: 'Time off',
      suiteExpenses: 'Expense reports',
      suiteEvaluations: 'Performance reviews',
      suiteTraining: 'Training',
      suiteClimate: 'Climate & surveys',
      suiteComms: 'Communications',
      suitePF: 'My finances',
      suiteStatusActive: 'Active',
      suiteStatusPrepared: 'Ready',
      risksTitle: 'Priority risks',
      risksBody: 'Demo consolidation: prioritize critical docs, absenteeism, and cycle closures before month-end.',
      risk1: 'Expired or unapproved documentation for key employees.',
      risk2: 'Overlapping time off in operational teams.',
      risk3: 'Expenses pending sync with Finance.',
      risk4: 'Performance reviews waiting on manager input.',
      risk5: 'Permissions & Spectra Flow/Auth still in preparation — validate before go-live.',
      periodLabel: 'Period',
      execSummaryTitle: 'Executive summary',
      execSummarySubtitle: 'Key metrics for leadership reviews.',
      execActive: 'Active employees',
      execCompliance: 'Document compliance',
      execClimate: 'Average climate',
      execAttendance: 'Attendance today',
      execTasks: 'Critical tasks',
      execPi: 'People Intelligence',
      recommendedTitle: 'Recommended actions',
      recommended1: 'Regularize critical documents and expirations in the digital file.',
      recommended2: 'Approve or respond to pending time off.',
      recommended3: 'Review overdue tasks in Notifications.',
      recommended4: 'Send operational reminders from HR emails (simulation or prepared mode).',
      recommended5: 'Complete onboarding for new joiners.',
      suitePortal: 'Employee portal',
      suitePI: 'People Intelligence',
      suiteReports: 'Executive reports',
      moduleStateTitle: 'Module status',
      moduleStateVersion: 'Version',
      moduleStateProduct: 'Status',
      moduleStateProductValue: 'Release Candidate',
      moduleStateMode: 'Mode',
      moduleStateModeValue: 'Demo / controlled production',
      moduleStateEmail: 'HR email',
      moduleStateEmailDemo: 'Demo configuration / pending real validation',
      moduleStateEmailReal: 'Real channel detected — validate policies before go-live',
      moduleStateStorage: 'Document storage',
      moduleStateStorageMeta: 'Demo (metadata-only)',
      moduleStateStorageReady: 'Spaces ready in config — validate production bucket',
      moduleStateStoragePending: 'Pending bucket and credential validation',
      moduleStateFlow: 'Flow/Auth',
      moduleStateFlowValue: 'Prepared / pending real connection',
      moduleStateFinance: 'Finance',
      moduleStateFinanceValue: 'Prepared / pending real sync',
      moduleStateCta: 'Open checklist in Settings',
      opsHrTitle: 'HR operational status',
      opsHrSubtitle: 'Quick access to tenant pillars (demo view + seeded data).',
      opsHrLink: 'Open',
      opsLegajo: 'Records',
      opsAttendance: 'Attendance',
      opsLeaves: 'Time off',
      opsExpenses: 'Expenses',
      opsComms: 'Communications',
      opsEval: 'Performance reviews',
      opsTraining: 'Training',
      opsClimate: 'Climate',
      opsReports: 'Reports',
      demoReadyTitle: 'Demo-ready checklist',
      demoReadySubtitle: 'Visual checklist for commercial walkthroughs.',
      demoCk1: 'Demo data loaded for the tenant',
      demoCk2: 'Core modules visible and navigable',
      demoCk3: 'HR emails prepared (no real send until validation)',
      demoCk4: 'Document storage prepared (metadata or Spaces per config)',
      demoCk5: 'Roles and permissions defined in catalog',
      demoCk6: 'Flow/Auth prepared conceptually',
      demoCk7: 'Executive reports available in catalog',
      nextRealTitle: 'Before a real customer',
      nextReal1: 'Validate real Resend credentials and domains.',
      nextReal2: 'Validate DigitalOcean Spaces bucket and retention.',
      nextReal3: 'Connect Spectra Flow/Auth and production tenant_id.',
      nextReal4: 'Validate tenant_id and data segregation.',
      nextReal5: 'Finalize permissions matrix by role.',
      nextReal6: 'Test mobile and tablet with pilot users.',
      nextReal7: 'Define backup, rollback, and cutover owners.',
      proOrgHealthTitle: 'Organizational health',
      proOrgHealthLead: 'Composite indicator (compliance, Intelligence, climate; demo).',
      proOrgHealthBadge: 'People OS score',
      proExecKpiTitle: 'Executive KPIs',
      proExecKpiLead: 'Quick read for leadership: headcount, operations, and talent.',
      proHeadcount: 'Active headcount',
      proAbsentee: 'Estimated absenteeism',
      proAbsenteeHint: 'Derived from attendance and time off (demo).',
      proLeavesPen: 'Pending time off',
      proClimateShort: 'Employee climate',
      proEvalsPen: 'Reviews in flight',
      proTrainGap: 'Training focus',
      proExpensesPen: 'Expenses in pipeline',
      proSmartAlerts: 'Smart alerts',
      proAlert1: 'Critical documentation with upcoming expiry.',
      proAlert2: 'Finance sync pending on approved expense reports.',
      proAlert3: 'Critical tasks in Notifications still open.',
      proNextExecTitle: 'Suggested next actions',
      proNextExec1: 'Prioritize records and payslips before payroll close.',
      proNextExec2: 'Resolve time off that impacts coverage and SLAs.',
      proNextExec3: 'Publish the internal month-end communication.',
      proRisksExecTitle: 'Detected risks (consolidated view)',
      proRisksExecLead: 'Cross-tenant signals (demo + loaded data).',
      proOppsExecTitle: 'Improvement opportunities',
      proOppsExecLead: 'Culture and efficiency moves with high perceived return.',
      finWellbeingAggregatedNote:
        'Financial wellbeing shown here is aggregated for the demo; personal activity stays in each employee’s My Finances.',
    },
    dashboard93: {
      heroEyebrow: 'HR console',
      greetingMorning: 'Good morning, {name}',
      greetingAfternoon: 'Good afternoon, {name}',
      greetingEvening: 'Good evening, {name}',
      heroStable: 'Your organization looks stable.',
      heroAttention: '{n} topics need attention.',
      heroFlowCta: 'Open Flow IA',
      statusStable: 'Overall status: stable',
      statusWatch: 'Overall status: watch pending items',
      kpiSectionTitle: 'Key indicators',
      kpiCritical: 'Critical pending',
      kpiDocHealth: 'Document health',
      prioritiesTitle: 'Needs attention',
      prioritiesLead: 'Operational items with immediate impact.',
      prioritiesEmpty: 'No critical pending items right now.',
      priorityLeaves: 'Pending leave requests',
      priorityDocs: 'Documents due or in review',
      priorityOnboarding: 'Onboarding in progress',
      priorityMissingFile: 'Documents missing files',
      priorityExpenses: 'Pending expense reports',
      priorityCtaReview: 'Review',
      priorityCtaOpen: 'Open',
      priorityCtaTrack: 'Track',
      priorityCtaFix: 'Fix',
      priorityCtaApprove: 'Approve',
      flowIaLabel: 'Flow IA',
      suggestionBadge: 'Flow IA suggestion',
      flowIaSeeMore: 'See more',
      flowIaGoModule: 'Go to module',
      flowIaEmpty: 'No critical signals at the moment.',
      flowIaEmptyAction: 'Ask Flow IA to dig deeper.',
      flowIaDocsDue: 'I detected {n} document(s) due or in review.',
      flowIaDocsDueAction: 'Prioritize Digital Records before month-end.',
      flowIaLeavesPending: '{n} leave request(s) need resolution.',
      flowIaLeavesPendingAction: 'Review coverage and approvals.',
      flowIaMgrLeaves: 'You have {n} leave request(s) awaiting approval.',
      flowIaMgrLeavesAction: 'Your team is waiting for a response.',
      flowIaExpenses: '{n} expense report(s) in progress.',
      flowIaExpensesAction: 'Align with Finance if needed.',
      flowIaPiAction: 'See detail in People Intelligence.',
      flowIaStable: 'Stable operations — no critical alerts.',
      flowIaStableAction: 'Flow IA keeps monitoring the tenant.',
      activityTitle: 'Recent activity',
      activityLead: 'Compact tenant timeline.',
      activityEmpty: 'No recent activity to show.',
      activityError: 'Could not load extended activity.',
      activityOpen: 'View',
      activityDoc: 'Document: {title}',
      activityComm: 'Communication: {title}',
      activityLeave: 'Leave: {name}',
      activityExpense: 'Expense: {name}',
      activityAward: 'Recognition: {title}',
      activityLifecycle: 'Lifecycle: {action}',
      legacyAnalyticsSummary: 'Full analytics and legacy modules',
    },
    hub93: {
      greeting: 'Hi, {name}',
      navAria: 'Personal shortcuts',
      navShift: 'My shift',
      navVacation: 'My time off',
      navDocs: 'My documents',
      navRequests: 'My requests',
      quickTitle: 'Quick access',
      quickLeave: 'Request leave',
      quickPayslips: 'View payslips',
      quickUpload: 'Upload document',
      quickAttendance: 'View attendance',
      statusTitle: 'My status',
      statusVacationAvail: 'Next leave',
      statusScheduled: 'Scheduled',
      statusNone: 'Not scheduled',
      statusNextLeave: 'Start date',
      statusDocsPending: 'Pending documents',
      statusAttendanceToday: 'Attendance today',
      statusAttendanceOpen: 'Check',
      statusAttendanceHint: 'Clock in from Attendance',
      newsTitle: 'Updates',
      newsSeeAll: 'All communications',
      newsEmpty: 'No recent updates.',
      newsError: 'Could not load communications.',
      newsOpen: 'Open',
      teamTitle: 'My team',
      teamError: 'Could not load team information.',
      teamMembers: 'Members',
      teamAbsences: 'Pending leaves',
      teamPendingExp: 'Pending expenses',
      teamCtaApprovals: 'Review approvals',
      flowGo: 'Go',
      flowDocPending: 'You have {n} document(s) pending signature or upload.',
      flowDocPendingAction: 'Complete them from your records.',
      flowExpPending: 'You have {n} expense report(s) in progress.',
      flowExpPendingAction: 'Track status under My expenses.',
      flowMgrLeaves: 'You have {n} leave request(s) awaiting your approval.',
      flowMgrLeavesAction: 'Review team requests.',
      flowRrhhDefault: 'Stable operation on your profile.',
      flowRrhhDefaultAction: 'Return to the ops dashboard when needed.',
      flowAllClear: 'You are up to date.',
      flowAllClearAction: 'Flow IA will notify you when something new appears.',
      alertsAria: 'Personal alerts',
      cultureTitle: 'Community',
      cultureEvents: 'Upcoming events',
      cultureEventsEmpty: 'No upcoming events.',
      cultureRecognitions: 'Recent recognition',
      cultureRecognitionsEmpty: 'No recent recognition yet.',
      cultureAnniversaries: 'Upcoming anniversaries',
      cultureAnniversariesEmpty: 'No anniversaries in the next 30 days.',
      cultureBirthdays: 'Upcoming birthdays',
      cultureBirthdaysEmpty: 'No upcoming birthdays.',
      cultureFeed: 'Wall updates',
      cultureFeedLink: 'Open Wall',
      cultureFeedEmpty: 'No wall posts yet.',
      cultureError: 'Could not load community.',
    },
    feedWall: {
      title: 'Wall',
      lead: 'Team updates — simple, clear, tenant-scoped.',
      composerAria: 'New post',
      composerPlaceholder: 'Share an update…',
      audienceLabel: 'Audience',
      audienceOrg: 'Whole organization',
      audienceTeam: 'My team',
      audienceDepartment: 'My department',
      publish: 'Publish',
      flowIaImprove: 'Improve with Flow IA',
      flowIaImprovePrompt: 'Improve this wall draft. Be clear, warm, and concise. Return only the improved text.',
      empty: 'No posts yet.',
      emptyCta: 'Create first post',
      errorLoad: 'Could not load the wall.',
      errorPublish: 'Could not publish.',
      errorFlowIa: 'Flow IA is unavailable right now.',
      forbidden: 'You do not have permission to view the wall.',
      loadMore: 'Load more',
      pinned: 'Pinned',
      comment: 'Comment',
      commentPlaceholder: 'Write a comment…',
      commentSubmit: 'Send',
      moreComments: 'See more comments',
      reactLike: 'Like',
      reactCelebrate: 'Celebrate',
      reactSupport: 'Support',
      kindNormal: 'Post',
      kindRecognition: 'Recognition',
      kindCelebration: 'Celebration',
      kindEvent: 'Event',
      kindCommunication: 'Communication',
      asideTitle: 'Shortcuts',
      asideComms: 'Formal communications',
      asideCelebrations: 'Recognition',
      asidePortal: 'Employee portal',
      asideHint: 'The wall is social; Communications remains the editorial channel.',
    },
    chatPage: {
      title: 'Chat',
      lead: 'Internal conversations in your tenant — simple and secure.',
      forbidden: 'You do not have permission to view Chat.',
      empty: 'You have no conversations yet.',
      errorLoad: 'Could not load Chat.',
      searchPeople: 'Search people…',
      directLabel: 'Direct message',
      unnamed: 'Conversation',
      pickConversation: 'Pick a conversation or search for someone to start.',
      back: 'Back',
      composerPlaceholder: 'Write a message…',
      send: 'Send',
      edited: 'edited',
      flowIaSummarize: 'Summarize with Flow IA',
      flowIaSummarizePrompt:
        'Summarize this work conversation in 3-5 clear bullet points. Be brief and neutral. Return only the summary.',
      flowIaImprove: 'Improve message with Flow IA',
      flowIaImprovePrompt:
        'Improve this chat message draft. Be clear and professional. Return only the improved text.',
    },
    employees: {
      title: 'People',
      subtitle: 'Premium directory with managers, tenure, and quick links to core workflows.',
      summaryActive: 'Active',
      summaryOnboarding: 'Onboarding',
      summaryManagers: 'Leaders',
      summaryAreas: 'Areas',
      filtersPlaceholder: 'Filters by status and area — coming soon',
      search: 'Search by name, email, or area…',
      new: 'New employee',
      edit: 'Edit',
      delete: 'Delete',
      empty: 'No employees yet.',
      modalTitle: 'Employee',
      fieldFirstName: 'First name',
      fieldLastName: 'Last name',
      fieldEmail: 'Email',
      fieldPhone: 'Phone',
      fieldArea: 'Area',
      fieldDepartment: 'Department',
      fieldPosition: 'Role',
      fieldStatus: 'Status',
      fieldEmploymentType: 'Employment type',
      save: 'Save',
      cancel: 'Cancel',
      legajoHint: 'File ready',
      viewLegajo: 'View file',
      columns: {
        name: 'Name',
        email: 'Email',
        area: 'Area',
        manager: 'Manager',
        tenure: 'Start date',
        status: 'Status',
        position: 'Role',
        legajo: 'File',
        lifecycle: 'Lifecycle',
        progress: 'Doc progress',
      },
      viewLifecycle: 'Lifecycle',
      manager: 'Manager',
      tenure: 'Tenure',
      quickAttendance: 'Attendance',
      quickEval: 'Reviews',
      timelineTitle: 'Latest events',
      timelineHint: 'Illustrative timeline for the commercial preview.',
      timeline1: 'New hire recorded in Administration.',
      timeline2: 'Document uploaded to the digital file.',
      timeline3: 'Time off approved by People Ops.',
      timeline4: 'Performance review completed.',
      timeline5: 'Mandatory training assigned.',
      timeline6: 'Spot bonus recognition published.',
      viewProfilePremium: 'Premium profile',
      profilePremiumClose: 'Close profile',
      empProfileTitle: 'Employee profile',
      empProfileLead: 'Unified People Ops view (demo + directory data).',
      empProfileDemoNote: 'Some tiles mix real directory fields with illustrative metrics.',
      empProfilePersonal: 'Personal & contact',
      empProfileWork: 'Work details',
      empProfileStatus: 'Status',
      empProfileArea: 'Area',
      empProfileRole: 'Role / title',
      empProfileSeniority: 'Seniority',
      empProfileSeniorityDemo: 'Mid-level (demo)',
      empProfileStart: 'Start date',
      empProfileTenure: 'Tenure',
      empProfileManager: 'Manager',
      empProfileDocs: 'Documents',
      empProfileDocsHint: 'Open Records for status, expiry, and signature.',
      empProfileLeaves: 'Time off',
      empProfileLeavesHint: 'Open Time off for requests and approvals.',
      empProfileEval: 'Performance',
      empProfileEvalHint: 'Open Performance for cycles and feedback.',
      empProfileTrain: 'Training',
      empProfileTrainHint: 'Open Training for assignments and compliance.',
      empProfileComms: 'Communications',
      empProfileCommsHint: 'Internal announcements and reads (demo).',
      empProfileExpenses: 'Expenses',
      empProfileExpensesHint: 'Open Expenses for amounts and approvers.',
      empProfileTimeline: 'Employee timeline',
      empProfileNext: 'Suggested next actions',
      empProfileNext1: 'Confirm critical documentation is current.',
      empProfileNext2: 'Schedule a manager 1:1 in the next fortnight.',
      empProfileNext3: 'Review pending mandatory training.',
      tenureMonthShort: 'months (approx.)',
      tenureUnderOne: '< 1 mo (approx.)',
    },
    identityLink: {
      title: 'Spectra account',
      statusLabel: 'Link status',
      invite: 'Invite to Spectra',
      linkExisting: 'Link existing user',
      resend: 'Resend invitation',
      suspend: 'Suspend access',
      revoke: 'Revoke link',
      retrySync: 'Retry sync',
      confirmTitle: 'Confirm link',
      confirmLead: 'Enter the Flow user ID. The backend re-authorizes tenant and permissions.',
      flowUserId: 'Flow user ID',
      confirmLink: 'Confirm link',
      actionOk: 'Operation recorded.',
      actionError: 'Could not complete the operation.',
      status: {
        unlinked: 'No account',
        invited: 'Invitation pending',
        pending_claim: 'Confirmation pending',
        linked: 'Linked',
        suspended: 'Suspended',
        revoked: 'Revoked',
      },
    },
    reportsPage: {
      eyebrow: 'Intelligence & reporting',
      title: 'HR reports',
      lead: 'Executive report library for leadership, People Ops, and finance. Bulk export is on the roadmap.',
      note: 'Figures are illustrative. Real export will ship when the reporting engine is wired.',
      atAGlanceTitle: 'Executive snapshot (demo)',
      atAGlanceLead:
        'Illustrative numbers for leadership and People Ops. My Finances stays private and is not included in these cuts.',
      agHeadcount: 'Headcount by area (stable)',
      agAbsenteeism: 'Period absenteeism',
      agLeaves: 'Time off in flight',
      agPerf: 'Active performance reviews',
      agClimate: 'Employee climate (pulse)',
      agTraining: 'Training / compliance',
      agExpenses: 'Expense reports in pipeline',
      agDocs: 'Documents pending review',
      agComms: 'Internal communications',
      agExecSignals: 'Intelligence signals',
      rcLine: 'v0.3.5 — Executive catalog with Flow IA and responsive reading.',
      badgeRC: 'v0.3.5',
      execPackTitle: 'Executive pack for leadership',
      execPackLead: 'Four suggested reads for a leadership huddle.',
      preview: 'Preview',
      previewHint: 'Interactive preview will unlock with the reporting engine.',
      exportHint: 'Bulk export arrives in the next stage without rebuilding permissions.',
      badgeCatalog: 'Demo catalog',
      kpisTitle: 'Catalog snapshot',
      kpiAvailable: 'Available reports',
      kpiUpdated: 'Last refresh',
      kpiUpdatedValue: 'Today',
      kpiAreas: 'Areas covered',
      kpiRisks: 'Risks flagged',
      kpiRisksValue: 'Under control',
      catalogTitle: 'Reports ready for review',
      metaFreq: 'Frequency',
      metaAudience: 'Recommended audience',
      previewSoon: 'Preview',
      exportSoon: 'Export coming soon',
      footerHint: 'Need another cut? People Intelligence consolidates alerts while we finalize the reporting engine.',
      linkPi: 'Open People Intelligence',
      linkDash: 'Back to dashboard',
      privacyOrgTitle: 'Privacy and report scope',
      privacyOrgBody:
        'Company reports consolidate headcount, attendance, time off, climate, training, and approved expenses. My Finances stays private to each employee and is not included in these cuts.',
      execInsightsTitle: 'Executive insights (demo)',
      execInsight1: 'Stable headcount with slight concentration in Sales and Support.',
      execInsight2: 'Absenteeism within range; watch overlapping time off.',
      execInsight3: 'Stable climate with a post-release pulse opportunity.',
      execInsight4: 'Mandatory training gap closable within 30 days.',
      execInsight5: 'Clean expense pipeline toward Finance when real sync is on.',
      execInsight6: 'People Intelligence as a cross-read layer without exposing sensitive personal data.',
      freqWeekly: 'Weekly',
      freqBiweekly: 'Biweekly',
      freqMonthly: 'Monthly',
      freqQuarterly: 'Quarterly',
      freqSemiannual: 'Semiannual',
      audienceOps: 'Operations & team leads',
      audienceManagers: 'Managers & business lines',
      audienceCompliance: 'Compliance & People Ops',
      audienceFinance: 'Finance & spend control',
      audienceLeadership: 'Leadership & culture',
      audiencePeople: 'People Operations',
      audienceDirection: 'Executive committee',
      statusReady: 'Ready for preview',
      statusPrepared: 'In preparation',
      items: {
        attendance: {
          title: 'Attendance report',
          desc: 'Presence, remote work, late arrivals, and hours by area.',
        },
        leaves: {
          title: 'Time off report',
          desc: 'Requests, approvals, and projected overlaps.',
        },
        docs: {
          title: 'Document report',
          desc: 'Compliance, expirations, and files missing in storage.',
        },
        expenses: {
          title: 'Expense report',
          desc: 'Reports by status, amount, and finance sync.',
        },
        climate: {
          title: 'Climate report',
          desc: 'eNPS, participation, and aggregated qualitative signals.',
        },
        performance: {
          title: 'Performance report',
          desc: 'Cycles, calibrations, and competency gaps.',
        },
        training: {
          title: 'Training report',
          desc: 'Assignments, progress, and regulatory completion.',
        },
        monthly: {
          title: 'Monthly HR report',
          desc: 'Monthly slice of people, risks, documentation, and priority actions.',
        },
        culture: {
          title: 'Culture report',
          desc: 'Recognition signals and collaboration patterns across areas.',
        },
        audit: {
          title: 'Audit & permissions report',
          desc: 'Access matrix, sensitive changes, and traceability for compliance.',
        },
        executive: {
          title: 'Monthly executive pack',
          desc: 'Single leadership brief: people, risks, and actions.',
        },
      },
    },
    legajo: {
      heroTitle: 'Digital file & documents',
      heroSubtitle: 'Centralize labor documents, payslips, certificates, and document compliance.',
      suiteEyebrow: 'Document suite',
      metadataHint:
        'Real files live in external storage when Spaces or S3 is enabled. MongoDB keeps metadata and workflow states only.',
      secureStorageBanner:
        'Ready for Spaces/S3: the database does not store heavy files or base64; only metadata, states, and safe references.',
      kpiTotal: 'Total documents',
      kpiPending: 'Pending',
      kpiExpired: 'Expired',
      kpiApproved: 'Approved',
      kpiReceipts: 'Payslips available',
      kpiCompliance: 'Document compliance',
      kpiMissingFile: 'Required docs without file',
      tabsAria: 'File sections',
      tab_documentos: 'Documents',
      tab_recibos: 'Digital payslips',
      tab_vencimientos: 'Expirations',
      tab_compliance: 'Compliance',
      tab_storage: 'Storage',
      catalogTitle: 'Labor document types',
      catalogSubtitle: 'Spectra HR reference. Table status reflects loaded metadata.',
      catalog_dni: 'ID / identification',
      catalog_contract: 'Employment contract',
      catalog_labor: 'Payroll registration',
      catalog_bank: 'Bank details / CBU',
      catalog_cert_med: 'Medical certificates',
      catalog_cv: 'CV',
      catalog_policies: 'Signed policies',
      catalog_evaluation: 'Performance reviews',
      catalog_training: 'Training records',
      receiptColPeriod: 'Period',
      receiptColEmployee: 'Employee',
      receiptColArea: 'Area',
      receiptColStatus: 'Status',
      receiptColIssued: 'Issued',
      receiptColFile: 'File',
      receiptColActions: 'Actions',
      receiptFileMeta: 'Metadata only',
      receiptActionMeta: 'View metadata',
      receiptActionSign: 'Request signature',
      receiptActionAvailable: 'Mark available',
      receiptStatus_available: 'Available',
      receiptStatus_pending: 'Pending',
      receiptStatus_draft: 'Draft',
      receiptStatus_signed: 'Signed',
      receiptStatus_observed: 'Observed',
      receiptStatus_archived: 'Archived',
      receiptKpiAvailable: 'Available payslips',
      receiptKpiDraft: 'Drafts',
      receiptKpiSigned: 'Signed',
      receiptKpiObserved: 'Observed',
      receiptKpiLastPeriod: 'Latest period',
      receiptsStorageNote:
        'Payslips are stored as metadata and binary in object storage when DigitalOcean Spaces is active (RRHH_STORAGE_MODE=spaces).',
      receiptColGross: 'Gross',
      receiptColNet: 'Net',
      receiptActionAttach: 'Attach payslip',
      newPayslip: 'New payslip',
      payslipFieldMonth: 'Month (1–12)',
      payslipFieldYear: 'Year',
      payslipFieldGross: 'Gross (ARS)',
      payslipFieldNet: 'Net (ARS)',
      docUploadTitle: 'Attach file to document',
      receiptUploadTitle: 'Attach file to payslip',
      uploadHintMetadataOnly:
        'Real storage is off. We will record file metadata (name, size, MIME) only, not the binary.',
      uploadHintReady: 'You can attach the file. Size and MIME are validated before upload to Spaces.',
      uploadMaxLabel: 'Max size:',
      uploadTypesLabel: 'Allowed types:',
      downloadUnavailable: 'Download link not available in this mode.',
      docType_identification: 'ID / identification',
      docType_contract: 'Employment contract',
      docType_labor_registration: 'Payroll registration',
      docType_bank_account: 'Bank details / CBU',
      docType_medical_certificate: 'Medical certificate',
      docType_cv: 'CV',
      docType_policy: 'Signed policy',
      docType_payslip: 'Payslip',
      docType_training_certificate: 'Training certificate',
      docType_performance_review: 'Performance review',
      docType_other: 'Other',
      expiringTitle: 'Upcoming expirations',
      expColDoc: 'Document',
      expColEmployee: 'Employee',
      expColDue: 'Due date',
      expColUrgency: 'Urgency',
      expColNext: 'Next action',
      urgency_high: 'High',
      urgency_medium: 'Medium',
      urgency_low: 'Low',
      nextRenewMedical: 'Coordinate renewal with provider and HR.',
      nextUpdateId: 'Request an updated copy from the employee.',
      nextResignPolicies: 'Send policy pack for e-signature.',
      complianceGlobalPct: 'Global compliance',
      complianceCompleteEmployees: 'Employees complete',
      compliancePendingEmployees: 'Employees with gaps',
      complianceCriticalMissing: 'Missing critical documents',
      complianceMissingBinary: 'No file in storage',
      complianceTabIntro: 'Executive view computed from metadata and demo rules.',
      complianceBlockTitle: 'Document compliance',
      complianceBlockBody:
        'Indicators are computed from tenant metadata. Binary files will connect to S3/DigitalOcean Spaces.',
      filterEmployee: 'Employee',
      filterStatus: 'Status',
      filterType: 'Document type',
      filterRequired: 'Required',
      allRequired: 'All (required filter)',
      requiredYes: 'Required only',
      requiredNo: 'Not required',
      allEmployees: 'All employees',
      allStatuses: 'All statuses',
      allTypes: 'All types',
      newDocument: 'New document',
      editDocument: 'Edit document',
      emptyTitle: 'No documents for current filters',
      emptyDesc: 'Create metadata or adjust filters. Binary storage arrives with S3/Spaces.',
      colEmployee: 'Employee',
      colType: 'Type',
      colTitle: 'Title',
      colStatus: 'Status',
      colRequired: 'Required',
      colExpires: 'Expiry',
      colStorage: 'Storage',
      colFileState: 'File state',
      actionMeta: 'View metadata',
      actionAttach: 'Attach file',
      actionEdit: 'Edit',
      actionApprove: 'Approve',
      actionReject: 'Reject',
      actionPending: 'Mark pending',
      actionExpired: 'Mark expired',
      actionDownload: 'Get link',
      rejectPrompt: 'Rejection reason (optional)',
      rejectDefault: 'Rejected by HR',
      modalNote: 'Metadata only: no binary upload in this release.',
      modalNoteV23:
        'Create document metadata. For binaries use “Attach file”; with Spaces enabled files go to object storage.',
      fieldEmployee: 'Employee',
      fieldType: 'Document type',
      fieldTitle: 'Title',
      fieldDescription: 'Description',
      fieldRequired: 'Required document',
      fieldStatus: 'Status',
      fieldExpires: 'Expiry (optional)',
      fieldStorage: 'Storage provider',
      fieldUrl: 'External URL (optional)',
      fieldFileName: 'File name (optional)',
      metaTitle: 'Document metadata',
      closeMeta: 'Close',
      yes: 'Yes',
      no: 'No',
      status: {
        pending: 'Pending',
        uploaded: 'Uploaded',
        approved: 'Approved',
        rejected: 'Rejected',
        expired: 'Expired',
      },
      storage: {
        pending: 'Pending',
        s3: 'Amazon S3',
        digitalocean_spaces: 'DigitalOcean Spaces',
        external_url: 'External URL',
        metadata_only: 'Metadata only',
      },
      fileState: {
        pending: 'No file',
        metadata_only: 'Metadata only (no binary)',
        uploaded: 'File in Spaces',
        external: 'External URL',
        error: 'Storage error',
        spaces_ready: 'Ready for Spaces',
      },
      storageTabTitle: 'Document storage configuration',
      storageTabIntro:
        'MongoDB stores metadata and references. PDFs and images go to DigitalOcean Spaces (S3 compatible) when spaces mode is active and complete.',
      storageFieldMode: 'Mode',
      storageFieldStatus: 'Runtime status',
      storageFieldProvider: 'Configured provider',
      storageFieldMax: 'Max upload size',
      storageFieldMimes: 'Allowed MIME types',
      storageFieldBucket: 'Bucket configured',
      storageFieldCreds: 'Credentials configured',
      storageFutureNote: 'Next: signed URLs with audit, role permissions, and retention policies.',
      storageMode_metadata_only: 'Metadata only',
      storageMode_spaces: 'Spaces / S3',
      storageRuntime_metadata_only: 'Metadata only (no remote upload)',
      storageRuntime_ready: 'Ready to upload files',
      storageRuntime_missing_config: 'Missing configuration (DO_SPACES_* variables)',
      storageProvider_digitalocean_spaces: 'DigitalOcean Spaces',
    },
    orgChart: {
      title: 'Org chart',
      subtitle: 'By area and department.',
      manager: 'Lead',
      team: 'Team',
      empty: 'No org data.',
    },
    leaves: {
      title: 'Time off',
      subtitle: 'Requests, approvals, and a simple upcoming-absence view.',
      heroLead: 'Operational time off with KPIs, filters, and a coverage calendar strip.',
      suiteHeroEyebrow: 'Absence & coverage',
      suiteHeroTitle: 'Time off & leave',
      filterType: 'Leave type',
      filterStatus: 'Status',
      filterEmployee: 'Employee',
      filterPeriod: 'Period (month)',
      kpiUpcoming: 'Upcoming absences',
      kpiApprovedMonth: 'Approved this month',
      calTitle: 'Operational calendar (week)',
      calHint: 'Simplified coverage view; full calendar integration on the roadmap.',
      impactLow: 'Low operational impact',
      impactMed: 'Medium impact',
      impactHigh: 'High impact',
      summaryPending: 'Pending',
      summaryApproved: 'Approved',
      summaryRejected: 'Rejected',
      summaryDays: 'Days requested',
      summaryUpcoming: 'Upcoming absences',
      upcomingTitle: 'Upcoming absences (calendar strip)',
      upcomingEmpty: 'No approved or pending time off with a future start date.',
      new: 'New request',
      approve: 'Approve',
      reject: 'Reject',
      empty: 'No requests.',
      type: 'Type',
      dates: 'Dates',
      days: 'Days',
      status: 'Status',
      employee: 'Employee',
      pickEmployee: 'Employee',
      dateFrom: 'From',
      dateTo: 'To',
      reason: 'Reason',
      loading: 'Loading time off…',
      errorLoad: 'Could not load time off.',
    },
    expenses: {
      title: 'Expense reports',
      subtitle: 'Demo workflow with Finance placeholder.',
      financeBanner: 'Ready to sync with Spectra Finance.',
      financeFlowLong:
        'This flow is ready to sync approved expenses with Spectra Finance.',
      timelineTitle: 'Approval flow',
      timelineDraft: 'Draft',
      timelineSent: 'Submitted',
      timelineManager: 'Manager',
      timelineFinance: 'Finance',
      timelinePaid: 'Paid',
      metricsManager: 'Pending manager',
      metricsFinance: 'Pending finance',
      summaryPending: 'Pending',
      summaryApproved: 'Approved',
      summaryUnpaid: 'Unpaid',
      summaryTotal: 'Total submitted',
      new: 'New expense',
      syncFinance: 'Sync with Finance',
      financeSync: 'Finance sync',
      notSynced: 'Not synced',
      prepared: 'Prepared',
      pending: 'Pending',
      synced: 'Synced',
      error: 'Error',
      empty: 'No expense reports.',
      amount: 'Amount',
      category: 'Category',
      status: 'Status',
      submit: 'Submit',
      managerApprove: 'Manager approve',
      financeApprove: 'Finance approve',
      markPaid: 'Mark paid',
      reject: 'Reject',
      heroLead: 'Demo approval flow: amounts, categories, and statuses ready to connect to Spectra Finance.',
      suiteHeroEyebrow: 'Finance-ready',
      suiteHeroTitle: 'Expense reports',
      kpiPendingSync: 'Pending Finance sync',
      status_draft: 'Draft',
      status_submitted: 'Submitted',
      status_manager_approved: 'Manager approved',
      status_finance_approved: 'Finance approved',
      status_rejected: 'Rejected',
      status_paid: 'Paid',
      status_cancelled: 'Cancelled',
      financeReadyTitle: 'Ready for Spectra Finance',
      financeReady1: 'Approved expense ready to export',
      financeReady2: 'Finance review in unified inbox',
      financeReady3: 'Future sync via API',
      financeReady4: 'Reimbursement / payment recorded in Finance',
      kpiTotal: 'Total submitted',
      kpiPendingApproval: 'Pending approval',
      kpiApprovedFinance: 'Approved (finance)',
      kpiPaid: 'Paid',
      kpiRejected: 'Rejected',
      fieldEmployee: 'Employee',
      fieldCurrency: 'Currency',
      fieldDescription: 'Description',
      fieldExpenseDate: 'Expense date',
      amountCentsHint: 'Amount in cents (e.g. 10000 = ARS 100.00)',
      financeNextTitle: 'Future integration with Spectra Finance',
      financeNextLead:
        'Ready to sync approved reports: metadata, status, and finance_sync_status will align with Finance.',
      loading: 'Loading expense reports…',
      mobileCardsTitle: 'Mobile view',
      mobileCardsHint: 'Same data in compact cards for small screens.',
      nextActionsTitle: 'Suggested next actions',
      nextAction1: 'Clear manager-pending items before close.',
      nextAction2: 'Validate categories and amounts for Finance.',
      nextAction3: 'Mark paid once reimbursement is credited.',
    },
    personalFinance: {
      title: 'My finances',
      subtitle: 'Your private financial dashboard.',
      heroTitle: 'Personal financial wellbeing',
      heroLead:
        'Your movements are private. The company does not see your personal income, spending, or debt.',
      privacyHighlight:
        'Your movements are private. The company does not see your personal income, spending, or debt.',
      privacyCompany: 'This data is private to the employee and not visible to the company.',
      tipsTitle: 'Financial tips',
      tip1: 'Define a monthly budget and review it weekly.',
      tip2: 'Split fixed and variable costs to forecast the month.',
      tip3: 'Set a savings goal and automate a recurring transfer.',
      goalsTitle: 'Goals',
      goalPlaceholder: 'Savings goals and tracking will arrive in upcoming releases.',
      sectionBudgetTitle: 'Monthly budget',
      budgetUsed: 'Used',
      budgetAvailable: 'Available',
      budgetExceeded: 'Over budget',
      budgetDemoHint: 'Illustrative split based on your month movements.',
      goalsDetailTitle: 'Savings goals',
      goalEmergency: 'Emergency fund (6 months of expenses)',
      goalVacation: 'Vacation',
      goalEducation: 'Education',
      goalDebt: 'Debt payoff',
      recoBlockTitle: 'Financial wellbeing tips',
      reco1: 'Aim to save at least 10% of income consistently.',
      reco2: 'Review recurring subscriptions each quarter.',
      reco3: 'Prioritize emergency savings before higher-risk investments.',
      recurringIn: 'Recurring income',
      recurringOut: 'Recurring expenses',
      categorySplit: 'Spending by category',
      debt: 'Debt',
      roadmapTitle: 'Coming soon',
      roadmapBody:
        'Financial education, guided goals, and smart alerts to build healthier habits.',
      privacySection: 'Privacy',
      privacyTitle: 'Privacy',
      privacyBody:
        'This information is private to you. The company cannot see your personal transactions.',
      incomeMonth: 'Income (month)',
      expenseMonth: 'Spending (month)',
      balance: 'Estimated balance',
      savings: 'Estimated savings',
      healthScore: 'Financial health',
      transactions: 'Transactions',
      newIncome: 'New income',
      newExpense: 'New expense',
      fieldType: 'Type',
      fieldAmountCents: 'Amount (cents)',
      fieldCurrency: 'Currency',
      fieldCategory: 'Personal category',
      fieldDescription: 'Description',
      empty: 'No transactions.',
      loading: 'Loading your financial summary…',
      summaryTitle: 'Personal financial summary',
      summaryIntro: 'A clear view of health, balance, and monthly habits.',
      summaryHealth: 'Financial health',
      summaryBalance: 'Estimated balance',
      summaryCompare: 'Income vs spending',
      summarySavingsLine: 'Estimated savings',
      summaryNextTitle: 'Upcoming moves and recommendations',
      wellbeingTitle: 'Financial wellbeing',
      wellbeingSubtitle: 'Holistic score from income, spending, and savings (demo).',
      metricBalanceMonth: 'Month balance',
      metricIncomeApprox: 'Approx. income',
      metricExpenseApprox: 'Approx. spending',
      metricSavingsApprox: 'Estimated savings',
      metricDebtGap: 'Gap vs income',
      emptySummaryTitle: 'Nothing to show yet',
      emptySummaryDesc: 'Add movements to see trends and recommendations here.',
      barIncome: 'Income',
      barExpense: 'Spending',
      txType: {
        income: 'Income',
        expense: 'Expense',
      },
      pfTabSummary: 'Summary',
      pfTabBudget: 'Budget',
      pfTabMovements: 'Transactions',
      pfTabGoals: 'Savings goals',
      pfTabRecurring: 'Recurring',
      pfTabDebts: 'Debts / commitments',
      pfTabProjection: 'Projection',
      pfTabReco: 'Recommendations',
      pfDemoNote:
        'Extended budget and local adjustments are saved in your browser (demo). Sample goals and debts are not sent to company reports.',
      pfCreateBudget: 'Create budget',
      pfBudgetMonthlyTotal: 'Total monthly budget (ARS, indicative)',
      pfBudgetFieldArs: 'Monthly amount (ARS)',
      pfColCategory: 'Category',
      pfColBudgeted: 'Budgeted',
      pfColSpent: 'Spent',
      pfColAvailable: 'Available',
      pfColPct: '% used',
      pfColStatus: 'Status',
      pfColActions: 'Actions',
      pfStatusOk: 'OK',
      pfStatusWarn: 'Watch',
      pfStatusOver: 'Over',
      pf503020Title: '50 / 30 / 20 rule',
      pf503020Need: 'Needs (50%)',
      pf503020Want: 'Wants (30%)',
      pf503020Save: 'Savings / debt (20%)',
      pf503020Ideal: 'Target',
      pf503020Your: 'Your estimated split (demo)',
      pfCat_housing: 'Housing',
      pfCat_food: 'Food',
      pfCat_transport: 'Transport',
      pfCat_health: 'Health',
      pfCat_education: 'Education',
      pfCat_services: 'Utilities & services',
      pfCat_debts: 'Debt payments',
      pfCat_leisure: 'Leisure',
      pfCat_savings: 'Savings',
      pfCat_other: 'Other',
      pfBudgetModalTitle: 'Monthly budget (demo)',
      pfBudgetModalLead: 'Adjust amounts in Argentine pesos per category. Saved in your browser (demo); not on the server.',
      pfBudgetModalClose: 'Done',
      pfBudgetPersistHint: 'Budget amounts are stored only in your browser (demo).',
      pfBudgetToastSaved: 'Budget saved on this device',
      pfBudgetResetConfirm: 'Reset demo budget to suggested values and remove extra categories?',
      pfBudgetResetDone: 'Budget reset',
      pfBudgetResetBtn: 'Reset demo',
      pfBudgetAddCategory: 'Add category',
      pfBudgetExtraLabel: 'Category name',
      pfBudgetExtraCap: 'Monthly cap (ARS)',
      pfBudgetDeleteRow: 'Remove',
      pfBudgetEditCap: 'Edit cap',
      pfBudgetConfirmDeleteExtra: 'Remove this category from the budget?',
      pfBudgetEstIncome: 'Estimated monthly income (demo, local)',
      pfBudgetEstExpense: 'Estimated monthly spend (demo, local)',
      pfAlertsTitle: 'Indicative alerts',
      pfAlertHighSpend: 'Month spending is high vs estimated income.',
      pfAlertLowSave: 'Estimated savings are low: consider reviewing priorities.',
      pfAlertOverBudget: 'Spending exceeds income for the period (estimated deficit).',
      pfAlertDebtDue: 'A commitment has a due date soon (demo).',
      pfDistTitle: 'Simple spending split',
      pfDistNeeds: 'Needs',
      pfDistWants: 'Wants',
      pfDistSaveDebt: 'Savings / paydown',
      pfScoreTitle: 'Financial health score',
      pfPrivacyRepeat: 'This data is private to the employee and not visible to the company.',
      pfFilterAll: 'All',
      pfFilterIncome: 'Income only',
      pfFilterExpense: 'Expenses only',
      pfFilterCategory: 'Category',
      pfFilterMonth: 'Month',
      pfFilterMonthAll: 'All months',
      pfBtnLoadIncome: 'Add income',
      pfBtnLoadExpense: 'Add expense',
      pfTableDate: 'Date',
      pfTableType: 'Type',
      pfTableCategory: 'Category',
      pfTableAmount: 'Amount',
      pfGoalTarget: 'Target',
      pfGoalSaved: 'Saved',
      pfGoalPct: 'Progress',
      pfGoalDue: 'Target date',
      pfGoalMonthly: 'Suggested monthly contribution',
      pfGoalStatus: 'Status',
      pfGoalStatusOn: 'In progress',
      pfGoalStatusRisk: 'At risk',
      pfGoalStatusDone: 'Done',
      pfNewGoal: 'New goal',
      pfGoalInsight: 'To move toward this goal, an indicative contribution is {amount}/month.',
      pfRecurKpiIn: 'Fixed income',
      pfRecurKpiOut: 'Fixed expenses',
      pfRecurKpiMargin: 'Available margin',
      pfRecurKpiSubs: 'Active subscriptions (demo)',
      pfRecurAlertsTitle: 'Recurring alerts',
      pfRecurAlertHigh: 'Fixed costs are high vs fixed income (demo).',
      pfRecurAlertSub: 'Review low-use subscriptions.',
      pfRecurAlertDue: 'Upcoming commitment due date.',
      pfDebtStrategyTitle: 'Suggested strategy (indicative)',
      pfDebtAvalanche: 'Avalanche: prioritize higher interest or financing cost.',
      pfDebtSnowball: 'Snowball: close smaller debts first to free cash flow.',
      pfDebtHint:
        'If rates are high, prioritizing financing cost often makes sense; if you need cash flow quickly, smaller balances can help. Not personalized advice.',
      pfProjTitle: '3-month projection (demo)',
      pfProjBalance: 'Est. balance',
      pfProjSavings: 'Cumulative savings',
      pfProjCommit: 'Commitments',
      pfProjDeficitRisk: 'Deficit risk',
      pfProjScenarioCons: 'Conservative',
      pfProjScenarioBase: 'Base',
      pfProjScenarioOpt: 'Optimistic',
      pfProjSimSave: 'If I save {amount} more per month…',
      pfProjSimCut: 'If I cut spending by {pct}%…',
      pfRecoMbaTitle: 'Financial wellbeing recommendations',
      pfRecoMba1: 'Build an emergency fund gradually.',
      pfRecoMba2: 'Review recurring costs and subscriptions.',
      pfRecoMba3: 'Split the budget by category and review weekly.',
      pfRecoMba4: 'Automate a fixed transfer at the start of the month.',
      pfRecoMba5: 'Avoid expensive debt without an amortization plan.',
      pfRecoMba6: 'Prioritize financial education and tracking habits.',
      pfRecoMba7: 'Set one concrete, measurable monthly goal.',
      pfRecoFlowHint: 'Use Flow IA above for specific questions (avoid sensitive data in chat).',
      pfDisclaimerAdvice:
        'Recommendations and simulations are indicative and do not constitute professional financial or legal advice.',
      pfPrivacyModule:
        'My Finances is a private space. The company does not see your transactions, goals, or personal debts.',
      pfPrivacyAggregates: 'Future: aggregated anonymized reports for the organization, not individual data.',
      pfDemoGoalPurchase: 'Major purchase',
      pfDebtInstallment: 'Indicative installment',
      pfDebtRateLabel: 'Rate (demo)',
      pfDebtDueLabel: 'Due date',
      pfDebtProgressLabel: 'Paid progress',
      pfDebtPriorityLabel: 'Priority',
      pfDebtPri_high: 'High',
      pfDebtPri_medium: 'Medium',
      pfProjScenarioCol: 'Scenario',
      pfProjMonthN: 'Month {n}',
      pfRecur_salary: 'Salary',
      pfRecur_extra: 'Bonus / extras',
      pfRecur_freelance: 'Freelance / fees',
      pfRecur_rent: 'Rent / mortgage',
      pfRecur_utilities: 'Utilities (power, gas, internet)',
      pfRecur_subs: 'Digital subscriptions',
      pfRecur_loan: 'Loans / cards',
      pfRecur_insurance: 'Insurance',
    },
    awards: {
      title: 'Awards',
      subtitle: 'Recognition and culture.',
      heroTitle: 'Culture and recognition',
      heroLead: 'Celebrate visible wins aligned with strategy.',
      cultureLine:
        'Timely recognition strengthens culture, retention, and engagement.',
      categoriesTitle: 'Categories',
      catInnovation: 'Innovation',
      catAttitude: 'Attitude',
      catTeamwork: 'Teamwork',
      catPerformance: 'Performance',
      catLeadership: 'Leadership',
      employeeMonth: 'Employee of the month',
      recent: 'Recent recognition',
      new: 'New award',
      empty: 'No awards.',
      points: 'points',
    },
    pi: {
      title: 'People Intelligence',
      subtitle: 'Operational signals, document risk, and suggested next steps.',
      rcLine: 'v0.3.5 — People Intelligence, Executive Brief, and Flow IA with stronger contrast.',
      privacyFinances: 'Personal financial data stays private and is not cross-linked without explicit policy.',
      commercialHint: 'Ready to run with Spectra Flow, Finance, and Spaces once real connections are validated.',
      docRiskTitle: 'Document risk',
      docRiskIntro: 'Quick view of HR document expiries and pending items (demo).',
      nextActionTitle: 'Recommended next action',
      recOk: 'Document portfolio looks stable. Keep periodic reviews.',
      recBusy:
        'There are {expired} expired documents and {pending} pending. Prioritize employment contract, ID, and CBU proof before month-end.',
      metricsLine: '{total} documents · {approved} approved · compliance {compliance}%',
      roadmapNote:
        'Intelligence v1 is rules-based. Contextual Flow IA is available per screen; the next stage deepens with real tenant data and trained models.',
      rulesBanner:
        'Intelligence v1 is rules-based on tenant data (demo). No machine-learning model is running yet.',
      flowIaBanner:
        'Contextual Flow IA: ask for screen-specific guidance. Intelligence v1 remains rules-based; the next stage deepens with real tenant data.',
      execBriefTitle: 'Executive Brief · Organizational health',
      execBriefLead:
        'Consolidated executive view (demo) for leadership and People Ops. Area signals only; no personal My Finances data.',
      orgHealthLabel: 'Organizational health',
      orgHealthHint: 'Blend of compliance, Intelligence, and climate (demo weighting).',
      areaInsightsTitle: 'Area read (demo)',
      areaInsight1: 'Sales: time off and coverage focus before month-end.',
      areaInsight2: 'Support: morning shift late arrivals in focus.',
      areaInsight3: 'Product: higher perceived load in the latest survey.',
      areaInsight4: 'People: onboarding with banking documentation pending.',
      rotRiskTitle: 'Turnover risk (simulated)',
      rotRiskValue: 'Medium-low · stable signal',
      rotRiskHint: 'Indicative only; not an individual prediction.',
      burnoutTitle: 'Burnout risk (simulated)',
      burnoutValue: 'Medium in 2 areas',
      burnoutHint: 'Review release load and manager support.',
      trainingGapTitle: 'Training gaps',
      trainingGapBody: '12 people at 78% mandatory compliance — close the gap before Q2.',
      climateAlertTitle: 'Climate and participation alerts',
      climateAlertBody: 'Slight dip in last pulse participation; a close-the-loop comms helps.',
      piFlowIaCta: 'Analyze with Flow IA',
      piFlowIaCtaHint: 'Scroll to the assistant for questions with this screen’s context.',
      rulesTitle: 'People Intelligence v1 · rules-based',
      whatHrNowTitle: 'What HR should do now',
      whatHrNowIntro: 'Practical priorities from the current signals (demo):',
      whatHrNow1: 'Regularize critical documents (ID, contract, banking) before payroll close.',
      whatHrNow2: 'Approve time off that affects operational coverage.',
      whatHrNow3: 'Review absenteeism and recurring late arrivals by area.',
      whatHrNow4: 'Close pending performance reviews in the active cycle.',
      whatHrNow5: 'Run a climate or pulse survey to validate workload and leadership.',
      nextIaBanner: 'Next: contextual AI integrated with Spectra Flow and operational signals.',
      scoreLabel: 'Intelligence score',
      metric_documents_pending: 'Pending documentation',
      metric_documents_expired: 'Expired documentation',
      metric_pending_leaves: 'Pending time off',
      metric_pending_expense_reports: 'Expense reports in approval',
      metric_pending_expense_cents: 'Pending amount (cents)',
      metric_late_arrivals_week: 'Late arrivals (7 days)',
      metric_awards_count: 'Recognition events',
      metric_compliance_rate: 'Document compliance (%)',
      metric_documents_missing_file: 'Required documents without file',
      metric_payslips_available: 'Payslips available',
      metric_payslips_draft: 'Payslip drafts',
      metric_payslips_signed: 'Signed payslips',
      metric_payslips_observed: 'Observed payslips',
      riskLabel: 'Risk level',
      riskHigh: 'High',
      riskMedium: 'Medium',
      riskLow: 'Low',
      metricsTitle: 'Aggregated metrics',
      insightsTitle: 'Insights',
      insightsEmpty: 'No insights available.',
      recTitle: 'Recommendations',
      recEmpty: 'No extra recommendations.',
      lifecycleSectionTitle: 'Employee lifecycle',
      lifecycleInsight1: 'Blocked onboardings',
      lifecycleInsight2: 'Critical documentation pending',
      lifecycleInsight3: 'Offboarding risks',
      lifecycleInsight4: 'Overdue HR tasks',
      lifecycleRec1: 'Two onboardings have pending documentation. Prioritize contract and banking details before day one.',
      lifecycleRec2: 'Review offboardings with pending access or equipment to reduce operational and security risk.',
      lifecycleRec3: 'Assign overdue tasks to named owners and close the loop within the week.',
      lifecycleCta: 'Open Employee lifecycle',
      dimensionsTitle: 'Insights by dimension',
      dim_docs: 'Documentation: prioritize expiries and unsigned contracts.',
      dim_attendance: 'Attendance: late arrival spikes in Support and Sales.',
      dim_leaves: 'Time off: overlaps during release weeks.',
      dim_expenses: 'Expenses: reports ready for Finance with sync pending.',
      dim_perf: 'Performance: 12 reviews at manager stage.',
      dim_train: 'Training: mandatory compliance at 78%.',
      dim_climate: 'Climate: stable eNPS, medium-high perceived workload.',
      dim_culture: 'Culture: recognition aligned with collaboration.',
      dim_wellbeing: 'Financial wellbeing: average portal score.',
      dimWellbeingTitle: 'Financial wellbeing (employee)',
      dimWellbeingScoreDemo: 'Aggregated portal financial health (demo, no identities).',
      dimWellbeingRiskDemo:
        'Personal financial risk is only shown in aggregated anonymized form here; HR cannot access individual amounts or debts.',
      dimWellbeingPrivacy:
        'Private My Finances activity is not exposed to HR; any future reporting would be aggregated under explicit policy.',
      dim_comms: 'Comms & tasks: draft announcements, prepared emails, and reminders.',
      dim_governance:
        'HR governance: base roles are defined in the catalog; Spectra Flow/Auth sync is pending. Validate permissions before production clients and separate employee, manager, HR, and finance access.',
      commsSectionTitle: 'Communications & tasks',
      commsInsight1: 'There are announcements still in draft on the internal board.',
      commsInsight2: 'There are critical tasks overdue or due within 48 hours.',
      commsInsight3: 'Employees with pending documentation may need an operational email.',
      commsInsight4: 'Participation in the last climate survey is low.',
      commsRec1: 'Send a documentation reminder from HR emails (prepared; no real send yet).',
      commsRec2: 'Publish or schedule the weekly announcement under Communications.',
      commsRec3: 'Prioritize critical tasks in Notifications and assign an owner.',
      commsRec4: 'Prepare onboarding emails for new hires using the Welcome template.',
      risksBlockTitle: 'Detected risks',
      risk_doc: 'Document risk: outdated certificates and policies.',
      risk_ops: 'Operational risk: vacation coverage in Q1.',
      risk_culture: 'Cultural risk: low participation in last survey.',
      risk_finance: 'Financial risk: approved expenses not synced.',
      risk_perf: 'Performance risk: manager feedback pending >14 days.',
      risk_governance: 'Governance risk: permissions not yet synced with Spectra Flow/Auth — validate before go-live.',
      oppsTitle: 'Opportunities',
      opp_train: 'Leadership training plan for new managers.',
      opp_awards: 'Public recognition in Sales for quarterly close.',
      opp_wellbeing: 'Personal finance webinar and budgeting.',
      opp_onboard: 'Unified onboarding kit with digital checklist.',
      opp_climate: 'Light climate pulse after release to catch workload early.',
      opp_recognition: 'Public recognition aligned to collaboration values.',
      recActionsTitle: 'Actionable recommendations',
      recAction1: 'Send operational HR emails (templates ready; validate real mode).',
      recAction2: 'Close onboarding for new hires with the digital checklist.',
      recAction3: 'Review critical documents and expirations in Digital records.',
      recAction4: 'Approve or respond to time off that affects coverage.',
      recAction5: 'Review pending expenses and align with Finance when needed.',
      recAction6: 'Complete performance reviews at the manager stage.',
    },
    portal: {
      title: 'Employee portal',
      subtitleHeader: 'Self-service, records, and wellbeing in one place.',
      welcomePrefix: 'Hello,',
      subtitle: 'Your unified Spectra self-service hub.',
      quickActions: 'Quick actions',
      nextLeave: 'Next time off or absence',
      noLeave: 'No upcoming time off on file.',
      financeSummary: 'Personal finance snapshot',
      noFinance: 'No personal finance data yet.',
      recentDocs: 'Latest HR file documents',
      emptyDocs: 'No recent documents.',
      payslips: 'Payslips (metadata)',
      recentExpenses: 'Latest expense reports',
      emptyExpenses: 'No recent expenses.',
      awards: 'Recognition received',
      emptyAwards: 'No recent recognition.',
      heroRoleFallback: 'Team member',
      tileDocs: 'My documents',
      tileLeaves: 'My time off',
      tileExpenses: 'My expenses',
      tileFinance: 'My finances',
      tileAwards: 'Recognition',
      quickActionsHint: 'Shortcuts for what you use most in Spectra HR.',
      weekTitle: 'My week',
      weekSubtitle: 'Upcoming items and personal reminders.',
      weekLeave: 'Next absence',
      weekTraining: 'Assigned training',
      weekDoc: 'Pending document',
      weekExpense: 'Expense to fix',
      weekComms: 'Internal communication',
      weekNone: 'No highlighted items.',
      weekTrainingDemo: 'Spectra onboarding — compliance track (72% complete).',
      weekCommsDemo: 'Remote work policy 2026 — recommended read.',
      statusWorking: 'Work status',
      privacyFinance: 'Personal finances stay private: the company does not see transaction detail.',
    },
    attendance: {
      title: 'Attendance / Shifts',
      subtitle: 'Daily KPIs, records, and filters. Ready for time clocks or external integrations.',
      kpiPresent: 'Present today',
      kpiLate: 'Late arrivals',
      kpiAbsent: 'Absent',
      kpiWeekHours: 'Estimated week hours',
      kpiRemote: 'Remote today',
      weekSummaryTitle: 'Weekly summary (loaded data)',
      weekSummaryEmpty: 'No rows in the visible range. Adjust filters or add attendance.',
      weekSummaryHint: 'Hours and statuses are derived from the table below.',
      filterDate: 'Date',
      filterEmployee: 'Employee',
      filterStatus: 'Status',
      allEmployees: 'All',
      allStatuses: 'All',
      quickDemo: 'Add demo (late)',
      newRecord: 'New record',
      futureHint: 'Next: mobile check-in, geofencing, and Spectra Flow sync.',
      colEmployee: 'Employee',
      colDate: 'Date',
      colIn: 'Check-in',
      colOut: 'Check-out',
      colStatus: 'Status',
      colHours: 'Hours',
      colNotes: 'Notes',
      empty: 'No attendance rows yet. Create a manual entry or use the demo.',
      modalTitle: 'Register attendance',
      pickEmployee: 'Select…',
      delete: 'Delete',
      confirmDelete: 'Delete this record?',
      demoNote: 'Demo record — late arrival',
      kpiCompliance: 'Schedule compliance',
      insightsTitle: 'Attendance insights',
      insightLate: 'Recurring late arrivals in the morning shift (Support).',
      insightArea: 'Absences concentrated in Sales the week after a holiday.',
      insightHours: 'Incomplete shifts: 4 records missing check-out.',
      clockHint: 'Ready for biometric clocks or external attendance vendors.',
      status: {
        present: 'Present',
        late: 'Late',
        absent: 'Absent',
        remote: 'Remote',
        leave: 'Leave',
      },
    },
    communications: {
      title: 'HR communications',
      subtitle: 'Manage internal announcements, operational emails, templates, and message tracking.',
      modeBanner:
        'HR operational channel: simulation, prepared, or real send depending on configuration. Separate from commercial marketing email. Providers such as Resend may still be in demo mode.',
      tabsAria: 'Communication sections',
      tab_bulletins: 'Announcements',
      tab_emails: 'HR emails',
      tab_templates: 'Templates',
      tab_history: 'History',
      tab_audiences: 'Audiences',
      kpiPublished: 'Published announcements',
      kpiEmailsPrepared: 'Prepared emails',
      kpiTemplatesActive: 'Active templates',
      kpiPendingMsgs: 'Pending messages',
      kpiReadRate: 'Estimated read rate',
      kpiReadRateValue: '81%',
      kpiReadRateHint: 'Demo estimate (simulated opens)',
      premiumStripTitle: 'Channels & audiences',
      premiumStripLead:
        'Internal comms ready: portal, operational email, and templates (no bulk real send in this version).',
      premiumChannelPortal: 'Employee portal',
      premiumChannelEmail: 'HR email',
      premiumChannelBoard: 'Internal board',
      premiumAudienceAll: 'Whole organization',
      premiumAudienceManagers: 'Leadership',
      premiumAudienceNewHires: 'New hires',
      featuredTitle: 'Featured announcements',
      actionView: 'View',
      actionEdit: 'Edit',
      actionArchive: 'Archive',
      actionPublish: 'Publish',
      whereSeeBannerTitle: 'Where do I see my test announcement?',
      whereSeeBannerLead:
        'Publish under “Announcements”, find it in the feed below, and check the bell icon (Communications tab). Portal reads are simulated in this demo.',
      internalPublishBanner:
        'Internal publishing is active. Real email delivery still depends on your provider configuration on the server.',
      recipientEstPrefix: 'Estimated recipients (demo):',
      commNotFoundToast:
        'We opened Communications. We could not find the exact item, but you can check the history or the list.',
      commSelectedBanner: 'Selected announcement',
      actionDuplicate: 'Duplicate',
      timelineCreated: 'Created',
      timelineUpdated: 'Edited',
      timelinePublished: 'Published',
      timelineViewed: 'Viewed by employee (demo)',
      emailSoftWarning:
        'We could not complete the operation with the provider. Your action is reflected in the UI; try simulation mode or check server configuration.',
      tplNew: 'New template',
      tplEdit: 'Edit template',
      tplDuplicate: 'Duplicate',
      tplDelete: 'Delete',
      tplPreview: 'Preview',
      tplSave: 'Save',
      tplClose: 'Close',
      tplName: 'Display name',
      tplCategory: 'Template category',
      tplChannel: 'Channel',
      tplStatus: 'Status',
      tplVars: 'Variables',
      tplPreviewText: 'Preview text',
      tplConfirmDelete: 'Delete this template?',
      tplToastSaved: 'Template saved',
      tplCatWelcome: 'Welcome',
      tplCatReceipt: 'Payslip',
      tplCatLeave: 'Time off',
      tplCatTraining: 'Training',
      tplCatClimate: 'Climate',
      tplCatGeneral: 'General',
      tplCatInternal: 'Internal communication',
      viewModalTitle: 'Announcement detail',
      viewClose: 'Close',
      editModalTitle: 'Edit announcement',
      emailPanelTitle: 'HR emails',
      emailPanelLead: 'Prepare operational messages. Sending will connect in a later release.',
      emailPanelLeadResend:
        'Controlled sending from this screen: simulation logs without Resend; real mode uses Resend with secure environment variables.',
      emailRefresh: 'Refresh status',
      emailProviderState: 'Email engine',
      emailModeLabel: 'Mode',
      emailModeReal: 'Real (Resend)',
      emailModeSimulation: 'Simulation',
      emailConfigStatus_ready: 'Real sending enabled',
      emailConfigStatus_simulation: 'Simulation mode (no real send)',
      emailConfigStatus_missing_api_key: 'RESEND_API_KEY missing',
      emailConfigStatus_disabled: 'Disabled (RRHH_EMAIL_ENABLED=false)',
      emailSimBanner:
        'Real sending is off or in simulation. A simulated log will be stored when you click “Simulate send”.',
      emailDisabledBanner: 'RRHH_EMAIL_ENABLED is false: only drafts or simulations.',
      emailMissingKeyBanner: 'No Resend API key on the server: real send unavailable. Configure RESEND_API_KEY securely.',
      emailGenericBanner: 'Check server configuration to enable Resend.',
      emailRealBanner:
        'Warning: real mode is on. Each send delivers a real email. Not used for commercial marketing email.',
      emailComposeTitle: 'Send HR email',
      fieldToEmail: 'Recipient email',
      fieldToName: 'Recipient name',
      fieldHtmlBody: 'HTML body',
      fieldTextBody: 'Plain text body',
      emailPreviewTitle: 'Preview',
      emailReloadTemplate: 'Apply template',
      emailPrepareSave: 'Prepare (do not send)',
      emailSendReal: 'Send email',
      emailSendSimulated: 'Simulate send',
      emailRealAck: 'I confirm I want to send a real email to the recipient above.',
      emailRealAckMissing: 'Check the confirmation box to send a real email.',
      emailPrepareOk: 'Prepared entry saved to history.',
      emailSendOk: 'Operation completed.',
      emailSendErr: 'Operation failed. Check data and configuration.',
      emailRecentLogTitle: 'Recent log',
      emailColDate: 'Date',
      emailColProvider: 'Provider',
      emailColError: 'Error',
      emailLogEmpty: 'No records yet.',
      emailProvider: {
        resend: 'Resend',
        simulation: 'Simulation',
      },
      emailLogStatus: {
        draft: 'Draft',
        prepared: 'Prepared',
        simulated: 'Simulated',
        sent: 'Sent',
        failed: 'Failed',
      },
      emailPrepare: 'Prepare email',
      emailDisclaimerTitle: 'Important',
      emailDisclaimer:
        'This module is prepared for real delivery via Resend / Spectra Email Engine in a future stage. This version does not send real emails.',
      emailColTo: 'Recipient',
      emailColStatus: 'Status',
      emailColUpdated: 'Updated',
      emailModalTitle: 'Prepare HR email',
      emailModalNote: 'No real email is sent; in demo it is only added to the table as prepared for this session.',
      emailSavePrepared: 'Save as prepared',
      emailUntitled: '(no subject)',
      fieldRecipient: 'Recipient',
      recipient_employee: 'Employee',
      recipient_area: 'Area',
      recipient_all: 'Everyone',
      recipient_area_demo: 'Area (sample)',
      recipient_employee_demo: 'Employee (sample)',
      fieldSubject: 'Subject',
      fieldEmailType: 'Email type',
      fieldTemplate: 'Template',
      fieldMessage: 'Message',
      fieldPriority: 'Priority',
      priority_low: 'Low',
      priority_normal: 'Medium',
      priority_high: 'High',
      emailType: {
        welcome: 'Welcome',
        docs_pending: 'Pending documentation',
        doc_expired: 'Expired document',
        leave_approved: 'Leave approved',
        leave_rejected: 'Leave rejected',
        expense_approved: 'Expense approved',
        expense_rejected: 'Expense rejected',
        eval_pending: 'Pending review',
        training_assigned: 'Training assigned',
        climate_survey: 'Climate survey',
        general_comms: 'General announcement',
      },
      emailStatus: {
        draft: 'Draft',
        prepared: 'Prepared',
        queued: 'Queued',
        sent_sim: 'Sent (simulated)',
        failed_sim: 'Failed (simulated)',
      },
      templateChannel: 'Channel',
      templateStatus: 'Status',
      templateVars: 'Available variables',
      templateEdited: 'Last edited',
      tplActive: 'Active',
      tplDraft: 'Draft',
      chEmail: 'Email',
      chPortal: 'Portal',
      chInternal: 'Internal',
      template: {
        tpl_welcome: 'Employee welcome',
        tpl_docs_pending: 'Pending documentation',
        tpl_doc_expired: 'Expired document',
        tpl_leave_ok: 'Leave approved',
        tpl_leave_rej: 'Leave rejected',
        tpl_exp_ok: 'Expense report approved',
        tpl_eval_pending: 'Pending performance review',
        tpl_training: 'Training assigned',
        tpl_climate_survey: 'Climate survey available',
        tpl_urgent_comms: 'Urgent announcement',
      },
      templatePreview: {
        tpl_welcome: 'Hello {{employee_name}}, welcome to {{company_name}}…',
        tpl_docs_pending: 'You have pending documentation: {{document_name}} before {{due_date}}.',
        tpl_doc_expired: 'The document {{document_name}} is expired; please contact HR.',
        tpl_leave_ok: 'Your leave for {{leave_dates}} has been approved.',
        tpl_leave_rej: 'Your leave request could not be approved at this time.',
        tpl_exp_ok: 'Your expense report was approved. Thank you.',
        tpl_eval_pending: '{{employee_name}}, you have a pending review with {{manager_name}}.',
        tpl_training: 'You were assigned {{course_name}}. Open the portal to continue.',
        tpl_climate_survey: 'Please take the survey: {{survey_link}}',
        tpl_urgent_comms: 'Urgent HR message for {{employee_name}}.',
      },
      historyTitle: 'Message history',
      historyLeadResend:
        'HR email records (simulated or sent via Resend). Data is stored in MongoDB when available.',
      historyLead:
        'Demo audit view. “Simulated” or “prepared” states do not imply real email delivery from this version.',
      historyTo: 'Recipient',
      historyChannel: 'Channel',
      historyActor: 'Owner',
      histTypeEmail: 'Email',
      histTypePortal: 'Portal',
      channelEmail: 'Email',
      channelPortal: 'Portal',
      channelInternal: 'Internal',
      histPrepared: 'Prepared',
      histQueued: 'Queued',
      histSentSim: 'Sent (simulated)',
      histReadSim: 'Read (simulated)',
      histFailedSim: 'Failed (simulated)',
      histResultOk: 'Simulated result: delivered',
      histResultRead: 'Simulated result: read',
      histResultPending: 'Pending future send engine',
      histResultQueue: 'Queued (future engine)',
      histResultFail: 'Simulated failure (review)',
      audiencesIntro: 'Suggested audiences to segment communications responsibly.',
      audienceCountSuffix: 'people approx.',
      audienceChannels: 'Suggested channels',
      audienceName: {
        aud_all: 'All employees',
        aud_managers: 'Managers',
        aud_new_hires: 'New hires',
        aud_docs_pending: 'Employees with pending documentation',
        aud_leaves_upcoming: 'Employees with upcoming leave',
        aud_eval_pending: 'Employees with pending reviews',
        aud_area_tech: 'Technology area',
        aud_area_sales: 'Sales area',
        aud_area_admin: 'Administration area',
        aud_area_people: 'People area',
      },
      audUseAll: 'Institutional announcements, climate, benefits.',
      audChAll: 'Portal, email, internal board',
      audUseMgr: 'Guidelines, approvals, team performance.',
      audChMgr: 'Email, portal, meetings',
      audUseNew: 'Onboarding, access, welcome.',
      audChNew: 'Email, portal, tasks',
      audUseDocs: 'Compliance and signature reminders.',
      audChDocs: 'Email, portal',
      audUseLeave: 'Coverage and calendar coordination.',
      audChLeave: 'Email, portal',
      audUseEval: 'Cycle closure and feedback.',
      audChEval: 'Portal, email',
      audUseTech: 'Technical or infrastructure updates.',
      audUseSales: 'Commercial communications or targets.',
      audUseAdmin: 'Administrative and back-office processes.',
      audUsePeople: 'Internal People policies.',
      audChArea: 'Email, portal, internal',
      audChPeople: 'Internal, email',
      audActive: 'Active',
      variablesBlockTitle: 'Available variables',
      variablesBlockBody:
        'Templates can include placeholders that the future engine will replace with real employee data.',
      variablesList:
        'Employee name|Manager name|Document name|Leave date range|Company name|Due date|Course name|Survey link',
      filterCategory: 'Category',
      filterStatus: 'Status',
      all: 'All',
      new: 'New announcement',
      empty: 'No announcements yet. Create the first one for your team.',
      publish: 'Publish',
      archive: 'Archive',
      unarchive: 'Back to draft',
      delete: 'Delete',
      confirmDelete: 'Delete this announcement?',
      modalTitle: 'New announcement',
      fieldTitle: 'Title',
      fieldCategory: 'Category',
      fieldAudience: 'Audience',
      fieldStatus: 'Initial status',
      fieldSummary: 'Summary',
      fieldBody: 'Body',
      fieldAuthor: 'Author',
      publishToFeed: 'Also publish to Wall',
      publishToFeedHint: 'Only applies when status is Published.',
      notPublished: 'Not published',
      category: {
        general: 'General',
        rrhh: 'HR',
        compliance: 'Compliance',
        benefits: 'Benefits',
        urgent: 'Urgent',
      },
      audience: { all: 'Everyone', managers: 'Managers', employees: 'Employees' },
      status: { draft: 'Draft', published: 'Published', archived: 'Archived' },
      feedTitle: 'Internal feed',
    },
    permissions: {
      sectionTitle: 'HR roles & permissions',
      matrixTitle: 'Permission matrix by module',
      matrixLegend: '✓ = allowed for simulated role · — = no · Read-only demo.',
      matrixModuleCol: 'Module / area',
      matrixReadOnly: 'Matrix reflects the role selected above. Production will sync with Spectra Flow/Auth.',
      mockRoleLabel: 'Simulated role (demo)',
      internalCode: 'Internal code',
      source_local_demo: 'Source: local demo',
      source_flow_future: 'Source: Flow (future)',
      profilesTitle: 'Recommended profiles',
      profileFor: 'Audience',
      profileModules: 'Typical modules',
      profileRestrictions: 'Restrictions',
      profileRisk: 'Risk / note',
      flowSectionTitle: 'Future integration with Spectra Flow/Auth Center',
      flowIntro: 'Spectra Flow will centralize identity, tenants and module enablement. RRHH applies fine-grained permissions locally.',
      flowBullet1: 'Flow will be the central source of users and sessions.',
      flowBullet2: 'Flow will provide tenant_id and multi-tenant context.',
      flowBullet3: 'Flow will validate that the HR module is enabled for the tenant.',
      flowBullet4: 'Flow will provide global role and per-module roles.',
      flowBullet5: 'RRHH applies permissions locally (module_role / module_permissions).',
      flowBullet6: 'RRHH is not an isolated owner of users: identity is delegated to Flow.',
      flowBullet7: 'Email and Storage may remain in demo mode; no assumption of production wiring.',
      flowNoSso: 'Real SSO and token validation against Flow/Auth are not enabled in this release.',
      flowChainAria: 'Access governance chain',
      flowStepLogin: 'Flow login',
      flowStepValidate: 'Validate module access',
      flowStepTenant: 'Tenant context',
      flowStepRole: 'HR role',
      flowStepPerms: 'Module permissions',
      flowStepGuards: 'UI/API guards',
      requirePermBadge: 'Permission required',
      action_view: 'View',
      action_create: 'Create',
      action_edit: 'Edit',
      action_approve: 'Approve',
      action_reject: 'Reject',
      action_delete: 'Delete',
      action_export: 'Export',
      action_admin: 'Administer',
      module_dashboard: 'Dashboard',
      module_employee_portal: 'Employee portal',
      module_employees: 'Employees',
      module_lifecycle: 'Employee lifecycle',
      module_org_chart: 'Org chart',
      module_legajo: 'Digital file',
      module_payslips: 'Payslips',
      module_attendance: 'Attendance',
      module_leaves: 'Time off',
      module_expenses: 'Expense reports',
      module_personal_finance: 'My finances',
      module_communications: 'Communications',
      module_rrhh_emails: 'HR emails',
      module_evaluations: 'Performance reviews',
      module_training: 'Training',
      module_surveys: 'Climate & surveys',
      module_awards: 'Awards / recognition',
      module_feed: 'Social wall',
      module_chat: 'Internal chat',
      module_flow_ia: 'Flow IA',
      module_people_intelligence: 'People Intelligence',
      module_notifications: 'Notifications & tasks',
      module_settings: 'Settings',
      roleTitle_rrhh_admin: 'HR administrator',
      roleDesc_rrhh_admin: 'Full access to the HR module.',
      roleTitle_rrhh_manager: 'HR manager',
      roleDesc_rrhh_manager: 'Manages employees, time off, documents, reviews and communications.',
      roleTitle_rrhh_operator: 'HR operator',
      roleDesc_rrhh_operator: 'Loads and updates operational information.',
      roleTitle_employee_self_service: 'Employee portal',
      roleDesc_employee_self_service: 'Limited portal access: own documents, time off, expenses and personal finance.',
      roleTitle_finance_reviewer: 'Finance reviewer',
      roleDesc_finance_reviewer: 'Reviews expense reports and states linkable to Finance.',
      roleTitle_viewer: 'Viewer',
      roleDesc_viewer: 'Read-only where allowed.',
      profile_hr_team_title: 'Full HR team',
      profile_hr_team_for: 'Administrators and People Operations leads.',
      profile_hr_team_modules: 'All operational modules and limited configuration.',
      profile_hr_team_restrictions: 'Must follow corporate policies and segregation of duties.',
      profile_hr_team_risk: 'High impact: audit bulk changes and exports.',
      profile_area_manager_title: 'Area manager',
      profile_area_manager_for: 'Leaders with direct reports.',
      profile_area_manager_modules: 'Employees, time off, attendance, reviews, communications.',
      profile_area_manager_restrictions: 'No global settings or others’ financial data.',
      profile_area_manager_risk: 'Medium: review time-off approvals and sensitive data.',
      profile_self_service_title: 'Self-service employee',
      profile_self_service_for: 'Each employee in the portal.',
      profile_self_service_modules: 'Portal, own file, time off, expenses, personal finance.',
      profile_self_service_restrictions: 'No third-party data or executive dashboards.',
      profile_self_service_risk: 'Low if the portal is scoped correctly.',
      profile_finance_title: 'Finance / reimbursements',
      profile_finance_for: 'Finance team and expense approvers.',
      profile_finance_modules: 'Expense reports, communications read, exports.',
      profile_finance_restrictions: 'No employee hire/termination or full file unless needed.',
      profile_finance_risk: 'Medium: validate approval limits and traceability.',
      profile_exec_read_title: 'Leadership / read-only',
      profile_exec_read_for: 'C-level and staff needing visibility.',
      profile_exec_read_modules: 'Dashboard, People Intelligence, reports and selective reads.',
      profile_exec_read_restrictions: 'No destructive actions or operational approvals.',
      profile_exec_read_risk: 'Low; watch bulk exports.',
      profile_audit_title: 'Audit / compliance',
      profile_audit_for: 'Internal or external auditors.',
      profile_audit_modules: 'Broad read, exports, communication and file traces.',
      profile_audit_restrictions: 'No master-data edits outside formal flows.',
      profile_audit_risk: 'Control confidentiality and retention of exports.',
      permDeniedBadge: 'Permission required',
    },
    notifications: {
      title: 'Notifications & HR tasks',
      subtitle: 'Prioritize operational alerts and tasks with clear owners (demo view).',
      subTabsAria: 'Notifications or tasks',
      sub_notifications: 'Notifications',
      sub_tasks: 'Tasks',
      kpiPending: 'Pending',
      kpiOverdue: 'Overdue',
      kpiCritical: 'Critical',
      kpiDone: 'Completed',
      kpiWeek: 'Next 7 days',
      listTitle: 'Notifications',
      tasksTitle: 'HR tasks',
      due: 'Due',
      priority_high: 'High',
      priority_medium: 'Medium',
      priority_low: 'Low',
      status_pending: 'Pending',
      status_in_progress: 'In progress',
      status_completed: 'Completed',
      status_overdue: 'Overdue',
      actionComplete: 'Mark complete',
      actionSnooze: 'Snooze',
      actionOrigin: 'View source',
      actionAssign: 'Assign owner',
      actionAssignHint: 'Demo: assignment on roadmap',
      notifItemDocExpired: 'Expired document — urgent review',
      notifItemLeavePending: 'Leave pending approval',
      notifItemExpensePending: 'Expense pending approval',
      notifItemEvalPending: 'Pending performance review',
      notifItemTrainingOverdue: 'Overdue training',
      notifItemSurveyLow: 'Survey with low participation',
      notifItemOnboard: 'Incomplete onboarding',
      notifItemOffboard: 'Blocked offboarding',
      notifItemFlowPrep: 'Prepare Spectra Flow/Auth validation before go-live',
      taskDemo1: 'Review expired documents for Ana Martínez',
      taskDemo2: 'Approve leave for Martín Gómez',
      taskDemo3: 'Complete onboarding for Sofía Herrera',
      taskDemo4: 'Review pending expense for Diego Rojas',
      taskDemo5: 'Publish monthly climate survey',
      taskDemo6: 'Close Q2 performance cycle',
      taskDemo7: 'Assign mandatory training',
      taskPerm1: 'Review HR permission matrix',
      taskPerm2: 'Define owners per module',
      taskPerm3: 'Validate employee portal access',
      taskPerm4: 'Review Finance / reimbursement permissions',
      taskPerm5: 'Prepare Flow/Auth integration',
      todayTitle: 'What HR should close today',
      todayIntro: 'Five prioritized actions from operational signals (demo):',
      today1: 'Unblock offboarding with pending access and equipment.',
      today2: 'Send documentation reminders to employees at risk.',
      today3: 'Publish or schedule the weekly People announcement.',
      today4: 'Resolve critical overdue tasks for leave and expenses.',
      today5: 'Prepare onboarding emails for this week’s new hires.',
      colTask: 'Task',
      colOwner: 'Owner',
      colModule: 'Source module',
      colDue: 'Due date',
      colPriority: 'Priority',
      colStatus: 'Status',
      emptySection: 'Nothing in this bucket.',
      rcHint: 'Demo view: we group alerts like a production control center.',
      secCritical: 'Critical alerts',
      secOverdue: 'Overdue',
      secUpcoming: 'Upcoming (7 days)',
      secIntegration: 'Integration follow-ups',
      secOther: 'Other operational alerts',
      tasksSecCritical: 'Critical tasks',
      tasksSecOverdue: 'Overdue tasks',
      tasksSecUpcoming: 'Upcoming tasks (7 days)',
      tasksSecIntegration: 'Integration tasks (Flow/Auth & permissions)',
      tasksSecOther: 'Operational backlog',
      tasksSecOtherHint: 'Operational work outside the immediate critical window.',
      preClientTitle: 'Before turning on a real customer',
      preClientLead: 'Responsible go-live checklist (no automation in this build):',
      preClient1: 'Validate HR email domains and senders.',
      preClient2: 'Validate document storage bucket and policies.',
      preClient3: 'Exercise login and roles with Spectra Flow/Auth.',
      preClient4: 'Review permission matrix and segregation of duties.',
      preClient5: 'Load real master data with a migration plan.',
      preClient6: 'Test mobile and tablet with real scenarios.',
      preClient7: 'Define backup, rollback, and on-call owners.',
    },
    settings: {
      title: 'Settings',
      subtitle: 'Module preferences and integrations.',
      heroEyebrow: 'Administration',
      heroTitle: 'Tenant configuration',
      heroLead:
        'Manage language, visible modules, roles, integrations, and document storage in a tabbed layout with room to breathe.',
      tabsAria: 'Configuration sections',
      tab_general: 'General',
      tab_modules: 'Visible modules',
      tab_roles: 'Roles & permissions',
      tab_integrations: 'Integrations',
      tab_storage: 'Document storage',
      tab_security: 'Security & Flow/Auth',
      tab_lifecycle: 'Employee lifecycle',
      tab_comms: 'Communications & email',
      tab_release: 'Release checklist',
      integrationFlowCenterTitle: 'Spectra Flow/Auth Center',
      commsEmailTitle: 'HR email & communications',
      commsEmailLead:
        'HR uses operational and transactional emails for employees. This is separate from commercial email marketing.',
      commsLiveTitle: 'Live server status',
      commsProviderLabel: 'Provider',
      commsStatusRuntime: 'Status',
      commsModeRuntime: 'Mode',
      commsFromRuntime: 'Configured sender',
      commsReplyRuntime: 'Reply-to',
      commsReplyYes: 'Configured',
      commsReplyNo: 'Not configured',
      commsStateLabel: 'Current state',
      commsStateValue: 'Prepared — no real outbound email in this version.',
      commsEngineLabel: 'Future engine',
      commsEngineValue: 'Resend or Spectra Email Engine (future activation).',
      commsFromLabel: 'HR sender',
      commsFromValue: 'Configurable later (verified domain).',
      commsReplyLabel: 'Reply-to',
      commsReplyValue: 'Dedicated HR queue — future configuration.',
      commsLogsLabel: 'Send logs',
      commsLogsValue: 'Audit trail when the engine is connected.',
      commsMarketingSepTitle: 'Separation from marketing',
      commsMarketingSepBody:
        'Commercial marketing campaigns do not share sender or lists with HR transactional communications.',
      sectionGeneral: 'General',
      sectionLanguage: 'Language',
      sectionCurrency: 'Currency',
      pfWellbeingTitle: 'My Finances / financial wellbeing',
      pfWellbeingStatus: 'Module status',
      pfWellbeingStatusActive: 'Active for employees',
      pfWellbeingPrivacy1: 'Privacy: personal employee data; the company cannot access individual transactions.',
      pfWellbeingPrivacy2: 'Goals, debts, and demo budgets stay in the employee private space.',
      pfWellbeingFuture1: 'Future: aggregated anonymized reporting for the organization (no personal data).',
      pfWellbeingFuture2: 'Future: financial education, benefits, and healthy nudges.',
      sectionModules: 'Visible modules',
      sectionIntegrations: 'Integrations',
      sectionFlow: 'Flow/Auth integration',
      sectionFinance: 'Finance integration',
      flowIaCardTitle: 'Flow IA',
      flowIaCardIntro:
        'Flow IA delivers contextual recommendations per screen. When real AI is off, the product uses rule-based guidance. API keys are configured on the server only.',
      flowIaStatusLabel: 'Current status',
      flowIaModeActive: 'Active (real AI)',
      flowIaModeFallback: 'Fallback (rules)',
      flowIaModePendingKey: 'Pending API key',
      flowIaModeDisabled: 'Disabled (rules only)',
      flowIaProviderLabel: 'Configured provider',
      flowIaModelLabel: 'Model',
      flowIaModuleVersionLabel: 'Module version',
      flowIaLoading: 'Loading status…',
      flowIaErr: 'Could not read Flow IA status.',
      sectionSecurity: 'Security',
      tenantMode: 'Tenant mode',
      tenantPrepared: 'Ready for multi-tenant with Flow/Auth.',
      storagePending: 'Spaces / S3: pending on roadmap.',
      storageFuture: 'Future storage (S3 / Spaces)',
      storageHint: 'Documents and attachments on the enterprise roadmap.',
      defaultLang: 'Default language',
      defaultCurrency: 'Default currency',
      visibleModules: 'Visible modules',
      flowAuth: 'Flow/Auth integration',
      finance: 'Finance integration',
      flowPrepared: 'Prepared (future validation against Auth Center).',
      financePrepared: 'Prepared for expense sync.',
      architectureNote:
        'Spectra HR is ready to inherit tenant_id, roles, and permissions from Spectra Flow/Auth Center.',
      sectionRoles: 'HR roles & permissions',
      rolesIntro: 'Demonstration matrix of capabilities by role. Not a substitute for real security policy.',
      rolesSyncNote: 'In production, these permissions will sync with Spectra Flow/Auth Center.',
      sectionFutureIntegrations: 'Future integrations',
      integFlowLabel: 'Spectra Flow / Auth',
      integFinanceLabel: 'Spectra Finance',
      integStorageLabel: 'Object storage (S3 / Spaces)',
      integClockLabel: 'External time clock / attendance',
      integSignLabel: 'Digital signatures / documents',
      integStatusPending: 'Pending',
      integFlowDesc:
        'Corporate identity, SSO, and permissions centralized in Spectra Flow / Auth Center (future activation).',
      integFinanceDesc: 'Sync approved expense reports and statuses with Spectra Finance.',
      integStorageDesc: 'Files in DigitalOcean Spaces or Amazon S3; database holds metadata and keys only.',
      integClockDesc: 'Punches from physical clocks or external attendance vendors.',
      integSignDesc: 'E-signature flows and document custody for contracts and addenda.',
      integrationsFlagPrepared: 'Prepared (demo)',
      integrationsFlagOff: 'Flag off',
      integrationFlowCenterIntro:
        'Future governance path: identity in Flow, permissions enforced in HR. No real SSO in this build.',
      integrationFlowStep1: 'The user signs in to Spectra Flow.',
      integrationFlowStep2: 'Flow validates the tenant.',
      integrationFlowStep3: 'Flow checks that the HR module is enabled.',
      integrationFlowStep4: 'Flow returns the global role.',
      integrationFlowStep5: 'Flow returns module roles.',
      integrationFlowStep6: 'HR applies local permissions and guards.',
      integrationFlowStep7: 'HR renders tenant data under active policies.',
      integrationFlowStatusTitle: 'Readiness signals',
      integrationFlowNoSso:
        'SSO, tokens, and live calls to Auth Center are not active. This view documents the expected contract.',
      flowRcStatusConceptual: 'Conceptually prepared',
      flowRcStatusEndpoint: 'Future endpoint',
      flowRcStatusTenant: 'tenant_id ready',
      flowRcStatusModuleRoles: 'Module roles ready',
      flowRcStatusGranular: 'Granular permissions ready',
      flowRcStatusPendingReal: 'Pending real connection',
      releaseModuleStateTitle: 'Module status',
      releaseModuleStateLead: 'Commercial demo summary. No secrets or API keys are shown.',
      releaseRowVersion: 'Version',
      releaseRowState: 'Status',
      releaseRowStateValue: 'Final polish / demo-ready',
      releaseRowMode: 'Mode',
      releaseRowModeValue: 'Demo / controlled production',
      releaseRowEmail: 'HR email',
      releaseRowStorage: 'Storage',
      releaseRowFlow: 'Flow/Auth',
      releaseRowFinance: 'Finance',
      releaseEmailDemo: 'Demo configuration / pending real validation',
      releaseEmailReal: 'Real channel detected — validate policies before go-live',
      releaseStorageMeta: 'Metadata-only / demo',
      releaseStorageSpaces: 'Spaces ready in configuration — validate production bucket',
      releaseStoragePending: 'Pending bucket and credential validation',
      releaseFlowPrepared: 'Prepared / pending real connection',
      releaseFinancePrepared: 'Prepared / pending real sync',
      releaseChecklistTitle: 'Release checklist',
      releaseCheckEmailTitle: 'HR email',
      releaseCheckEmailPending: 'Pending: validate real credentials before go-live.',
      releaseCheckStorageTitle: 'Document storage',
      releaseCheckStoragePending: 'Pending: validate production bucket and retention.',
      releaseCheckFlowTitle: 'Flow/Auth',
      releaseCheckFlowCurrent: 'Current state: conceptually prepared.',
      releaseCheckFlowPending: 'Pending: real connection and token validation.',
      releaseCheckFinanceTitle: 'Finance',
      releaseCheckFinanceCurrent: 'Current state: prepared for expense sync.',
      releaseCheckFinancePending: 'Pending: real sync of approved spend.',
      releaseCheckMobileTitle: 'Mobile',
      releaseCheckMobileCurrent: 'Status: visual QA in progress for RC.',
      releaseCheckMobilePending: 'Pending: final QA on real devices.',
      releaseCheckPermTitle: 'Permissions',
      releaseCheckPermCurrent: 'Status: matrix defined in catalog.',
      releaseCheckPermPending: 'Pending: connect to Auth Center in production.',
      connectorsSectionTitle: 'Connectors & integrations',
      connectorEmailTitle: 'HR email / Resend',
      connectorEmailDesc: 'Demo or real channel depending on environment variables. Separate from commercial marketing email.',
      connectorEmailUsage: 'Usage: operational and transactional communications.',
      connectorStorageTitle: 'Storage / DigitalOcean Spaces',
      connectorStorageDesc: 'Metadata-only, demo, or Spaces depending on current configuration.',
      connectorStorageUsage: 'Usage: documents, payslips, and contracts with signed URLs.',
      connectorFlowTitle: 'Spectra Flow/Auth',
      connectorFlowDesc: 'Corporate identity, tenants, and roles centralized (future activation).',
      connectorFlowUsage: 'Usage: users, tenants, roles, and global permissions.',
      connectorFinanceTitle: 'Spectra Finance',
      connectorFinanceDesc: 'Expense sync and payment states (prepared).',
      connectorFinanceUsage: 'Usage: expense reports, reimbursements, and approved spend.',
      connectorSignTitle: 'Digital signatures',
      connectorSignDesc: 'Roadmap: e-signatures and custody for contracts and receipts.',
      connectorSignUsage: 'Future usage: contracts, receipts, and sensitive documents.',
      connectorClockTitle: 'External attendance',
      connectorClockDesc: 'Roadmap: biometric clocks and vendor punch integrations.',
      connectorClockUsage: 'Future usage: clocks, biometrics, and shift integrations.',
      connectorStateFuture: 'Future / inactive',
      connectorStatePrepared: 'Prepared (no real sync)',
      connectorStateEmailDemo: 'Demo / simulation',
      connectorStateEmailReal: 'Real channel configured',
      connectorStateStorageMeta: 'Metadata-only',
      connectorStateStorageReady: 'Spaces operational',
      connectorStateStoragePending: 'Pending configuration',
      storageCardCurrentTitle: 'Current state (metadata-only)',
      storageCardFutureTitle: 'Next: object storage',
      storageDocRuleTitle: 'Recommended architecture',
      securityFlowHint:
        'In production, sessions and access policies will align with Spectra Flow / Auth Center and tenant RBAC.',
      sectionStorageDocumental: 'Document storage',
      storageApiTitle: 'Live status (GET /api/storage/config)',
      storageApiLoading: 'Loading storage configuration…',
      storageApiMode: 'Mode',
      storageApiStatus: 'Status',
      storageApiEnabled: 'Remote uploads enabled',
      storageApiBucket: 'Bucket configured',
      storageApiCreds: 'Credentials configured',
      storageApiMax: 'Max file size',
      storageApiMimes: 'Allowed MIME types',
      storageApiVarsBody:
        'To enable Spaces: RRHH_STORAGE_MODE=spaces, DO_SPACES_KEY, DO_SPACES_SECRET, DO_SPACES_BUCKET, DO_SPACES_ENDPOINT (optional DO_SPACES_PUBLIC_BASE_URL). Never expose secrets in the frontend.',
      storageDocState: 'Current state: metadata in MongoDB only (no heavy binaries in the database).',
      storageDocFuture: 'Future provider: DigitalOcean Spaces / Amazon S3.',
      storageDocRule: 'Rule: files live in object storage; Mongo stores metadata, keys, and permissions.',
      matrixCaption: 'Module permission matrix (read-only demo)',
      matrixLegend: 'R: read · W: write · —: no access',
      matrixRoleCol: 'Role',
      modEmployees: 'People',
      modLegajo: 'Employee file',
      modDocuments: 'Documents',
      modAttendance: 'Attendance',
      modLeaves: 'Time off',
      modExpenses: 'Expenses',
      modPersonalFinance: 'Personal finance',
      modCommunications: 'Communications',
      modAwards: 'Awards',
      modSettings: 'Settings',
      roleRrhhAdmin: 'rrhh_admin',
      roleRrhhManager: 'rrhh_manager',
      roleRrhhOperator: 'rrhh_operator',
      roleSelfService: 'employee_self_service',
      roleFinanceReviewer: 'finance_reviewer',
      roleViewer: 'viewer',
      version: 'Version',
      lifecycleFlowsTitle: 'Employee lifecycle flows',
      lifecycleFlowsIntro:
        'Reference view of standard Spectra flows. In production this will orchestrate with Flow/Auth, Spaces, and automated tasks.',
      lifecycleOnTitle: 'Standard onboarding',
      lifecycleOnBody:
        'Hiring checklist: personal data, ID, banking, contract, payroll registration, equipment, access, induction, mandatory training, and manager kickoff.',
      lifecycleOffTitle: 'Standard offboarding',
      lifecycleOffBody:
        'Termination notice, final documentation, equipment return, access revocation, settlement, exit interview, certificates, and file closure.',
      lifecycleDocsTitle: 'Required documents by stage',
      lifecycleDocsBody:
        'Onboarding: ID, banking, signed contract. Operations: certificates and updates. Offboarding: settlements, work certificates, and document closure.',
      lifecycleAutoTitle: 'Automatic tasks (roadmap)',
      lifecycleAutoBody:
        'Manager reminders, HR tasks when employee status changes, and webhooks to Finance and the employee file.',
      lifecycleIntegTitle: 'Future integration with Flow/Auth and Spaces',
      lifecycleIntegBody:
        'SSO, role-based permissions, and attachments in object storage without binary payloads in Mongo—the same tenant architecture as the rest of HR.',
    },
    evaluations: {
      eyebrow: 'People suite',
      heroTitle: 'Performance reviews',
      heroLead:
        'Track cycles, owners, and next actions. Demo data until the real workflow is connected.',
      demoBanner: 'Visual MVP · demo data on the frontend',
      kpiActive: 'Active reviews',
      kpiPending: 'Pending',
      kpiCompleted: 'Completed',
      kpiAvg: 'Average performance',
      cycleTitle: 'Review cycle',
      cycleIntro: 'Standard Spectra flow to align expectations and feedback.',
      cycleObjectives: 'Objectives',
      cycleObjectivesDesc: 'Shared definition of goals and competencies for the period.',
      cycleSelf: 'Self-assessment',
      cycleSelfDesc: 'Employee reflection with evidence and wins.',
      cycleManager: 'Manager review',
      cycleManagerDesc: 'Calibration and constructive comments from the line manager.',
      cycleFeedback: 'Final feedback',
      cycleFeedbackDesc: 'Formal closure, action plan, and record in the employee file.',
      listTitle: 'Review inbox',
      managerLabel: 'Manager',
      scoreLabel: 'Score',
      scorePending: 'Score pending',
      status_draft: 'Draft',
      status_pending_employee: 'Pending employee',
      status_pending_manager: 'Pending manager',
      status_completed: 'Completed',
      status_archived: 'Archived',
      nextActionManager: 'Send consolidated pack to HR',
      nextActionSelf: 'Complete self-assessment',
      nextActionArchive: 'Archive in digital file',
      nextActionPublish: 'Publish cycle to managers',
      nextActionNone: 'No action required',
    },
    trainings: {
      eyebrow: 'People suite',
      heroTitle: 'Training',
      heroLead: 'Catalog, modality, and progress per course. Demo view until the LMS is integrated.',
      demoBanner: 'Visual MVP · demo data on the frontend',
      kpiActive: 'Active courses',
      kpiEnrolled: 'Enrollments',
      kpiCompleted: 'Courses completed',
      kpiHours: 'Training hours (est.)',
      pathTitle: 'Recommended learning path',
      pathIntro: 'Suggested sequence for operators and leaders in development.',
      path1: 'Spectra onboarding + essential compliance',
      path2: 'Leadership and feedback (live + practice)',
      path3: 'Technical deep dive or wellbeing track per career plan',
      catalogTitle: 'Catalog and status',
      progress: 'Progress',
      participants: 'Participants',
      modality_async: 'Async',
      modality_live: 'Live',
      modality_blended: 'Blended',
      status_active: 'Active',
      status_draft: 'Draft',
      cat_onboarding: 'Onboarding',
      cat_compliance: 'Compliance',
      cat_leadership: 'Leadership',
      cat_technical: 'Technical',
      cat_wellness: 'Wellbeing',
    },
    climate: {
      eyebrow: 'People suite',
      heroTitle: 'Climate & surveys',
      heroLead: 'Team pulse, eNPS, and active surveys. Illustrative data to validate executive UX.',
      demoBanner: 'Visual MVP · demo data on the frontend',
      kpiClimate: 'Average climate',
      kpiResponses: 'Responses received',
      kpiActive: 'Active surveys',
      kpiEnps: 'eNPS / pulse',
      pulseTitle: 'Organizational pulse',
      pulseIntro: 'Synthetic indicators per dimension (demo).',
      pulse_motivation: 'Motivation',
      pulse_workload: 'Workload',
      pulse_leadership: 'Leadership',
      pulse_communication: 'Communication',
      listTitle: 'Surveys',
      audience: 'Audience',
      closes: 'Closes',
      responses: 'Responses',
      score: 'Score',
      status_active: 'Active',
      status_draft: 'Draft',
      status_closed: 'Closed',
      aud_all: 'Whole company',
      aud_managers: 'Managers',
      aud_employees: 'Employees',
      risksTitle: 'Cultural risks (demo)',
      risksBody:
        'When perceived workload rises and motivation drops, align with leaders on relief and transparent communication.',
    },
    lifecycle: {
      title: 'Employee lifecycle',
      subtitle: 'Run onboarding, internal changes, documentation, tasks, and offboarding in one place.',
      eyebrow: 'People Operations',
      demoBanner: 'Demo view · sample data on the frontend',
      tabsAria: 'Lifecycle sections',
      tab_onboarding: 'Onboarding',
      tab_offboarding: 'Offboarding',
      tab_timeline: 'Timeline',
      timelineRailHint: 'Visual timeline with key employee milestones (demo).',
      kpiOnboarding: 'Active onboardings',
      kpiDocsPending: 'Pending documentation',
      kpiTasksOverdue: 'Overdue HR tasks',
      kpiOffboarding: 'Active offboardings',
      kpiAvgProgress: 'Average progress',
      flowStatus_pending: 'Pending',
      flowStatus_in_progress: 'In progress',
      flowStatus_blocked: 'Blocked',
      flowStatus_completed: 'Completed',
      progress: 'Progress',
      nextAction: 'Next action',
      manager: 'Manager',
      startDate: 'Start date',
      endDate: 'End date',
      checklistOnTitle: 'Hire checklist',
      checklistOffTitle: 'Exit checklist',
      checkOnboard_personal: 'Personal data',
      checkOnboard_idDoc: 'ID / identification',
      checkOnboard_bank: 'Bank details / CBU',
      checkOnboard_contract: 'Contract',
      checkOnboard_laborReg: 'Labor registration',
      checkOnboard_equipment: 'Equipment / tools',
      checkOnboard_access: 'Access',
      checkOnboard_induction: 'Initial induction',
      checkOnboard_mandatoryTraining: 'Mandatory training',
      checkOnboard_managerMeet: 'Manager meeting',
      checkOff_notice: 'Termination notice',
      checkOff_finalDocs: 'Final documentation',
      checkOff_equipmentReturn: 'Equipment return',
      checkOff_accessRevoke: 'Access revocation',
      checkOff_settlement: 'Final settlement',
      checkOff_exitInterview: 'Exit interview',
      checkOff_certificates: 'Certificates',
      checkOff_fileClose: 'File closure',
      onboardNextBank: 'Complete bank details / CBU in the file.',
      onboardNextContract: 'Blocked: signed contract and system hire missing.',
      onboardNextNone: 'Onboarding complete. Archive checklist in the file.',
      onboardNextPersonal: 'Start personal data and documentation capture.',
      offboardNextAccess: 'Pending: revoke corporate access.',
      offboardNextFile: 'Close digital file and final certificates.',
      emptyOnboarding: 'No employees in onboarding for the current filters.',
      focusFilter: 'Filter from employees',
      alert_access_pending: 'Access pending',
      alert_equipment_pending: 'Equipment not returned',
      alert_docs_pending: 'Final documentation pending',
      evType_hire_created: 'Hire',
      evType_contract_uploaded: 'Contract',
      evType_doc_approved: 'Document',
      evType_leave_requested: 'Time off',
      evType_expense_submitted: 'Expense',
      evType_eval_completed: 'Review',
      evType_training_assigned: 'Training',
      evType_award: 'Recognition',
      evType_area_change: 'Internal move',
      evType_offboard_started: 'Offboarding',
      evHireCreated: 'Employee hire created in Spectra HR.',
      evContract: 'Employment contract uploaded as metadata (e-signature pending).',
      evDocApproved: 'ID document approved by HR.',
      evLeave: 'Time off requested and under review.',
      evExpense: 'Expense report submitted for approval.',
      evEval: 'Performance review completed.',
      evTraining: 'Mandatory training assigned to the employee.',
      evAward: 'Recognition published in culture.',
      evArea: 'Area change recorded in org chart.',
      evOffboard: 'Offboarding started — exit checklist active.',
      stageOperational: 'In operation',
      stageOnboarding: 'Onboarding',
      stageOffboarding: 'Offboarding',
      stageInactive: 'Inactive',
    },
    comingSoon: {
      badge: 'Coming soon',
      badgePrepared: 'Ready',
      genericSubtitle: 'Enterprise roadmap module — shipping in a future release.',
      valueProposition:
        'Every module ships with the same Spectra standard: permissions, audit trails, and a unified experience.',
      preparedNote:
        'This module is on the Spectra HR roadmap and will ship in a future release.',
      featuresTitle: 'Planned capabilities',
      records: {
        title: 'Employee files',
        category: 'People',
        desc: 'Full employee history: documents, contracts, time off, reviews, and recognition.',
        f1: 'Single employee timeline view',
        f2: 'Versioned contracts and addenda',
        f3: 'Integration with time off and performance',
        f4: 'Audit trails and role-based access',
      },
      documents: {
        title: 'Documents',
        category: 'People',
        desc: 'Document management with expiry, visibility, and future S3/Spaces storage.',
        f1: 'Folders by employee and area',
        f2: 'Expiry alerts',
        f3: 'Signatures and traceability',
        f4: 'Secure cloud storage',
      },
      employeePortal: {
        title: 'Employee portal',
        category: 'People',
        desc: 'Self-service for data, requests, documents, benefits, and personal finances.',
        f1: 'Personal data updates',
        f2: 'Requests and tracking',
        f3: 'Benefits and payroll summary',
        f4: 'My finances integrated',
      },
      attendance: {
        title: 'Attendance',
        category: 'Processes',
        desc: 'Time tracking, remote/on-site, absences, and reporting.',
        f1: 'Clock-in and schedule policies',
        f2: 'Remote vs on-site',
        f3: 'Time off integration',
        f4: 'Leader reports',
      },
      payrollReceipts: {
        title: 'Payslips',
        category: 'Processes',
        desc: 'Digital payslips, signatures, and traceability.',
        f1: 'Payslips by period',
        f2: 'E-signature',
        f3: 'Downloadable history',
        f4: 'Compliance ready',
      },
      communications: {
        title: 'Communications',
        category: 'Culture',
        desc: 'Internal announcements, segmentation, and read receipts.',
        f1: 'Targeted announcements',
        f2: 'Read confirmations',
        f3: 'Executive templates',
        f4: 'Scheduled delivery',
      },
      feed: {
        title: 'Internal feed',
        category: 'Culture',
        desc: 'Culture, news, birthdays, wins, and recognition.',
        f1: 'Culture wall',
        f2: 'Birthdays and anniversaries',
        f3: 'Team wins',
        f4: 'Awards integration',
      },
      compliance: {
        title: 'Compliance',
        category: 'Culture',
        desc: 'Internal channel, incidents, follow-up, and audit.',
        f1: 'Confidential channel',
        f2: 'Incident management',
        f3: 'Review workflows',
        f4: 'Compliance reporting',
      },
      performance: {
        title: 'Performance',
        category: 'Development',
        desc: 'Reviews, feedback, competencies, and improvement plans.',
        f1: 'Review cycles',
        f2: '360 feedback',
        f3: 'Competencies and OKRs',
        f4: 'Development plans',
      },
      recruiting: {
        title: 'Recruiting',
        category: 'Development',
        desc: 'Requisitions, candidates, interviews, and hire conversion.',
        f1: 'Candidate pipeline',
        f2: 'Interviews and scorecards',
        f3: 'Offers and approvals',
        f4: 'Automatic HR onboarding',
      },
      training: {
        title: 'Training',
        category: 'Development',
        desc: 'Courses, progress, certificates, and expiries.',
        f1: 'Course catalog',
        f2: 'Per-employee progress',
        f3: 'Certificates',
        f4: 'Expiry reminders',
      },
      surveys: {
        title: 'Surveys',
        category: 'Development',
        desc: 'Engagement, eNPS, satisfaction, and internal forms.',
        f1: 'eNPS and climate',
        f2: 'Drag-and-drop forms',
        f3: 'Configurable anonymity',
        f4: 'Results dashboard',
      },
      tasks: {
        title: 'Tasks',
        category: 'Processes',
        desc: 'Onboarding, offboarding, checklists, and owners.',
        f1: 'Process templates',
        f2: 'Assignment and SLAs',
        f3: 'Onboarding checklists',
        f4: 'Area tracking',
      },
      reports: {
        title: 'Reports',
        category: 'Intelligence',
        desc: 'People analytics, turnover, absence, expenses, and performance.',
        f1: 'HR KPIs',
        f2: 'Turnover and retention',
        f3: 'Absence and leave',
        f4: 'Executive export',
      },
      peopleIntelligence: {
        title: 'People Intelligence',
        category: 'Intelligence',
        desc: 'AI insights, flight risk, suggestions, and alerts.',
        f1: 'Risk alerts',
        f2: 'Actionable suggestions',
        f3: 'Internal benchmarks',
        f4: 'Operational data integration',
      },
    },
    status: {
      active: 'Active',
      onboarding: 'Onboarding',
      inactive: 'Inactive',
      offboarding: 'Offboarding',
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
      draft: 'Draft',
      submitted: 'Submitted',
      manager_approved: 'Manager approved',
      finance_approved: 'Finance approved',
      paid: 'Paid',
    },
    common: {
      loading: 'Loading…',
      error: 'Failed to load data',
      actions: 'Actions',
      suitePrivacyLine: 'Personal data protected; roles ready for Spectra Flow/Auth; documents ready for secure storage.',
      emptyPremiumHint: 'No data here yet. Add records or use demo mode for the walkthrough.',
    },
    leaveTypes: {
      vacation: 'Vacation',
      sick: 'Sick leave',
      study: 'Study leave',
      personal: 'Personal',
      remote: 'Remote work',
      other: 'Other',
    },
    leaveStatuses: {
      pending: 'Pending',
      approved: 'Approved',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    expenseCategories: {
      travel: 'Travel / transfers',
      meals: 'Meals',
      office: 'Office',
      training: 'Training',
      health: 'Health',
      equipment: 'Equipment',
      other: 'Other',
    },
    employmentType: {
      full_time: 'Full time',
      part_time: 'Part time',
      contractor: 'Contractor',
      intern: 'Intern',
    },
    employeeStatus: {
      active: 'Active',
      onboarding: 'Onboarding',
      inactive: 'Inactive',
      offboarding: 'Offboarding',
    },
  },
};
